// ============================================================================
//  MarkerMaker.js
//  ---------------------------------------------------------------------------
//  Full Marker-Making pipeline (as per Nirvikalp CAD Plan, 27 May 2026)
//
//  STEP 1  → StyleSelector          (same filters as Workstation)
//  STEP 2  → Pattern Loader          (load all patterns saved with style)
//  STEP 3  → Spec Sheet Import        (CSV / JSON / paste from Style Registry)
//  STEP 4  → Spec→Pattern Cross-Check (measurement comparison & rule-based adj.)
//  STEP 5  → Pattern Editor           (notches, grain lines, darts, measure tools)
//  STEP 6  → Grading View             (size nesting in layers, compatibility check)
//  STEP 7  → Seam Compatibility       (neck vs collar, cuff vs sleeve, etc.)
//  STEP 8  → Marker Planner           (3 modes: PO ratio | manual | auto)
//
//  Usage:
//    import MarkerMaker from './MarkerMaker';
//    <MarkerMaker db={db} onClose={() => {}} />
//
//  The component expects `db.masterStyles[]` and `db.masterParts[]` which
//  already exist in the Nirvikalp state tree populated by Workstation /
//  PartDNA. All API calls use the same save_universal.php endpoint.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import GARMENT_STANDARDS, { getGradingTable, buildStandardPatterns, getGradedMeasurements, TEMPLATE_ANCHORS, TEMPLATE_MEASUREMENTS, calcMeasurementsFromAnchors } from './GarmentStandards';
import { GARMENT_TEMPLATES, offsetPolygon } from './ShapeBuilder';  // reuse ShapeBuilder's parametric shapes + proper polygon offset in MarkerMaker
import TemplateSelector from './components/Templates/TemplateSelector';
import DxfImporter from './DxfImporter';
import {
  ChevronRight, ChevronLeft, X, Check, Upload, Download,
  Ruler, Scissors, Grid, Layers, AlertTriangle, CheckCircle2,
  Plus, Trash2, Edit2, Eye, ZoomIn, ZoomOut, RotateCcw,
  RefreshCw, Package, FileText, Settings, Search, Save
} from 'lucide-react';

// ---------------------------------------------------------------------------
//  CONSTANTS & API
// ---------------------------------------------------------------------------
const API_BASE = 'https://nirvikalp.integrashion.com/api';
const SAVE_URL  = `${API_BASE}/save_universal.php`;
const LOAD_URL  = `${API_BASE}/load_universal.php`;

const GARMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

// Default seam rules: which measurements must match across seams
const DEFAULT_SEAM_RULES = [
  { id: 'neck_collar',   label: 'Neckline ↔ Collar Band',       partA: 'neck',         partB: 'collar_band',   specA: 'neckWidth',   specB: 'neckLength',   tolerance: 0.5 },
  { id: 'armhole_sleeve',label: 'Armhole ↔ Sleeve Crown',        partA: 'body',         partB: 'sleeve',        specA: 'armholeDrop', specB: 'capHeight',    tolerance: 0.8 },
  { id: 'cuff_sleeve',   label: 'Cuff Length ↔ Sleeve Opening',  partA: 'cuff',         partB: 'sleeve',        specA: 'cuffLength',  specB: 'cuffWidth',    tolerance: 0.3 },
  { id: 'side_seam',     label: 'Front Side Seam ↔ Back Side',   partA: 'tee_front',    partB: 'tee_back',      specA: 'bodyLength',  specB: 'bodyLength',   tolerance: 0.0 },
  { id: 'yoke_back',     label: 'Yoke Width ↔ Back Shoulder',    partA: 'yoke',         partB: 'tee_back',      specA: 'yokeWidth',   specB: 'shoulderWidth',tolerance: 0.5 },
];

// Same idea, for trousers — the shirt rules above never match any trouser
// piece name/template, which is why the checker previously showed every
// row as "—" (missing) the moment a trouser style was loaded instead of a
// shirt. Uses the exact halfHem/halfKnee/inseam keys the trouser shape
// generators and grading already share between Front and Back Panel.
const TROUSER_SEAM_RULES = [
  { id: 't_hem',    label: 'Front Hem ↔ Back Hem',       partA: 'front panel', partB: 'back panel', specA: 'halfHem',  specB: 'halfHem',  tolerance: 0.3 },
  { id: 't_knee',   label: 'Front Knee ↔ Back Knee',     partA: 'front panel', partB: 'back panel', specA: 'halfKnee', specB: 'halfKnee', tolerance: 0.5 },
  { id: 't_inseam', label: 'Front Inseam ↔ Back Inseam', partA: 'front panel', partB: 'back panel', specA: 'inseam',   specB: 'inseam',   tolerance: 0.3 },
];

// Picks whichever rule set actually matches the pieces that are loaded,
// instead of always showing the (shirt-only) default regardless of product.
const detectSeamRuleSet = (patterns) => {
  const names = (patterns || []).map(p => (p.partName || '').toLowerCase());
  const templates = (patterns || []).map(p => (p.template || '').toLowerCase());
  const hasTrouserPanels = names.some(n => n.includes('front panel')) && names.some(n => n.includes('back panel'));
  const looksLikeTrouser = hasTrouserPanels && (templates.some(t => t.includes('trouser')) || names.some(n => n.includes('waistband') || n.includes('fly')));
  if (looksLikeTrouser) return TROUSER_SEAM_RULES;
  const looksLikeShirt = names.some(n => n.includes('collar') || n.includes('sleeve') || n.includes('yoke'));
  if (looksLikeShirt) return DEFAULT_SEAM_RULES;
  return [];
};

// ============================================================================
//  PATTERN FILE FORMAT  (.npf — Nirvikalp Pattern File, JSON)
//  Lightweight, portable; can be used for texture fill or 3D simulation.
// ============================================================================
export const createEmptyPattern = (partId, partName, template = '', measurements = {}) => ({
  format: 'npf-v1',
  id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  partId,
  partName,
  template,
  measurements,                    // finished-garment measurements (cm)
  seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },  // default SA in cm
  grainLine: { x1: 0, y1: 0, x2: 0, y2: 1, angleDeg: 90 },  // warp = vertical
  notches: [],     // [{ id, t (0-1 edge position), edgeIdx, type:'slit'|'v', depth:1 }]
  darts: [],       // [{ id, x, y, width, depth, angle }]
  measurementPoints: [],  // [{ id, label, x1, y1, x2, y2 }]
  gradingRules: {},       // { S: { dx: {}, dy: {} }, M: {}, ... }  delta per measurement key
  specMapping: {},        // { specSheetKey: measurementKey }
  brandRules: [],         // [{ field, operator, value, action }]
  createdOn: new Date().toISOString(),
  updatedOn: new Date().toISOString(),
});

// Serialise to downloadable JSON file
export const exportPatternFile = (pattern) => {
  const blob = new Blob([JSON.stringify(pattern, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${pattern.partName || 'pattern'}_${pattern.id}.npf`;
  a.click();
};

// Parse spec-sheet text (CSV / JSON / paste)
// ── Garment industry fraction parser: "17 3/4"→17.75, "1 1/8"→1.125 ─────────
export const parseFrac = (s) => {
  const str = String(s || '').trim().replace(/['"]/g, '');
  if (!str || str === '-' || /^n\/?a$/i.test(str)) return NaN;
  const m1 = str.match(/^([\d.]+)\s+(\d+)\/(\d+)$/);
  if (m1) return parseFloat(m1[1]) + parseFloat(m1[2]) / parseFloat(m1[3]);
  const m2 = str.match(/^(\d+)\/(\d+)$/);
  if (m2) return parseFloat(m2[1]) / parseFloat(m2[2]);
  return parseFloat(str);
};

// ── Parse one CSV line, respecting quoted commas ──────────────────────────
const parseCsvRow = (line) => {
  const r = []; let q = false, cur = '';
  for (const ch of line) {
    if (ch === '"')     { q = !q; }
    else if (ch === ',' && !q) { r.push(cur.trim()); cur = ''; }
    else { cur += ch; }
  }
  r.push(cur.trim());
  return r;
};

// ── Full garment spec sheet parser ────────────────────────────────────────
// Handles the real industry CSV format:
//   Row 1-4:  metadata  (BRAND, FIT, GENDER, VENDOR …)
//   Row N:    header    POINT OF MEASUREMENT | TOL +/- | S | (blank) | M | (blank) | L …
//   Row N+x:  measurements, with values that may be fractions ("17 3/4")
// Also handles simple key:value text and JSON as fallbacks.
export const parseSpecSheet = (raw) => {
  if (!raw) return {};
  const txt = raw.trim().replace(/^\uFEFF/, ''); // strip BOM

  // JSON fallback
  if (txt.startsWith('{') || txt.startsWith('[')) {
    try {
      const j = JSON.parse(txt);
      if (Array.isArray(j)) {
        const out = {};
        j.forEach(r => {
          const k = r.key || r.measurement || r.name || r.description;
          const v = r.value ?? r.val ?? r.current ?? r.target;
          if (k) out[String(k).trim()] = parseFloat(v);
        });
        return { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'json' };
      }
      const out = {};
      Object.entries(j).forEach(([k, v]) => { out[k.trim()] = parseFloat(v); });
      return { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'json' };
    } catch (_) {}
  }

  // CSV multi-column format
  const rows = txt.split(/\r?\n/).map(parseCsvRow);
  const META_KEYS = ['BRAND','FIT','GENDER','VENDOR','SEASON','STYLE','DATE','CUSTOMER'];
  const meta = {};
  const sizeColMap = {}; // { 'S': 2, 'M': 4, … }
  let headerIdx = -1;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const c0 = (rows[i][0] || '').trim();
    const c0u = c0.toUpperCase();
    if (META_KEYS.some(k => c0u.startsWith(k))) {
      meta[c0] = (rows[i][1] || '').trim();
    }
    if (c0u.includes('POINT') || c0u.includes('MEASUREMENT') || c0u === 'POM') {
      headerIdx = i;
      rows[i].forEach((cell, ci) => {
        const sz = cell.trim().toUpperCase();
        if (/^(XXS|XS|S|M|L|XL|2XL|XXL|3XL|XXXL|\d{2}|\d{2}\/\d{2})$/.test(sz))
          sizeColMap[sz] = ci;
      });
      break;
    }
  }

  const sizes = Object.keys(sizeColMap);

  if (headerIdx !== -1 && sizes.length > 0) {
    const base = {}, sizeData = {}, tols = {};
    sizes.forEach(sz => (sizeData[sz] = {}));

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      const name = (row[0] || '').replace(/"/g, '').trim();
      if (!name || /^(SIGN|DATE|PAGE)/i.test(name)) continue;
      if (!sizes.some(sz => !isNaN(parseFrac(row[sizeColMap[sz]])))) continue;

      const tol = parseFrac(row[1]);
      if (!isNaN(tol)) tols[name] = tol;
      sizes.forEach(sz => {
        const v = parseFrac(row[sizeColMap[sz]]);
        if (!isNaN(v)) sizeData[sz][name] = v;
      });
    }

    const baseSz = sizeColMap['M'] ? 'M' : sizes[Math.floor(sizes.length / 2)];
    Object.assign(base, sizeData[baseSz] || {});

    // Detect inches: body-type measurements < 50 are almost certainly in inches
    const sample = Object.values(base)[0] || 100;
    const isInches = sample > 0 && sample < 50;

    return { base, sizeData, sizes, tolerances: tols, meta, baseSize: baseSz, isInches, _format: 'garment_csv' };
  }

  // Simple key:value text fallback
  const out = {};
  txt.split(/\r?\n/).forEach(line => {
    const parts = line.split(/[,\t:;=|]/).map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const v = parseFrac(parts[parts.length - 1]);
      if (!isNaN(v)) out[parts[0]] = v;
    }
  });
  return Object.keys(out).length
    ? { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'simple' }
    : {};
};

// Fuzzy match spec-sheet keys to pattern measurement keys
const normKey = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
export const fuzzyMapSpec = (specData, measurementKeys) => {
  const mapping = {};
  measurementKeys.forEach(mk => {
    const nk = normKey(mk);
    const hit = Object.keys(specData).find(sk => {
      const ns = normKey(sk);
      return ns === nk || ns.includes(nk) || nk.includes(ns);
    });
    if (hit) mapping[hit] = mk;
  });
  return mapping;
};

// Apply grading delta to base measurements to get measurements for a size
const applyGrading = (baseM, gradingRules, size) => {
  const rule = gradingRules[size];
  if (!rule) return { ...baseM };
  const out = { ...baseM };
  Object.entries(rule).forEach(([key, delta]) => {
    if (out[key] !== undefined) out[key] = +(out[key] + delta).toFixed(2);
  });
  return out;
};

// ═══════════════════════════════════════════════════════════════════════════
//  POINT-BASED GRADING FOR TRACED SHAPES
// ─────────────────────────────────────────────────────────────────────────
//  A graded `measurements` object by itself does nothing for a piece that
//  has tracedPoints — buildDisplayPoly always prefers the traced shape and
//  never looks at measurements again once tracedPoints exist. That means,
//  until this, EVERY traced/hand-drawn piece rendered identically at every
//  size, both in the Grading preview and — more seriously — in the Marker
//  step, which packed the same ungraded shape for every size in the ratio.
//
//  Fix: tag individual points with which measurement key drives their X
//  and/or Y position ({gradeX:'halfWaist', gradeY:'frontRise'}, etc). Grading
//  then moves each tagged point by that size's delta and interpolates every
//  other point between its nearest tagged neighbours (by outline position),
//  so curves grade smoothly even with only a handful of anchors tagged.
//  Pieces without tags fall back to a whole-shape bbox scale — cruder, but
//  still far better than no grading at all.
// ═══════════════════════════════════════════════════════════════════════════
const gradeTracedPoints = (tracedPoints, gradeTags, rule) => {
  if (!tracedPoints?.length || !rule) return tracedPoints;
  const n = tracedPoints.length;
  const tags = gradeTags?.length === n ? gradeTags : [];
  // Cumulative arc-length around the loop. Interpolation between two tagged
  // anchors uses actual distance travelled along the outline, not raw point
  // count — points end up unevenly spaced after a lot of rope-dragging or
  // repeated inserts, and index-based interpolation badly misweights a
  // point that's spatially close to an anchor but far from it in the array
  // (visually invisible on the un-graded shape; shows up as a lump the
  // moment grading actually moves things).
  const segLen = tracedPoints.map((p,i) => {
    const q = tracedPoints[(i+1)%n];
    return Math.hypot(q.x-p.x, q.y-p.y);
  });
  const cum = [0];
  for (let i=0;i<n;i++) cum.push(cum[i]+segLen[i]);
  const totalLen = cum[n] || 1;
  const resolveAxis = (axis) => {
    const scaleKey = axis + 'Scale';
    const raw = tracedPoints.map((_, i) => {
      const key = tags[i]?.[axis];
      if (!key) return null;
      const d = rule[key];
      if (d === undefined) return 0;
      const scale = tags[i]?.[scaleKey] ?? 1;
      return d * scale;
    });
    const known = raw.map((v, i) => v !== null ? i : -1).filter(i => i >= 0);
    if (known.length === 0) return tracedPoints.map(() => 0);
    return raw.map((v, i) => {
      if (v !== null) return v;
      let before = null, after = null, bd = Infinity, ad = Infinity;
      known.forEach(k => {
        const df = (i - k + n) % n; if (df > 0 && df < bd) { bd = df; before = k; }
        const da = (k - i + n) % n; if (da > 0 && da < ad) { ad = da; after = k; }
      });
      if (before === null) return raw[after]  || 0;
      if (after  === null) return raw[before] || 0;
      if (before === after) return raw[before] || 0;
      const distBeforeToI     = (cum[i] - cum[before] + totalLen) % totalLen;
      const distBeforeToAfter = (cum[after] - cum[before] + totalLen) % totalLen || totalLen;
      const t = distBeforeToI / distBeforeToAfter;
      return (raw[before]||0) * (1 - t) + (raw[after]||0) * t;
    });
  };
  const dxs = resolveAxis('gradeX'), dys = resolveAxis('gradeY');
  return tracedPoints.map((p, i) => ({ ...p, x: +(p.x + dxs[i]).toFixed(2), y: +(p.y + dys[i]).toFixed(2) }));
};

// Auto-generates gradeTags for a traced piece that has none — e.g. anything
// hand-drawn, auto-traced, or otherwise custom, since only the built-in
// trouser generator tags itself today. Tags the piece's own extreme points
// (top/bottom/left/right) against its own width/height-style measurement
// keys, leaving every other point untagged so it interpolates smoothly
// (gradeTracedPoints above). This is an ADDITIVE, position-based nudge —
// unlike the old bboxGradeScale fallback below, it doesn't multiplicatively
// stretch the whole shape from one corner, which is what was blowing a
// small local feature (a dart notch, a curve) wildly out of proportion the
// moment a custom piece got graded.
const autoGenerateGradeTags = (tracedPoints, measurements) => {
  if (!tracedPoints?.length) return null;
  const keys = Object.keys(measurements || {});
  const widthKey  = keys.find(k => /^width$/i.test(k))  || keys.find(k => WIDTH_KEY_RE.test(k));
  const heightKey = keys.find(k => /^height$/i.test(k)) || keys.find(k => HEIGHT_KEY_RE.test(k));
  if (!widthKey && !heightKey) return null; // nothing usable to tag against
  let minXi=0,maxXi=0,minYi=0,maxYi=0;
  tracedPoints.forEach((p,i) => {
    if (p.x < tracedPoints[minXi].x) minXi=i;
    if (p.x > tracedPoints[maxXi].x) maxXi=i;
    if (p.y < tracedPoints[minYi].y) minYi=i;
    if (p.y > tracedPoints[maxYi].y) maxYi=i;
  });
  const tags = tracedPoints.map(() => ({ gradeX:null, gradeY:null }));
  tags[minYi] = { ...tags[minYi], gradeY: 'FIXED' }; // bottom/hem — fixed reference, grade outward from here
  if (heightKey) tags[maxYi] = { ...tags[maxYi], gradeY: heightKey };
  if (widthKey) {
    // 'width' is a full-width measurement, so each side only takes half the
    // delta, moving in opposite directions — gradeXScale is a new, purely
    // additive field so it doesn't change how the existing trouser gradeTags
    // (which already use half-width keys like halfWaist) behave.
    tags[minXi] = { ...tags[minXi], gradeX: widthKey, gradeXScale: -0.5 };
    tags[maxXi] = { ...tags[maxXi], gradeX: widthKey, gradeXScale:  0.5 };
  }
  return tags;
};

const WIDTH_KEY_RE  = /width|chest|hip|waist|bust|circumference|neck|shoulder|cuff|thigh|knee|hem/i;
const HEIGHT_KEY_RE = /height|body|arm|sleeve|inseam|rise|length|depth/i;
// Fallback for traced pieces with no per-point grade tags: scale the whole
// shape from its own bottom-left corner using width/height-style
// measurement deltas. Cruder than true point grading (doesn't hold an
// anatomical reference line fixed) but still correct at the sizes people
// actually check against — much better than not grading at all.
const bboxGradeScale = (baseM, gradedM) => {
  let wB=0,wG=0,hB=0,hG=0;
  Object.keys(baseM||{}).forEach(k => {
    const bv = baseM[k]||0, gv = gradedM[k] ?? bv;
    if (WIDTH_KEY_RE.test(k))       { wB+=bv; wG+=gv; }
    else if (HEIGHT_KEY_RE.test(k)) { hB+=bv; hG+=gv; }
  });
  return { sx: wB>0 ? wG/wB : 1, sy: hB>0 ? hG/hB : 1 };
};

// Returns a copy of `pat` graded for `size` per gradingRules[pat.id][size].
// Safe to call unconditionally — no-ops (returns pat as-is) when there's no
// rule for that size. This is the ONE place grading should be applied before
// handing a pattern to buildDisplayPoly / getPatternBbox, so every consumer
// (Grading preview, Marker layout, exports) sees the same graded shape.
const applyGradingToPattern = (pat, gradingRules, size) => {
  const rule = gradingRules?.[pat.id]?.[size];
  if (!rule || !Object.keys(rule).length) return pat;
  const gradedM = { ...pat.measurements };
  Object.entries(rule).forEach(([k, d]) => { if (gradedM[k] !== undefined) gradedM[k] = +(gradedM[k] + d).toFixed(2); });
  if (!pat.tracedPoints?.length) return { ...pat, measurements: gradedM };
  const tags = (pat.gradeTags?.length === pat.tracedPoints.length)
    ? pat.gradeTags
    : autoGenerateGradeTags(pat.tracedPoints, pat.measurements);
  if (tags) {
    return { ...pat, measurements: gradedM, tracedPoints: gradeTracedPoints(pat.tracedPoints, tags, rule) };
  }
  // Last resort — no measurement keys to tag against at all. Kept as a
  // fallback rather than leaving the piece completely ungraded, but this
  // is the crude whole-shape stretch that distorts local detail; it should
  // rarely be reached now that autoGenerateGradeTags covers any piece with
  // so much as a width or height measurement.
  const { sx, sy } = bboxGradeScale(pat.measurements, gradedM);
  if (sx === 1 && sy === 1) return { ...pat, measurements: gradedM };
  const xs = pat.tracedPoints.map(p=>p.x), ys = pat.tracedPoints.map(p=>p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const traced = pat.tracedPoints.map(p => ({
    ...p,
    x: +(minX + (p.x - minX) * sx).toFixed(2),
    y: +(minY + (p.y - minY) * sy).toFixed(2),
  }));
  return { ...pat, measurements: gradedM, tracedPoints: traced };
};

// ============================================================================
//  SVG PATTERN RENDERER
//  Converts template measurements + notches/grain/marks into an SVG preview.
//  If no template, renders a simple bounding box.
// ============================================================================
const PatternSvg = ({
  pattern,
  width = 320,
  height = 320,
  showNotches = true,
  showGrain = true,
  showMeasurements = true,
  showSA = false,
  highlightColor = '#1976D2',
  fillColor = 'rgba(59,130,246,0.07)',
  selected = false,
  onClick,
}) => {
  // Use the exact same shape source as the Reshape editor — tracedPoints,
  // then a saved "Standard" shape, then the anatomical SHAPE_FNS/ShapeBuilder
  // generators, then a generic box as last resort. Previously this component
  // guessed a generic shirt-body outline from width/height regardless of
  // piece type, which is why trouser panels/waistbands/pockets all rendered
  // as near-identical warped blobs in the library cards.
  const { pts, ptsMeta } = useMemo(() => buildDisplayPoly(pattern),
    [JSON.stringify(pattern.tracedPoints), JSON.stringify(pattern.measurements), pattern.template]);

  const xs = pts.map(p => p[0]);
  const ys = pts.map(p => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const pw = maxX - minX || 1, ph = maxY - minY || 1;
  const PAD = 12;
  const scaleX = (width - PAD * 2) / pw;
  const scaleY = (height - PAD * 2) / ph;
  const sc = Math.min(scaleX, scaleY);

  const tx = (x) => PAD + (x - minX) * sc;
  const ty = (y) => height - PAD - (y - minY) * sc;

  const outlineD = buildPathD(pts, ptsMeta, tx, ty);

  // SA offset — real polygon offset (was previously just a bounding-box
  // rectangle expand, which didn't follow the actual piece shape at all)
  const saPolyStr = showSA && pts.length >= 3 ? (() => {
    const sa = Math.max(...Object.values(pattern.seamAllowances || {top:1,right:1,bottom:1,left:1}));
    const saPts = offsetPolygon(dedupeLoop(pts), sa);
    return saPts.map(p => `${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ');
  })() : null;

  // Grain line: same real-grain-if-available logic as PatternSvgInline —
  // fractional position (% of the outline's own live bounding box) so grain
  // tracks correctly through reshaping, with a legacy-format fallback for
  // patterns imported before this fix, and the original hardcoded vertical
  // line as the final fallback when there's no grain data at all.
  let grainMidX1, grainMidX2, grainY1, grainY2;
  if (pattern.grainLine && pattern.grainLine.length === 2 && pattern.grainLine[0].fx !== undefined) {
    const [g1, g2] = pattern.grainLine;
    grainMidX1 = tx(minX + g1.fx * (maxX - minX)); grainY1 = ty(minY + g1.fy * (maxY - minY));
    grainMidX2 = tx(minX + g2.fx * (maxX - minX)); grainY2 = ty(minY + g2.fy * (maxY - minY));
  } else if (pattern.grainLine && pattern.grainLine.length === 2 && pattern.tracedPoints && pattern.tracedPoints.length >= 3) {
    const gxs = pattern.tracedPoints.map(p => p.x), gys = pattern.tracedPoints.map(p => p.y);
    const gMinX = Math.min(...gxs), gMinY = Math.min(...gys);
    const gW = (Math.max(...gxs) - gMinX) / 2;
    const [g1, g2] = pattern.grainLine;
    grainMidX1 = tx(g1.x - gMinX - gW); grainY1 = ty(g1.y - gMinY);
    grainMidX2 = tx(g2.x - gMinX - gW); grainY2 = ty(g2.y - gMinY);
  } else {
    const grainMidX = (tx(minX) + tx(maxX)) / 2;
    grainMidX1 = grainMidX; grainMidX2 = grainMidX;
    grainY1 = ty(minY + (maxY - minY) * 0.2);
    grainY2 = ty(minY + (maxY - minY) * 0.8);
  }

  // Measurement annotation lines
  const measLines = showMeasurements ? (pattern.measurementPoints || []) : [];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ cursor: onClick ? 'pointer' : 'default', border: selected ? `2px solid ${highlightColor}` : '1px solid #DEE2E6', borderRadius: 6, background: '#fafafa' }}
      onClick={onClick}
    >
      {saPolyStr && (
        <polygon points={saPolyStr} fill="none" stroke="#C62828" strokeWidth="0.8" strokeDasharray="4,3" />
      )}
      <path
        d={outlineD}
        fill={fillColor}
        stroke={selected ? highlightColor : '#2C3E50'}
        strokeWidth={selected ? 1.5 : 1}
        strokeLinejoin="round"
      />
      {showGrain && (
        <g>
          <line x1={grainMidX1} y1={grainY1} x2={grainMidX2} y2={grainY2}
            stroke="#2E7D32" strokeWidth="1.2" strokeDasharray="3,2" />
          <polygon
            points={`${grainMidX1 - 4},${grainY1 + 8} ${grainMidX1 + 4},${grainY1 + 8} ${grainMidX1},${grainY1}`}
            fill="#2E7D32"
          />
          <polygon
            points={`${grainMidX2 - 4},${grainY2 - 8} ${grainMidX2 + 4},${grainY2 - 8} ${grainMidX2},${grainY2}`}
            fill="#2E7D32"
          />
        </g>
      )}
      {showNotches && (pattern.notches || []).map((n, i) => {
        const edgeIdx = n.edgeIdx ?? 0;
        const [ax, ay] = pts[edgeIdx % pts.length];
        const [bx, by] = pts[(edgeIdx + 1) % pts.length];
        const t = n.t ?? 0.5;
        const nx = tx(ax + (bx - ax) * t);
        const ny_ = ty(ay + (by - ay) * t);
        return (
          <g key={n.id || i}>
            <line x1={nx - 5} y1={ny_} x2={nx + 5} y2={ny_} stroke="#FF8F00" strokeWidth="1.5" />
            <line x1={nx} y1={ny_ - 6} x2={nx} y2={ny_ + 6} stroke="#FF8F00" strokeWidth="1.5" />
          </g>
        );
      })}
      <text x={width / 2} y={height - 4} textAnchor="middle" fontSize="9" fill="#6C757D">{pattern.partName}</text>
    </svg>
  );
};


// ============================================================================
//  PRODUCT LIBRARY — Shirts, Trousers, T-Shirts, Polo T-Shirts
//  Industry-standard pattern shapes and grading rules (ISO / AATCC basis)
//  Base size: M (shirts/tshirts/polo) · 32 (trousers)
//  Shapes stored as polygon points in cm, warp-aligned (y = grain direction)
// ============================================================================

// Helper: generate polygon points for standard part types
// All shapes parameterised by measurements in cm.
// Points are [x, y] pairs starting at bottom-left, going clockwise.
// x = width direction (WEFT),  y = length direction (WARP = grain line)

const SHAPES = {

  // ── FRONT/BACK BODY (shared logic, direction flag for neckline depth) ──────
  body: ({ halfChest, halfWaist, halfHip, bodyLen, halfShoulder, neckW, armDrop, frontNeck = 2.5, isFront = true }) => {
    const shoulderSlope = isFront ? 2.0 : 1.5;
    const neckDrop      = isFront ? frontNeck : 1.0;
    const waistInset    = halfChest - halfWaist;
    const hipInset      = halfChest - halfHip;
    return [
      [0,                  0],                              // CF/CB hem
      [halfChest,          0],                              // side seam hem
      [halfChest - hipInset*0.3,  bodyLen * 0.28],          // hip flare point
      [halfChest - waistInset*0.5, bodyLen * 0.45],          // waist suppression
      [halfChest,          bodyLen - armDrop],              // side seam at underarm
      [halfShoulder + 1.2, bodyLen - armDrop * 0.35],       // armhole curve
      [halfShoulder,       bodyLen - shoulderSlope],        // shoulder tip
      [neckW,              bodyLen],                        // neckline–shoulder
      [0,                  bodyLen - neckDrop],             // CF/CB neckline
    ];
  },

  // ── SLEEVE ──────────────────────────────────────────────────────────────────
  sleeve: ({ cuffW, bicepW, crownH, length, isShort = false }) => {
    const cap  = isShort ? crownH * 0.85 : crownH;
    const taper = (bicepW - cuffW) / 2;
    return [
      [0,               0],                       // left cuff end
      [cuffW,           0],                       // right cuff end
      [cuffW + taper,   length - cap],            // right underarm
      [cuffW + taper + cap * 0.18, length - cap * 0.4], // right cap shoulder
      [cuffW / 2 + taper * 0.5, length],          // crown peak
      [-cap * 0.08,     length - cap * 0.4],      // left cap shoulder
      [-taper * 0.1,    length - cap],            // left underarm
    ];
  },

  // ── COLLAR FALL (curved / spread collar) ─────────────────────────────────
  collarFall: ({ halfNeck, fallH, spread = 0.8 }) => [
    [0,           0],
    [halfNeck,    0],
    [halfNeck + spread, fallH * 0.5],
    [halfNeck + spread * 0.5, fallH],
    [0,           fallH],
  ],

  // ── COLLAR BAND / NECK BAND ───────────────────────────────────────────────
  band: ({ halfNeck, bandH }) => [
    [0, 0], [halfNeck + 1.5, 0], [halfNeck + 1.5, bandH], [0, bandH],
  ],

  // ── CUFF (shirt) ─────────────────────────────────────────────────────────
  cuff: ({ cuffW, cuffH }) => [
    [0, 0], [cuffW, 0], [cuffW, cuffH], [0, cuffH],
  ],

  // ── YOKE (shirt back) ───────────────────────────────────────────────────
  yoke: ({ halfShoulder, yokeDepth, neckW }) => [
    [0,            0],
    [halfShoulder, 0],
    [halfShoulder, yokeDepth - 1.5],
    [neckW,        yokeDepth],
    [0,            yokeDepth],
  ],

  // ── CHEST POCKET ─────────────────────────────────────────────────────────
  pocket: ({ pW, pH }) => [
    [0, 0], [pW, 0], [pW, pH], [0, pH],
  ],

  // ── WAISTBAND (trouser) ──────────────────────────────────────────────────
  waistband: ({ halfWaist, bandH }) => [
    [0, 0], [halfWaist + 3, 0], [halfWaist + 3, bandH], [0, bandH],
  ],

  // ── TROUSER FRONT PANEL ──────────────────────────────────────────────────
  trouserFront: ({ waistHalf, hipHalf, thighHalf, kneeHalf, hemHalf, rise, outseam }) => {
    const inseam = outseam - rise;
    return [
      [hemHalf,   0],                    // hem inner
      [hemHalf,   0],                    // same (fold line placeholder)
      [kneeHalf,  outseam * 0.4],        // knee
      [thighHalf, outseam - rise * 0.8], // thigh
      [hipHalf,   outseam - rise * 0.3], // hip
      [waistHalf, outseam],              // waist side
      [waistHalf * 0.45, outseam + 0.5], // waist CF (with slight tuck)
      [0,         outseam - rise * 0.05],// fork point
      [hemHalf * 0.5, 0],                // hem inner seam
    ];
  },

  // ── TROUSER BACK PANEL ───────────────────────────────────────────────────
  trouserBack: ({ waistHalf, hipHalf, thighHalf, kneeHalf, hemHalf, rise, outseam, seatLift = 3 }) => [
    [hemHalf,   0],
    [kneeHalf * 1.05, outseam * 0.4],
    [thighHalf * 1.08, outseam - rise * 0.75],
    [hipHalf * 1.05,  outseam - rise * 0.28],
    [waistHalf * 1.05, outseam + seatLift],
    [waistHalf * 0.3, outseam + seatLift + 1], // back waist CB (extra rise)
    [0,               outseam - rise * 0.08 + seatLift], // back fork
    [hemHalf * 0.45,  0],
  ],

  // ── FLY FACING ───────────────────────────────────────────────────────────
  fly: ({ flyW, flyH }) => [
    [0, 0], [flyW, 0],
    [flyW, flyH * 0.85], [flyW * 0.5, flyH], [0, flyH],
  ],

  // ── NECK RIB (tshirt) ────────────────────────────────────────────────────
  neckRib: ({ halfNeck, ribH }) => [
    [0, 0], [halfNeck, 0], [halfNeck, ribH], [0, ribH],
  ],

  // ── POLO COLLAR RIB ──────────────────────────────────────────────────────
  poloCollar: ({ colW, colH }) => [
    [0, 0], [colW, 0], [colW * 1.05, colH * 0.5],
    [colW, colH], [0, colH],
  ],

  // ── POLO PLACKET ─────────────────────────────────────────────────────────
  placket: ({ plW, plH }) => [
    [0, 0], [plW, 0], [plW, plH], [0, plH],
  ],
};

// Convert polygon [[x,y]...] to tracedPoints [{x,y}...] format
const polyToTraced = (pts) => pts.map(([x, y]) => ({ x: +x.toFixed(2), y: +y.toFixed(2) }));

// ─── PRODUCT DEFINITIONS ─────────────────────────────────────────────────────

const PRODUCT_LIBRARY = {

  // ────────────────────────────────────────────────────────────────────────────
  shirt: {
    label: 'Formal / Casual Shirt',
    icon: '👔',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    // Base measurements (cm) at size M
    baseM: {
      chest: 104, waist: 98, hip: 106, shoulder: 44,
      bodyLength: 73, sleeveLength: 63, crownH: 17,
      bicep: 38, cuffWidth: 25.5, cuffHeight: 7,
      neckCirc: 40, collarFallH: 6, collarBandH: 3.5,
      yokeDepth: 10,
    },

    // Grading deltas per size step (cumulative from M)
    // Source: ISO 4416-1 + industry practice for men's dress shirts
    gradingSteps: {
      XS:  { chest:-8, waist:-8, hip:-8, shoulder:-2.4, bodyLength:-3, sleeveLength:-3, crownH:-0.8, bicep:-2.4, cuffWidth:-2, neckCirc:-4 },
      S:   { chest:-4, waist:-4, hip:-4, shoulder:-1.2, bodyLength:-1.5, sleeveLength:-1.5, crownH:-0.4, bicep:-1.2, cuffWidth:-1, neckCirc:-2 },
      M:   {},
      L:   { chest:+4, waist:+4, hip:+4, shoulder:+1.2, bodyLength:+1.5, sleeveLength:+1.5, crownH:+0.4, bicep:+1.2, cuffWidth:+1, neckCirc:+2 },
      XL:  { chest:+8, waist:+8, hip:+8, shoulder:+2.4, bodyLength:+3,   sleeveLength:+3,   crownH:+0.8, bicep:+2.4, cuffWidth:+2, neckCirc:+4 },
      XXL: { chest:+12,waist:+12,hip:+12,shoulder:+3.6, bodyLength:+4.5, sleeveLength:+4.5, crownH:+1.2, bicep:+3.6, cuffWidth:+3, neckCirc:+6 },
    },

    buildParts: (m) => [
      {
        id: 'shirt-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/4+1, height: m.bodyLength, shoulder: m.shoulder/2, neckWidth: m.neckCirc/6, armholeDrop: m.chest*0.22 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+1, halfWaist: m.waist/4+0.5, halfHip: m.hip/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neckCirc/6, armDrop: m.chest*0.22, frontNeck: 2.5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/4+1, height: m.bodyLength - m.yokeDepth, shoulder: m.shoulder/2, armholeDrop: m.chest*0.22 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+1, halfWaist: m.waist/4+0.5, halfHip: m.hip/4+0.5,
          bodyLen: m.bodyLength - m.yokeDepth, halfShoulder: m.shoulder/2,
          neckW: m.neckCirc/7, armDrop: m.chest*0.22, frontNeck: 1, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, cuffWidth: m.cuffWidth, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.cuffWidth, bicepW: m.bicep+2, crownH: m.crownH, length: m.sleeveLength,
        })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-collar', name: 'Collar Fall', category: 'collar',
        measurements: { width: m.neckCirc/2+1, height: m.collarFallH },
        tracedPoints: polyToTraced(SHAPES.collarFall({ halfNeck: m.neckCirc/2+1, fallH: m.collarFallH, spread: 1.2 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-band', name: 'Collar Band', category: 'collar',
        measurements: { width: m.neckCirc/2+1.5, height: m.collarBandH },
        tracedPoints: polyToTraced(SHAPES.band({ halfNeck: m.neckCirc/2+1.5, bandH: m.collarBandH })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-cuff', name: 'Cuff', category: 'cuff',
        measurements: { width: m.cuffWidth+1.5, height: m.cuffHeight },
        tracedPoints: polyToTraced(SHAPES.cuff({ cuffW: m.cuffWidth+1.5, cuffH: m.cuffHeight })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-yoke', name: 'Back Yoke', category: 'yoke',
        measurements: { width: m.shoulder/2+1, height: m.yokeDepth },
        tracedPoints: polyToTraced(SHAPES.yoke({ halfShoulder: m.shoulder/2+1, yokeDepth: m.yokeDepth, neckW: m.neckCirc/7 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-pocket', name: 'Chest Pocket', category: 'pocket',
        measurements: { width: 12, height: 12 },
        tracedPoints: polyToTraced(SHAPES.pocket({ pW: 12, pH: 12 })),
        seamAllowances: { top: 1, bottom: 2, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  trouser: {
    label: 'Trousers / Pants',
    icon: '👖',
    baseSize: '32',
    sizes: ['28', '30', '32', '34', '36', '38'],

    baseM: {
      waist: 82, hip: 100, thigh: 58, knee: 44, legOpening: 42,
      outseam: 102, rise: 28, waistbandH: 4,
    },

    gradingSteps: {
      '28': { waist:-10, hip:-6, thigh:-4, knee:-2, legOpening:-2, outseam:-2, rise:-1 },
      '30': { waist:-5,  hip:-3, thigh:-2, knee:-1, legOpening:-1, outseam:-1, rise:-0.5 },
      '32': {},
      '34': { waist:+5,  hip:+3, thigh:+2, knee:+1, legOpening:+1, outseam:+1, rise:+0.5 },
      '36': { waist:+10, hip:+6, thigh:+4, knee:+2, legOpening:+2, outseam:+2, rise:+1 },
      '38': { waist:+15, hip:+9, thigh:+6, knee:+3, legOpening:+3, outseam:+3, rise:+1.5 },
    },

    buildParts: (m) => {
      // shapeTrouserFront/Back (used everywhere else — PatternEditor, Reshape,
      // standard library) expect halfWaist/halfHip/halfThigh/halfKnee/halfHem +
      // inseam + frontRise/backRise, not this product's raw waist/hip/outseam
      // keys, so translate once here and reuse the *same* accurate generators
      // instead of the old straight-segment SHAPES.trouserFront/Back.
      const inseam = m.outseam - m.rise;
      const fm = {
        halfWaist: m.waist/4+1, halfHip: m.hip/4+1, halfThigh: m.thigh/4+0.5,
        halfKnee: m.knee/4+0.5, halfHem: m.legOpening/4+0.5, frontRise: m.rise, inseam,
      };
      const bm = {
        halfWaist: m.waist/4+2, halfHip: m.hip/4+2, halfThigh: m.thigh/4+1.5,
        halfKnee: m.knee/4+1, halfHem: m.legOpening/4+1, backRise: m.rise + 3, inseam,
      };
      const front = shapeTrouserFront(fm), back = shapeTrouserBack(bm);
      const pocket = shapePocket({ width: 16, height: 19 });
      const welt   = shapeWeltPocket({ width: 14, height: 4 });
      const fly    = shapeFlyPiece({ width: 5.5, height: m.rise * 0.75 });
      return [
      {
        id: 'trs-front', name: 'Front Panel', category: 'front',
        measurements: { ...fm, waistWidth: fm.halfWaist, hipWidth: fm.halfHip, thighWidth: fm.halfThigh, height: front.H },
        tracedPoints: polyToTraced(dedupeLoop(front.pts).map(([x,y]) => [x + front.W, y])),
        gradeTags: front.gradeTags,
        seamAllowances: { top: 1, bottom: 4, left: 1.5, right: 1.5 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'trs-back', name: 'Back Panel', category: 'back',
        measurements: { ...bm, waistWidth: bm.halfWaist, hipWidth: bm.halfHip, height: back.H },
        tracedPoints: polyToTraced(dedupeLoop(back.pts).map(([x,y]) => [x + back.W, y])),
        gradeTags: back.gradeTags,
        seamAllowances: { top: 1, bottom: 4, left: 1.5, right: 1.5 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'trs-waist', name: 'Waistband', category: 'waistband',
        measurements: { width: m.waist/2+5, height: m.waistbandH*2 },
        tracedPoints: polyToTraced(SHAPES.waistband({ halfWaist: m.waist/2+5, bandH: m.waistbandH*2 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 0 },
      },
      {
        id: 'trs-pocket', name: 'Front Pocket Bag', category: 'pocket',
        measurements: { width: 16, height: 19 },
        tracedPoints: polyToTraced(dedupeLoop(pocket.pts).map(([x,y]) => [x + pocket.W, y])),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'trs-welt', name: 'Back Welt Pocket', category: 'pocket',
        measurements: { width: 14, height: 4 },
        tracedPoints: polyToTraced(dedupeLoop(welt.pts).map(([x,y]) => [x + welt.W, y])),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 0 },
      },
      {
        id: 'trs-fly', name: 'Fly Piece', category: 'fly',
        measurements: { width: 5.5, height: m.rise * 0.75 },
        tracedPoints: polyToTraced(dedupeLoop(fly.pts).map(([x,y]) => [x + fly.W, y])),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ];},
  },

  // ────────────────────────────────────────────────────────────────────────────
  tshirt: {
    label: 'T-Shirt',
    icon: '👕',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    baseM: {
      chest: 100, bodyLength: 68, shoulder: 42,
      sleeveLength: 22, crownH: 14, bicep: 36,
      neck: 38, armholeDrop: 20, neckRibH: 4,
    },

    gradingSteps: {
      XS:  { chest:-10, bodyLength:-4, shoulder:-3, sleeveLength:-3, bicep:-3, neck:-2, armholeDrop:-1 },
      S:   { chest:-5,  bodyLength:-2, shoulder:-1.5, sleeveLength:-1.5, bicep:-1.5, neck:-1, armholeDrop:-0.5 },
      M:   {},
      L:   { chest:+5,  bodyLength:+2, shoulder:+1.5, sleeveLength:+1.5, bicep:+1.5, neck:+1, armholeDrop:+0.5 },
      XL:  { chest:+10, bodyLength:+4, shoulder:+3, sleeveLength:+3, bicep:+3, neck:+2, armholeDrop:+1 },
      XXL: { chest:+15, bodyLength:+6, shoulder:+4.5, sleeveLength:+4.5, bicep:+4.5, neck:+3, armholeDrop:+1.5 },
    },

    buildParts: (m) => [
      {
        id: 'tsh-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength, shoulder: m.shoulder/2, neck: m.neck/2 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/6, armDrop: m.armholeDrop, frontNeck: 3.5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength, shoulder: m.shoulder/2 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/7, armDrop: m.armholeDrop, frontNeck: 1, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.bicep * 0.82, bicepW: m.bicep+2, crownH: m.crownH,
          length: m.sleeveLength, isShort: true,
        })),
        seamAllowances: { top: 1, bottom: 2.5, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-rib', name: 'Neck Rib', category: 'trim',
        measurements: { width: m.neck/2, height: m.neckRibH * 2 },
        tracedPoints: polyToTraced(SHAPES.neckRib({ halfNeck: m.neck/2, ribH: m.neckRibH * 2 })),
        seamAllowances: { top: 0.7, bottom: 0.7, left: 0.7, right: 0.7 },
        grainLine: { angleDeg: 0 },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  polo: {
    label: 'Polo T-Shirt',
    icon: '🏌️',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    baseM: {
      chest: 102, bodyLength: 70, shoulder: 43,
      sleeveLength: 24, crownH: 14, bicep: 37,
      neck: 39, armholeDrop: 20,
      collarW: 38, collarH: 7,
      placketW: 3.5, placketH: 18,
    },

    gradingSteps: {
      XS:  { chest:-10, bodyLength:-4, shoulder:-3, sleeveLength:-3, bicep:-3, neck:-2, collarW:-2 },
      S:   { chest:-5,  bodyLength:-2, shoulder:-1.5, sleeveLength:-1.5, bicep:-1.5, neck:-1, collarW:-1 },
      M:   {},
      L:   { chest:+5,  bodyLength:+2, shoulder:+1.5, sleeveLength:+1.5, bicep:+1.5, neck:+1, collarW:+1 },
      XL:  { chest:+10, bodyLength:+4, shoulder:+3,   sleeveLength:+3,   bicep:+3,   neck:+2, collarW:+2 },
      XXL: { chest:+15, bodyLength:+6, shoulder:+4.5, sleeveLength:+4.5, bicep:+4.5, neck:+3, collarW:+3 },
    },

    buildParts: (m) => [
      {
        id: 'pol-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/6+0.5, armDrop: m.armholeDrop, frontNeck: 5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/7, armDrop: m.armholeDrop, frontNeck: 1.5, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.bicep * 0.84, bicepW: m.bicep+2, crownH: m.crownH,
          length: m.sleeveLength, isShort: true,
        })),
        seamAllowances: { top: 1, bottom: 2.5, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-collar', name: 'Collar Rib', category: 'collar',
        measurements: { width: m.collarW, height: m.collarH },
        tracedPoints: polyToTraced(SHAPES.poloCollar({ colW: m.collarW, colH: m.collarH })),
        seamAllowances: { top: 0.7, bottom: 0.7, left: 0.7, right: 0.7 },
        grainLine: { angleDeg: 0 },
      },
      {
        id: 'pol-placket', name: 'Front Placket', category: 'trim',
        measurements: { width: m.placketW*2, height: m.placketH },
        tracedPoints: polyToTraced(SHAPES.placket({ plW: m.placketW*2, plH: m.placketH })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ],
  },
};

// Build a full set of NPF pattern objects from a product at a given base size
const buildProductPatterns = (productKey, styleId, styleName) => {
  const product = PRODUCT_LIBRARY[productKey];
  if (!product) return [];
  const m = { ...product.baseM };
  return product.buildParts(m).map(part => ({
    format: 'npf-v1',
    id:     `${productKey}-${part.id}-${Date.now()}`,
    partId: `${styleId}-${part.id}`,
    partName:    part.name,
    category:    part.category,
    product:     productKey,
    template:    part.id,
    measurements: part.measurements,
    tracedPoints: part.tracedPoints,
    seamAllowances: part.seamAllowances,
    grainLine:   part.grainLine,
    notches: [], darts: [], measurementPoints: [],
    gradingRules: {},
    specMapping: {}, brandRules: [], svgOutline: '',
    createdOn: new Date().toISOString(), updatedOn: new Date().toISOString(),
  }));
};

// Get grading rules in MarkerMaker's internal format { patId: { size: { key: delta } } }
const buildProductGradingRules = (productKey, patterns) => {
  const product = PRODUCT_LIBRARY[productKey];
  if (!product) return {};
  const rules = {};
  patterns.forEach(pat => {
    rules[pat.id] = {};
    Object.entries(product.gradingSteps).forEach(([sz, deltas]) => {
      rules[pat.id][sz] = { ...deltas };
    });
  });
  return rules;
};

// ============================================================================
//  STEP WIZARD HEADER
// ============================================================================
const STEPS = [
  { id: 1, label: 'Style',      icon: '🗂️' },
  { id: 2, label: 'Patterns',   icon: '📐' },
  { id: 3, label: 'Spec Sheet', icon: '📋' },
  { id: 4, label: 'Cross-Check',icon: '✅' },
  { id: 5, label: 'Edit',       icon: '✏️' },
  { id: 6, label: 'Grading',    icon: '📏' },
  { id: 7, label: 'Seam Check', icon: '🔗' },
  { id: 8, label: 'Marker',     icon: '🗺️' },
];

const WizardHeader = ({ step, onStepClick }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '10px 16px', background: 'white', borderBottom: '1px solid #DEE2E6', overflowX: 'auto' }}>
    {STEPS.map((s, i) => (
      <React.Fragment key={s.id}>
        <button
          onClick={() => onStepClick(s.id)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 2, padding: '6px 10px', borderRadius: 8, border: 'none',
            background: step === s.id ? '#1A2A3A' : step > s.id ? '#C8E6C9' : '#F8F9FA',
            color: step === s.id ? 'white' : step > s.id ? '#166534' : '#ADB5BD',
            cursor: 'pointer', minWidth: 64, flexShrink: 0, fontSize: 9,
            fontWeight: step === s.id ? 'bold' : 'normal',
          }}
        >
          <span style={{ fontSize: 14 }}>{step > s.id ? '✓' : s.icon}</span>
          <span>{s.label}</span>
        </button>
        {i < STEPS.length - 1 && (
          <ChevronRight size={12} color={step > s.id + 1 ? '#43A047' : '#CED4DA'} style={{ flexShrink: 0 }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============================================================================
//  STEP 1 — STYLE SELECTOR
//  Same filter categories as Workstation.js (brand, season, product, fabric)
// ============================================================================
const StyleSelector = ({ db, onSelect }) => {
  // ── Self-loading: fetch directly if parent db hasn't populated yet ─────────
  // Root cause of "styles not showing": db.masterStyles doesn't exist;
  // App.js stores styles as db.mgmtStyles. Also fetch from API as fallback.
  const [localStyles, setLocalStyles]       = useState(db?.mgmtStyles || []);
  const [stylesLoading, setStylesLoading]   = useState(false);
  const [stylesError, setStylesError]       = useState('');

  useEffect(() => {
    // Use parent db if already populated
    const fromDb = db?.mgmtStyles || [];
    if (fromDb.length > 0) { setLocalStyles(fromDb); return; }
    // Otherwise fetch directly — covers the case where MarkerMaker renders
    // before App.js finishes loading get_all_data.php
    setStylesLoading(true);
    setStylesError('');
    fetch(`${API_BASE}/get_all_data.php`)
      .then(r => r.json())
      .then(data => {
        const s = data.mgmtStyles || data.masterStyles || [];
        if (s.length === 0) setStylesError('No styles found in database. Save a style from Workstation first.');
        setLocalStyles(s);
      })
      .catch(() => setStylesError('Could not connect to database. Check API URL.'))
      .finally(() => setStylesLoading(false));
  }, []); // eslint-disable-line

  // Keep in sync if parent db updates after mount
  useEffect(() => {
    if ((db?.mgmtStyles || []).length > 0) setLocalStyles(db.mgmtStyles);
  }, [db?.mgmtStyles]);

  const styles = localStyles;
  const [filters, setFilters] = useState({ brand: '', season: '', product: '', fabric: '', search: '' });

  const getField = (s, fields, fallback = '') =>
    fields.map(f => s[f]).find(v => v) || fallback;

  const filtered = useMemo(() => styles.filter(s => {
    const brand   = getField(s, ['brand','brandName','Brand'], '');
    const season  = getField(s, ['season','Season','seasonCode'], '');
    const product = getField(s, ['productType','product','garmentType','garmentCategory','category'], '');
    const fabric  = getField(s, ['fabricType','fabric','fabricComposition','material'], '');
    const text    = [s.name || '', s.styleNo || '', s.styleCode || ''].join(' ').toLowerCase();
    return (
      (!filters.brand   || brand.toLowerCase().includes(filters.brand.toLowerCase())) &&
      (!filters.season  || season.toLowerCase().includes(filters.season.toLowerCase())) &&
      (!filters.product || product.toLowerCase().includes(filters.product.toLowerCase())) &&
      (!filters.fabric  || fabric.toLowerCase().includes(filters.fabric.toLowerCase())) &&
      (!filters.search  || text.includes(filters.search.toLowerCase()))
    );
  }), [styles, filters]);

  // Derive unique options for each filter
  const options = useMemo(() => ({
    brand:   [...new Set(styles.map(s => getField(s, ['brand','brandName','Brand'])).filter(Boolean))],
    season:  [...new Set(styles.map(s => getField(s, ['season','Season','seasonCode'])).filter(Boolean))],
    product: [...new Set(styles.map(s => getField(s, ['productType','product','garmentType'])).filter(Boolean))],
    fabric:  [...new Set(styles.map(s => getField(s, ['fabricType','fabric','material'])).filter(Boolean))],
  }), [styles]);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 12, color: '#1A2A3A' }}>
        🗂️ Select a Style to load patterns
      </div>

      {/* Filter row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12, background: '#f0f9ff', padding: 10, borderRadius: 8 }}>
        <div>
          <label style={{ fontSize: 9, fontWeight: 'bold', color: '#0c4a6e', textTransform: 'uppercase' }}>Search</label>
          <input value={filters.search} onChange={e => setF('search', e.target.value)} placeholder="Style name / code…"
            style={{ width: '100%', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }} />
        </div>
        {['brand', 'season', 'product', 'fabric'].map(k => (
          <div key={k}>
            <label style={{ fontSize: 9, fontWeight: 'bold', color: '#0c4a6e', textTransform: 'uppercase' }}>{k}</label>
            <select value={filters[k]} onChange={e => setF(k, e.target.value)}
              style={{ width: '100%', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }}>
              <option value="">All</option>
              {(options[k] || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>

      {/* Loading / error state */}
      {stylesLoading && (
        <div style={{ textAlign:'center', padding:30, color:'#6A1B9A', fontSize:12 }}>
          ⟳ Loading styles from database…
        </div>
      )}
      {stylesError && !stylesLoading && (
        <div style={{ background:'#FFEBEE', border:'1px solid #fecaca', borderRadius:8, padding:12, marginBottom:10, fontSize:11, color:'#C62828' }}>
          ⚠ {stylesError}
        </div>
      )}

      {/* Results */}
      {!stylesLoading && filtered.length === 0 && !stylesError ? (
        <div style={{ textAlign: 'center', color: '#ADB5BD', padding: 40, fontSize: 12 }}>
          No styles found. Adjust filters or make sure styles are saved via Workstation.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {filtered.map(s => (
            <button key={s.id} onClick={() => onSelect(s)}
              style={{ background: 'white', border: '1px solid #DEE2E6', borderRadius: 8, padding: 12, cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {s.previewImage && <img src={s.previewImage} alt="" style={{ width: '100%', height: 80, objectFit: 'contain', marginBottom: 6, borderRadius: 4 }} />}
              <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1A2A3A', marginBottom: 3 }}>{s.name || s.styleCode || 'Untitled Style'}</div>
              <div style={{ fontSize: 10, color: '#6C757D' }}>
                {getField(s, ['brand','brandName']) && <span style={{ marginRight: 6 }}>🏷 {getField(s, ['brand','brandName'])}</span>}
                {getField(s, ['season','seasonCode']) && <span>📅 {getField(s, ['season','seasonCode'])}</span>}
              </div>
              <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>
                {(s.parts || []).length} part(s) · {(s.patterns || []).length} pattern(s) stored
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 2 — PATTERN LOADER
//  Shows all patterns stored with the selected style. Allows adding missing
//  patterns from PartDNA or creating blank ones.
// ============================================================================
// ============================================================================
//  DRAW FROM SCRATCH — measurement-accurate freehand pattern drafting
// ----------------------------------------------------------------------------
//  Click to place a corner node; click-and-drag to place a smooth (bezier)
//  node, same gesture as a standard pen tool. A live cm grid + rubber-band
//  readout give mouse precision; the "Length / Angle" fields let you pin the
//  last point to an exact measurement instead of relying on the mouse.
//  "Mirror across CF" drafts one symmetric half and auto-completes the other.
//  Finishing produces a normal pattern object (tracedPoints + measurements)
//  that flows straight into Grading and the Marker step like any other piece.
// ============================================================================
const DrawFromScratch = ({ onSave, onCancel }) => {
  const [pts, setPts]         = useState([]); // {x,y,smooth,hIn,hOut,pinned,corner}
  const [closed, setClosed]   = useState(false);
  const [zoom, setZoom]       = useState(1);
  const [pan, setPan]         = useState({ x: 0, y: 0 });
  const [snapGrid, setSnapGrid] = useState(true);
  const [gridSize, setGridSize] = useState(1);
  const [mirror, setMirror]   = useState(false);
  const [pending, setPending] = useState(null); // {startCm}
  const [hoverCm, setHoverCm] = useState(null);
  const [manualLen, setManualLen]     = useState('');
  const [manualAngle, setManualAngle] = useState('');
  const [pieceName, setPieceName]     = useState('');
  const [category, setCategory]       = useState('panel');
  const templateMeasurementsRef = useRef(null); // richer named measurements (halfWaist, inseam, etc) from a loaded template, if any
  const templateGradeTagsRef = useRef(null);    // matching per-point grade tags, valid only while the point count/order is unchanged
  const [seam, setSeam] = useState({ top: 1, right: 1, bottom: 1, left: 1 });
  const [autoSmooth, setAutoSmooth] = useState(false);   // OFF by default now — see the pen-tool rework below. When on, plain clicks still get an ambient curve guess; off (default), a point is a corner unless you explicitly drag out a handle for it.
  const [showNodes, setShowNodes]   = useState(true);   // hide dots to see the pure curve
  const [mergeDist, setMergeDist]   = useState(1);       // cm — "too-close" cleanup threshold
  const [elastic, setElastic]       = useState(true);     // "rope" drag — moving one point carries neighbours with it
  const [elasticRadius, setElasticRadius] = useState(3);  // how many neighbouring points feel the pull
  const [selIdx, setSelIdx]         = useState(null);    // selected node (for delete/copy/toggle)
  const [clipboard, setClipboard]   = useState(null);    // copied node
  const [history, setHistory]       = useState([]);      // undo stack: [{pts, closed}]
  const [future, setFuture]         = useState([]);       // redo stack
  const [mode, setMode]             = useState('draw');  // 'draw' | 'pan' | 'moveImage' | 'moveSketch'
  // Reference image to trace over
  const [img, setImg]         = useState(null);   // dataURL
  const [imgW, setImgW]       = useState(50);      // real-world width, cm
  const [imgH, setImgH]       = useState(50);
  const [imgPos, setImgPos]   = useState({ x: 0, y: 0 }); // cm, image's bottom-left
  const [imgOpacity, setImgOpacity] = useState(0.6);
  const [imgLocked, setImgLocked]   = useState(false);
  const svgRef = useRef(null);
  const fileRef = useRef(null);
  const dragRef = useRef(null); // generic drag tracker for pan / move-image / move-sketch
  const [canvasReady, setCanvasReady] = useState(false);

  // The canvas now fills available space responsively (width/height:100%
  // over a fixed viewBox), which means its on-screen size depends on layout
  // finishing — flexbox reflow, the modal's own mount transition, etc. If
  // the very first click lands before that settles, getScreenCTM() can
  // still reflect a stale/incomplete layout and the click maps to the wrong
  // canvas coordinate — exactly a "first click is always off" symptom,
  // since by the second click layout has already settled. Force a layout
  // read on mount (and once more a frame later, to be safe against
  // multi-pass reflows) before accepting any clicks.
  useEffect(() => {
    const settle = () => { if (svgRef.current) { svgRef.current.getBoundingClientRect(); setCanvasReady(true); } };
    settle();
    const raf = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(raf);
  }, []);

  const BASE_SC = 7, CW = 1000, CH = 700;
  const SC = BASE_SC * zoom;
  const OX = CW / 2 + pan.x, OY = CH - 50 + pan.y;
  const tx = x => OX + x * SC;
  const ty = y => OY - y * SC;

  const getSvgPt = (e) => {
    const svg = svgRef.current; if (!svg) return { x: 0, y: 0 };
    svg.getBoundingClientRect(); // force a fresh layout read before trusting getScreenCTM — see canvasReady effect above
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return { x: 0, y: 0 };
    const sp = pt.matrixTransform(ctm.inverse());
    return { x: sp.x, y: sp.y };
  };
  const toCm = (sx, sy) => ({ x: (sx - OX) / SC, y: (OY - sy) / SC });
  const snap = v => snapGrid ? +(Math.round(v / gridSize) * gridSize).toFixed(2) : +v.toFixed(2);

  // Push the current state onto the undo stack — call before any mutating action
  const pushHistory = () => { setHistory(h => [...h.slice(-49), { pts: pts.map(p=>({...p})), closed }]); setFuture([]); };
  const undo = () => {
    setHistory(h => {
      if (!h.length) return h;
      const last = h[h.length-1];
      setFuture(f => [...f.slice(-49), { pts: pts.map(p=>({...p})), closed }]);
      setPts(last.pts); setClosed(last.closed); setSelIdx(null);
      return h.slice(0, -1);
    });
  };
  const redo = () => {
    setFuture(f => {
      if (!f.length) return f;
      const next = f[f.length-1];
      setHistory(h => [...h.slice(-49), { pts: pts.map(p=>({...p})), closed }]);
      setPts(next.pts); setClosed(next.closed); setSelIdx(null);
      return f.slice(0, -1);
    });
  };

  // Spacebar-hold pans the canvas (same convention as Photoshop/Figma) —
  // release to snap straight back to whatever tool you were using, so you
  // don't have to keep clicking the Pan button to reposition mid-draw.
  const modeBeforeSpaceRef = useRef(null);

  // Keyboard shortcuts: ⌘/Ctrl+Z undo, ⌘/Ctrl+Shift+Z redo, Delete/Backspace
  // remove selected node, ⌘/Ctrl+C / ⌘/Ctrl+V copy/paste selected node,
  // hold Space to pan. Ignored while typing in a text field.
  useEffect(() => {
    const isTyping = () => ['INPUT','SELECT','TEXTAREA'].includes(document.activeElement?.tagName);
    const onKey = (e) => {
      if (isTyping()) return;
      if (e.code === 'Space' && !e.repeat && modeBeforeSpaceRef.current === null) {
        e.preventDefault();
        modeBeforeSpaceRef.current = mode;
        setMode('pan');
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z' && e.shiftKey) { e.preventDefault(); redo(); return; }
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if (mod && e.key.toLowerCase() === 'c' && selIdx != null) {
        e.preventDefault();
        setClipboard({ ...pts[selIdx] }); return;
      }
      if (mod && e.key.toLowerCase() === 'v' && clipboard) {
        e.preventDefault();
        pushHistory();
        const insertAt = (selIdx != null ? selIdx : pts.length - 1) + 1;
        const newPt = { ...clipboard, x: +(clipboard.x + 1.5).toFixed(2), y: +(clipboard.y + 1.5).toFixed(2) };
        setPts(prev => {
          const next = [...prev]; next.splice(insertAt, 0, newPt);
          return autoSmooth ? recomputeAutoTangents(next, closed) : next;
        });
        setSelIdx(insertAt);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selIdx != null) {
        e.preventDefault();
        deleteSelected();
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space' && modeBeforeSpaceRef.current !== null) {
        setMode(modeBeforeSpaceRef.current);
        modeBeforeSpaceRef.current = null;
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp); };
  }, [pts, closed, selIdx, clipboard, autoSmooth, mode]);

  // ══════════════════════════════════════════════════════════════════════
  //  AUTO-TRACE — click once inside the garment in the reference image and
  //  this extracts the actual outline automatically (flood-fill from the
  //  click, walk the resulting region's boundary, simplify to a clean point
  //  count), instead of hand-clicking every point. This is what actually
  //  removes the "click's-own-error becomes a wrong curve" problem — the
  //  outline comes from the photo's real edge, not from mouse precision.
  // ══════════════════════════════════════════════════════════════════════
  const [autoTraceBusy, setAutoTraceBusy] = useState(false);
  const [traceThreshold, setTraceThreshold] = useState(40); // colour-distance tolerance

  const runAutoTrace = async (seedCmX, seedCmY) => {
    if (!img) return;
    setAutoTraceBusy(true);
    try {
      const imageEl = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = img;
      });
      // Downsample for speed and to smooth over JPEG/compression noise —
      // sub-cm accuracy isn't available from a photo trace anyway, and a
      // smaller image also makes the boundary walk dramatically faster.
      const maxDim = 520;
      const scaleDown = Math.min(1, maxDim / Math.max(imageEl.naturalWidth, imageEl.naturalHeight));
      const procW = Math.max(1, Math.round(imageEl.naturalWidth * scaleDown));
      const procH = Math.max(1, Math.round(imageEl.naturalHeight * scaleDown));
      const canvas = document.createElement('canvas');
      canvas.width = procW; canvas.height = procH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imageEl, 0, 0, procW, procH);
      const imgData = ctx.getImageData(0, 0, procW, procH).data;

      // Seed pixel from the click, via the same cm↔image calibration used
      // to display the reference image (imgPos/imgW/imgH).
      const fracX = (seedCmX - imgPos.x) / imgW;
      const fracY = 1 - (seedCmY - imgPos.y) / imgH;
      const seedX = Math.round(fracX * procW), seedY = Math.round(fracY * procH);
      if (seedX < 0 || seedY < 0 || seedX >= procW || seedY >= procH) {
        alert('Click somewhere inside the reference image, on the garment itself.');
        return;
      }

      // ── Flood fill from the seed, accepting pixels within colour distance ──
      const idx = (x,y) => (y*procW+x)*4;
      const si = idx(seedX, seedY);
      const seedColor = [imgData[si], imgData[si+1], imgData[si+2]];
      const colorDist = (i) => {
        const dr=imgData[i]-seedColor[0], dg=imgData[i+1]-seedColor[1], db=imgData[i+2]-seedColor[2];
        return Math.sqrt(dr*dr+dg*dg+db*db);
      };
      const visited = new Uint8Array(procW*procH);
      const stack = [[seedX,seedY]];
      visited[seedY*procW+seedX] = 1;
      let minX=seedX,maxX=seedX,minY=seedY,maxY=seedY, count=0, touchedEdge=false;
      const CAP = 1_500_000;
      while (stack.length && count < CAP) {
        const [x,y] = stack.pop();
        count++;
        if (x<minX) minX=x; if (x>maxX) maxX=x;
        if (y<minY) minY=y; if (y>maxY) maxY=y;
        if (x===0||y===0||x===procW-1||y===procH-1) touchedEdge = true;
        const neigh = [[x+1,y],[x-1,y],[x,y+1],[x,y-1]];
        for (const [nx,ny] of neigh) {
          if (nx<0||ny<0||nx>=procW||ny>=procH) continue;
          const vi = ny*procW+nx;
          if (visited[vi]) continue;
          if (colorDist(idx(nx,ny)) <= traceThreshold) { visited[vi]=1; stack.push([nx,ny]); }
        }
      }
      const areaFrac = count / (procW*procH);
      if (touchedEdge || areaFrac > 0.85) {
        alert('The traced region spilled into the background (too much of the image got selected). Try a lower sensitivity, or click more centrally inside the garment, away from busy/low-contrast edges.');
        return;
      }
      if (count < 200) {
        alert('Could not find a distinct region there — try clicking somewhere with clearer contrast against the background, or raise the sensitivity.');
        return;
      }

      // ── Moore-neighbour boundary trace on the filled region ──
      const isInside = (x,y) => x>=0&&y>=0&&x<procW&&y<procH&&visited[y*procW+x]===1;
      let start=null;
      outer:
      for (let y=minY;y<=maxY;y++) for (let x=minX;x<=maxX;x++) { if (isInside(x,y)) { start=[x,y]; break outer; } }
      const dirs8 = [[0,-1],[1,-1],[1,0],[1,1],[0,1],[-1,1],[-1,0],[-1,-1]];
      const contour = [start];
      let cur = start, b = [start[0]-1, start[1]];
      let steps = 0; const MAX_STEPS = 60000;
      do {
        const dx=b[0]-cur[0], dy=b[1]-cur[1];
        let dIdx = dirs8.findIndex(([ddx,ddy])=>ddx===dx&&ddy===dy);
        if (dIdx<0) dIdx=0;
        let found=false;
        for (let k=1;k<=8;k++){
          const ni=(dIdx+k)%8, [ox,oy]=dirs8[ni], nx=cur[0]+ox, ny=cur[1]+oy;
          if (isInside(nx,ny)) { b=[cur[0]+dirs8[(dIdx+k-1)%8][0], cur[1]+dirs8[(dIdx+k-1)%8][1]]; cur=[nx,ny]; found=true; break; }
        }
        if (!found) break;
        contour.push(cur); steps++;
      } while (!(cur[0]===start[0]&&cur[1]===start[1]) && steps<MAX_STEPS);

      // ── Simplify (Douglas-Peucker on a closed loop) — targets a clean,
      // editable point count instead of hundreds of raw boundary pixels ──
      const perpDist = (p,a,c) => {
        const dx=c[0]-a[0], dy=c[1]-a[1], len=Math.hypot(dx,dy)||1;
        return Math.abs((p[0]-a[0])*dy - (p[1]-a[1])*dx) / len;
      };
      const simplifySeg = (arr, eps) => {
        if (arr.length<3) return arr;
        let maxD=0, mi=0;
        for (let i=1;i<arr.length-1;i++){ const d=perpDist(arr[i],arr[0],arr[arr.length-1]); if (d>maxD){maxD=d;mi=i;} }
        if (maxD>eps) return [...simplifySeg(arr.slice(0,mi+1),eps).slice(0,-1), ...simplifySeg(arr.slice(mi),eps)];
        return [arr[0], arr[arr.length-1]];
      };
      const simplifyClosed = (pts, eps) => {
        const half = Math.floor(pts.length/2);
        const p1 = simplifySeg(pts.slice(0,half+1), eps), p2 = simplifySeg(pts.slice(half), eps);
        return [...p1.slice(0,-1), ...p2.slice(0,-1)];
      };
      const diag = Math.hypot(procW, procH);
      let eps = diag * 0.006, simplified = simplifyClosed(contour, eps);
      // Adapt epsilon to land in a workable point-count range
      for (let i=0; i<6 && (simplified.length > 70 || simplified.length < 16); i++) {
        eps *= simplified.length > 70 ? 1.5 : 0.6;
        simplified = simplifyClosed(contour, eps);
      }

      // ── Convert to cm using the same calibration as image display ──
      const newPts = simplified.map(([px,py]) => {
        const fx = px/procW, fy = py/procH;
        return {
          x: +(imgPos.x + fx*imgW).toFixed(2),
          y: +(imgPos.y + (1-fy)*imgH).toFixed(2),
          smooth: false, hIn: null, hOut: null,
        };
      });

      pushHistory();
      const clean = dedupeLoop(newPts.map(p=>[p.x,p.y])).map(([x,y]) => ({x,y,smooth:false,hIn:null,hOut:null}));
      setPts(recomputeAutoTangents(clean, true));
      setClosed(true);
      setMode('draw');
    } catch (e) {
      alert('Auto-trace failed: ' + e.message);
    } finally {
      setAutoTraceBusy(false);
    }
  };

  // ── Handle an uploaded reference image ──────────────────────────────────
  const onImgUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      const probe = new Image();
      probe.onload = () => {
        // Default to a sensible on-canvas size, keeping the image's aspect
        // ratio; the width/height fields let the user calibrate it to the
        // real garment measurement before tracing.
        const aspect = probe.height / probe.width;
        setImgW(60); setImgH(+(60 * aspect).toFixed(1));
        setImgPos({ x: -30, y: 0 });
        setImg(url);
      };
      probe.src = url;
    };
    reader.readAsDataURL(file);
  };

  // ── Catmull-Rom auto-smoothing ──────────────────────────────────────────
  // Every plain click just places a point; the curve automatically flows
  // smoothly through it by deriving a tangent from its neighbours (uniform
  // Catmull-Rom → symmetric bezier handle). This replaces having to
  // precisely drag a handle at every single point to avoid a lumpy curve.
  // A point becomes "pinned" (excluded from auto-recompute) the moment you
  // manually drag its handle, so hand-tuned curves aren't overwritten.
  const recomputeAutoTangents = (list, isClosed) => {
    const n = list.length;
    if (n < 3) return list;
    return list.map((p, i) => {
      if (p.corner || p.pinned) return p;
      if (!isClosed && (i === 0 || i === n - 1)) return { ...p, smooth: false, hIn: null, hOut: null };
      const prev = list[(i - 1 + n) % n], next = list[(i + 1) % n];
      const dx = +((next.x - prev.x) / 6).toFixed(2), dy = +((next.y - prev.y) / 6).toFixed(2);
      return { ...p, smooth: true, hOut: [dx, dy], hIn: [-dx, -dy] };
    });
  };

  const deleteSelected = () => {
    if (selIdx == null) return;
    if (pts.length <= 3) { alert('A shape needs at least 3 points.'); return; }
    pushHistory();
    setPts(prev => {
      const next = prev.filter((_,i) => i !== selIdx);
      return autoSmooth ? recomputeAutoTangents(next, closed) : next;
    });
    setSelIdx(null);
  };

  // ── Dart tool ────────────────────────────────────────────────────────────
  // A single dragged-out point makes a rounded bump under the smooth curve
  // math (correct for most edits, but a dart needs a genuine sharp V, not a
  // bulge). This inserts THREE points at once — two close together right on
  // the original line (the dart's "mouth"), both hard corners, plus the
  // dragged tip between them, also a hard corner — so it reads as a real
  // notch regardless of the auto-smoothing everywhere else on the shape.
  const dartRef = useRef(null);
  const startDart = (svgPt) => {
    if (pts.length < 2) return;
    const edge = nearestEdge(pts.map(p=>[p.x,p.y]), svgPt.x, svgPt.y, tx, ty);
    const a = pts[edge.edgeIdx], b = pts[(edge.edgeIdx+1) % pts.length];
    const mouth = { x: +(a.x+(b.x-a.x)*edge.t).toFixed(2), y: +(a.y+(b.y-a.y)*edge.t).toFixed(2) };
    // Mouth half-width as a fraction of this edge's own length, so the dart
    // scales sensibly whether the edge is 2cm or 20cm long.
    const edgeLen = Math.hypot(b.x-a.x, b.y-a.y) || 1;
    const half = Math.min(edgeLen*0.06, 1.2);
    const dirx = (b.x-a.x)/edgeLen, diry = (b.y-a.y)/edgeLen;
    const mouthStart = { x:+(mouth.x-dirx*half).toFixed(2), y:+(mouth.y-diry*half).toFixed(2), corner:true, smooth:false, hIn:null, hOut:null };
    const mouthEnd   = { x:+(mouth.x+dirx*half).toFixed(2), y:+(mouth.y+diry*half).toFixed(2), corner:true, smooth:false, hIn:null, hOut:null };
    const tipStart    = { x: mouth.x, y: mouth.y, corner:true, smooth:false, hIn:null, hOut:null };
    dartRef.current = { edgeIdx: edge.edgeIdx };
    pushHistory();
    setPts(prev => {
      const next = [...prev];
      next.splice(edge.edgeIdx+1, 0, mouthStart, tipStart, mouthEnd);
      return next; // corners are excluded from auto-tangent recompute, so no need to re-smooth here
    });
    setSelIdx(edge.edgeIdx+2); // the tip point, ready to drag
    dragRef.current = { kind:'node', idx: edge.edgeIdx+2, lastCm: toCm(svgPt.x, svgPt.y), moved:false };
  };

  const HIT_R = 9;      // generous invisible hit-test radius, independent of the visual dot size
  const DOT_R = 2.6;    // small visible marker so it doesn't obscure exact placement
  const firstScreen = pts.length ? [tx(pts[0].x), ty(pts[0].y)] : null;

  // Handle screen positions for a smooth point — used for both rendering
  // and hit-testing the little draggable squares.
  const handlePos = (i, side) => {
    const p = pts[i]; const h = side === 'hOut' ? p.hOut : p.hIn;
    if (!p.smooth || !h) return null;
    return [tx(p.x + h[0]), ty(p.y + h[1])];
  };

  const onDown = (e) => {
    const p = getSvgPt(e);
    if (mode === 'autoTrace') {
      if (autoTraceBusy) return;
      const cm = toCm(p.x, p.y);
      runAutoTrace(cm.x, cm.y);
      return;
    }
    if (mode === 'pan') {
      dragRef.current = { kind:'pan', startClientX: e.clientX, startClientY: e.clientY, startPan: { ...pan } };
      return;
    }
    if (mode === 'moveImage') {
      if (imgLocked) return;
      const cm = toCm(p.x, p.y);
      dragRef.current = { kind:'image', startCm: cm, startImgPos: { ...imgPos } };
      return;
    }
    if (mode === 'moveSketch') {
      pushHistory();
      const cm = toCm(p.x, p.y);
      dragRef.current = { kind:'sketch', startCm: cm, startPts: pts.map(pt=>({...pt})) };
      return;
    }
    if (mode === 'addDart') {
      startDart(p);
      return;
    }
    // 'draw' mode below — closing the shape still takes priority over
    // grabbing/dragging when there are enough points and you click near the
    // gold start point.
    if (!closed && pts.length >= 3 && firstScreen && Math.hypot(firstScreen[0]-p.x, firstScreen[1]-p.y) <= HIT_R) {
      pushHistory();
      setClosed(true);
      setPts(prev => recomputeAutoTangents(prev, true));
      return;
    }
    // Handles first — smaller, more precise targets, and they sit close to
    // their own point so they must win the hit-test over it.
    for (let i = 0; i < pts.length; i++) {
      for (const side of ['hOut','hIn']) {
        const hp = handlePos(i, side);
        if (hp && Math.hypot(hp[0]-p.x, hp[1]-p.y) <= HIT_R) {
          pushHistory();
          setSelIdx(i);
          dragRef.current = { kind:'handle', idx:i, side, lastCm: toCm(p.x, p.y) };
          return;
        }
      }
    }
    // Grab an existing point — like picking up a spot on a piece of rope.
    // A plain click (no movement before mouseup) still just selects it; any
    // movement repositions it, and — when Elastic is on — carries nearby
    // points along with a falloff instead of putting a kink at that one spot.
    for (let i = 0; i < pts.length; i++) {
      const [nx, ny] = [tx(pts[i].x), ty(pts[i].y)];
      if (Math.hypot(nx-p.x, ny-p.y) <= HIT_R) {
        pushHistory();
        setSelIdx(i);
        dragRef.current = { kind:'node', idx: i, lastCm: toCm(p.x, p.y), moved:false };
        return;
      }
    }
    // Grab a spot ON the curve between two points (not an existing node) —
    // inserts a point right there and picks it up immediately, so you can
    // pull any part of the outline like a rope, not just where a node
    // already happens to be.
    if (pts.length >= 2) {
      const edge = nearestEdge(pts.map(pt=>[pt.x,pt.y]), p.x, p.y, tx, ty);
      const edgeDist = Math.hypot(p.x - edge.snapX, p.y - edge.snapY);
      if (edgeDist <= 13) {
        pushHistory();
        const a = pts[edge.edgeIdx], b = pts[(edge.edgeIdx+1) % pts.length];
        const newPt = { x: +(a.x+(b.x-a.x)*edge.t).toFixed(2), y: +(a.y+(b.y-a.y)*edge.t).toFixed(2), smooth:false, hIn:null, hOut:null };
        const insertAt = edge.edgeIdx + 1;
        setPts(prev => {
          const next = [...prev]; next.splice(insertAt, 0, newPt);
          return autoSmooth ? recomputeAutoTangents(next, closed) : next;
        });
        setSelIdx(insertAt);
        dragRef.current = { kind:'node', idx: insertAt, lastCm: toCm(p.x, p.y), moved:false };
        return;
      }
    }
    if (closed) { setSelIdx(null); return; } // empty space, shape already closed — just deselect
    setSelIdx(null);
    const cm = toCm(p.x, p.y);
    pushHistory();
    setPending({ startCm: { x: snap(cm.x), y: snap(cm.y) } });
  };

  const onDblClick = (e) => {
    if (mode !== 'draw') return;
    const p = getSvgPt(e);
    for (let i = 1; i < pts.length; i++) {
      const [nx, ny] = [tx(pts[i].x), ty(pts[i].y)];
      if (Math.hypot(nx-p.x, ny-p.y) <= HIT_R) { toggleCorner(i); return; }
    }
  };

  // Scroll to zoom — keeps mouse position roughly anchored rather than
  // always zooming toward the canvas centre.
  const onWheel = (e) => {
    e.preventDefault();
    const before = getSvgPt(e);
    const cmBefore = toCm(before.x, before.y);
    const newZoom = Math.max(0.3, Math.min(2.5, zoom + (e.deltaY < 0 ? 0.12 : -0.12)));
    const newSC = BASE_SC * newZoom;
    // Solve for the pan that keeps cmBefore under the cursor at the new zoom
    setPan(p => ({
      x: before.x - CW/2 - cmBefore.x*newSC,
      y: before.y - (CH-50) + cmBefore.y*newSC,
    }));
    setZoom(newZoom);
  };

  const onMove = (e) => {
    setHoverCm(toCm(getSvgPt(e).x, getSvgPt(e).y));
    const d = dragRef.current;
    if (!d) return;
    if (d.kind === 'pan') {
      setPan({ x: e.clientX - d.startClientX + d.startPan.x, y: e.clientY - d.startClientY + d.startPan.y });
    } else if (d.kind === 'image') {
      const cm = toCm(getSvgPt(e).x, getSvgPt(e).y);
      const dx = cm.x - d.startCm.x, dy = cm.y - d.startCm.y;
      setImgPos({ x: +(d.startImgPos.x + dx).toFixed(2), y: +(d.startImgPos.y + dy).toFixed(2) });
    } else if (d.kind === 'sketch') {
      const cm = toCm(getSvgPt(e).x, getSvgPt(e).y);
      const dx = cm.x - d.startCm.x, dy = cm.y - d.startCm.y;
      setPts(d.startPts.map(p => ({ ...p, x: +(p.x+dx).toFixed(2), y: +(p.y+dy).toFixed(2) })));
    } else if (d.kind === 'node') {
      const cmNow = toCm(getSvgPt(e).x, getSvgPt(e).y);
      const dx = cmNow.x - d.lastCm.x, dy = cmNow.y - d.lastCm.y;
      if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return;
      d.moved = true;
      setPts(prev => {
        let next = prev.map((pt,i) => i===d.idx ? { ...pt, x:+(pt.x+dx).toFixed(2), y:+(pt.y+dy).toFixed(2) } : pt);
        if (elastic) {
          const beforeElastic = next;
          next = applyProportionalNudge(next, d.idx, dx, dy, elasticRadius);
          // Hard corners (e.g. dart mouths) shouldn't get softened by a
          // nearby elastic pull — they're deliberate, precise vertices.
          next = next.map((pt,i) => pt.corner ? beforeElastic[i] : pt);
        }
        return autoSmooth ? recomputeAutoTangents(next, closed) : next;
      });
      d.lastCm = cmNow;
    } else if (d.kind === 'handle') {
      // Dragging a handle square adjusts ONLY that curve tangent — direct,
      // exact, no inference. By default the opposite handle mirrors it
      // (standard smooth-point behaviour); hold Alt to break the mirror and
      // shape each side of the curve independently (a "corner point with
      // curves on both sides", same as Illustrator's Alt-drag).
      const cmNow = toCm(getSvgPt(e).x, getSvgPt(e).y);
      setPts(prev => prev.map((pt,i) => {
        if (i !== d.idx) return pt;
        const newH = [+(cmNow.x-pt.x).toFixed(2), +(cmNow.y-pt.y).toFixed(2)];
        const mirrored = [-newH[0], -newH[1]];
        const breakMirror = !!e.altKey;
        if (d.side === 'hOut') {
          return { ...pt, smooth:true, pinned:true, hOut:newH, hIn: breakMirror ? pt.hIn : mirrored };
        } else {
          return { ...pt, smooth:true, pinned:true, hIn:newH, hOut: breakMirror ? pt.hOut : mirrored };
        }
      }));
    }
  };
  const onUp = (e) => {
    if (dragRef.current) { dragRef.current = null; return; }
    if (!pending) return;
    // Pen-tool behaviour, same as Illustrator/Photoshop/Figma: a plain
    // click places a hard corner exactly where you clicked — a straight
    // line, no ambiguity, always. Click-and-drag places a smooth point and
    // sets its curve handle to EXACTLY the vector you dragged, symmetric on
    // both sides — you're directly shaping the curve, not hoping an
    // algorithm infers it from neighbouring points. Re-drag the handle
    // squares afterward (rendered on any smooth point) to refine further.
    const cm = toCm(getSvgPt(e).x, getSvgPt(e).y);
    const dx = cm.x - pending.startCm.x, dy = cm.y - pending.startCm.y;
    const dragDist = Math.hypot(dx, dy);
    const isDrag = dragDist > 0.15;
    const newPt = isDrag
      ? { x: snap(pending.startCm.x), y: snap(pending.startCm.y), smooth:true, corner:false, pinned:true,
          hOut: [+dx.toFixed(2), +dy.toFixed(2)], hIn: [+(-dx).toFixed(2), +(-dy).toFixed(2)] }
      : { x: snap(pending.startCm.x), y: snap(pending.startCm.y), smooth:false, corner:true, hOut:null, hIn:null };
    setPts(prev => {
      const next = [...prev, newPt];
      return autoSmooth ? recomputeAutoTangents(next, false) : next;
    });
    setPending(null);
  };

  // Toggle a placed point between "sharp corner" and "part of the smooth
  // curve" — corners are excluded from auto-tangent recompute (useful for
  // dart points, fly-stand peaks, or anywhere the outline should kink).
  const toggleCorner = (idx) => {
    pushHistory();
    setPts(prev => {
      const next = prev.map((p,i) => i===idx ? { ...p, corner: !p.corner, pinned: false } : p);
      return recomputeAutoTangents(next, closed);
    });
  };

  // Merge points closer together than mergeDist — cleans up the "too many
  // close nodes" mess that happens when clicking many times trying to
  // approximate a curve manually.
  const mergeNearby = () => {
    if (pts.length < 4) return;
    pushHistory();
    const kept = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const last = kept[kept.length-1];
      if (Math.hypot(pts[i].x-last.x, pts[i].y-last.y) >= mergeDist) kept.push(pts[i]);
    }
    // Guard against merging the closing point into the start point
    if (closed && kept.length > 3 && Math.hypot(kept[kept.length-1].x-kept[0].x, kept[kept.length-1].y-kept[0].y) < mergeDist) kept.pop();
    setPts(recomputeAutoTangents(kept, closed));
    setSelIdx(null);
  };

  // Re-run auto-smoothing on demand (e.g. after toggling the option on, or
  // after manually dragging a few points and wanting a fresh pass).
  const smoothAllNow = () => setPts(prev => recomputeAutoTangents(prev.map(p=>({...p,pinned:false})), closed));

  // Nudge each point toward the average of its neighbours (single-pass
  // Laplacian smoothing, blended 50/50 with its original position so the
  // shape doesn't flatten, just relaxes). This is the direct fix for
  // "clicking slightly outside the line makes it convex, slightly inside
  // makes it concave" — a Catmull-Rom curve threads through every point
  // exactly, so a single imprecise click warps the curve around it. This
  // pass absorbs that click noise into a blend with its neighbours before
  // the curve is fit, instead of forcing the curve through the exact
  // (possibly slightly-off) pixel you clicked.
  const smoothPositions = () => {
    if (pts.length < 4) return;
    pushHistory();
    const n = pts.length;
    const relaxed = pts.map((p, i) => {
      if (p.corner || p.pinned) return p; // leave deliberate corners untouched
      const prev = pts[(i-1+n)%n], next = pts[(i+1)%n];
      return { ...p, x: +(p.x*0.5 + (prev.x+next.x)*0.25).toFixed(2), y: +(p.y*0.5 + (prev.y+next.y)*0.25).toFixed(2) };
    });
    setPts(recomputeAutoTangents(relaxed, closed));
  };

  // Pin the last placed point to an exact length + angle from the point
  // before it — this is what makes the tool "measurement-accurate" beyond
  // whatever the mouse + grid snap can manage.
  const applyManual = () => {
    if (pts.length === 0) return;
    const L = parseFloat(manualLen), A = parseFloat(manualAngle);
    if (isNaN(L) || isNaN(A)) return;
    const basePt = pts.length >= 2 ? pts[pts.length - 2] : { x: 0, y: 0 };
    const rad = A * Math.PI / 180;
    const nx = +(basePt.x + L * Math.cos(rad)).toFixed(2);
    const ny = +(basePt.y + L * Math.sin(rad)).toFixed(2);
    setPts(prev => {
      const next = prev.map((p, i) => i === prev.length - 1 ? { ...p, x: nx, y: ny } : p);
      return autoSmooth ? recomputeAutoTangents(next, false) : next;
    });
  };

  const liveSeg = (!closed && hoverCm && pts.length) ? (() => {
    const last = pts[pts.length - 1];
    const dx = hoverCm.x - last.x, dy = hoverCm.y - last.y;
    return { len: Math.hypot(dx, dy), angle: Math.atan2(dy, dx) * 180 / Math.PI };
  })() : null;

  // Both this tool's own preview and PatternEditor's renderer now consume
  // the exact same smooth/hIn/hOut/corner fields the same way (buildPathD),
  // so there's no translation step needed at save time any more — what you
  // see here is structurally what gets saved and structurally what
  // PatternEditor will show. (Previously this "baked" the midpoint-curve
  // preview into a dense point cloud to paper over a mismatch between two
  // different curve renderers; the mismatch is fixed at the root now.)
  const loadTemplate = (name) => {
    const fn = SPARSE_TEMPLATES[name];
    if (!fn) return;
    pushHistory();
    const { points, measurements, gradeTags } = fn({});
    setPts(points);
    setClosed(true);
    setSelIdx(null);
    setPieceName(name);
    const nl = name.toLowerCase();
    setCategory(
      nl.includes('sleeve') ? 'sleeve' : nl.includes('collar') ? 'collar' : nl.includes('pocket') ? 'pocket' :
      nl.includes('front') ? 'front' : nl.includes('back') ? 'back' : 'panel'
    );
    templateMeasurementsRef.current = measurements;
    templateGradeTagsRef.current = gradeTags;
  };

  const finish = () => {
    if (!closed) {
      if (pts.length < 3) { alert('Place at least 3 points, then click the first point again to close the shape.'); return; }
      setClosed(true); return;
    }
    let finalPts = pts;
    if (mirror) {
      const mirrored = pts.slice().reverse().map(p => ({
        ...p, x: -p.x,
        hOut: p.hIn  ? [-p.hIn[0],  p.hIn[1]]  : null,
        hIn:  p.hOut ? [-p.hOut[0], p.hOut[1]] : null,
      }));
      finalPts = [...pts, ...mirrored];
    }
    // Dedupe exact/near-duplicate consecutive points (e.g. an accidental
    // double-click at the same spot), keeping each point's full data —
    // smooth flag, handles, corner — not just its coordinates.
    const deduped = [finalPts[0]];
    for (let i = 1; i < finalPts.length; i++) {
      const last = deduped[deduped.length-1];
      if (Math.hypot(finalPts[i].x-last.x, finalPts[i].y-last.y) >= 0.03) deduped.push(finalPts[i]);
    }
    if (deduped.length > 3 && Math.hypot(deduped[deduped.length-1].x-deduped[0].x, deduped[deduped.length-1].y-deduped[0].y) < 0.03) deduped.pop();

    const xs = deduped.map(p=>p.x), ys = deduped.map(p=>p.y);
    const minX = Math.min(...xs), minY = Math.min(...ys);
    const traced = deduped.map(p => ({
      x: +(p.x-minX).toFixed(2), y: +(p.y-minY).toFixed(2),
      smooth: !!p.smooth, corner: !!p.corner,
      hIn: p.hIn || null, hOut: p.hOut || null,
    }));
    const width  = +(Math.max(...xs) - minX).toFixed(2);
    const height = +(Math.max(...ys) - minY).toFixed(2);
    // If this came from a template and hasn't been mirrored/restructured
    // since (same point count, no mirror), carry over its richer named
    // measurements and matching per-point grade tags instead of falling
    // back to the cruder bbox-only width/height grading.
    const templateStillValid = !mirror && templateGradeTagsRef.current?.length === deduped.length;
    onSave({
      id: 'draw-' + Date.now(), partName: pieceName || 'Custom Piece', category,
      measurements: { width, height, ...(templateStillValid ? templateMeasurementsRef.current : {}) },
      tracedPoints: traced,
      gradeTags: templateStillValid ? templateGradeTagsRef.current : undefined,
      seamAllowances: { ...seam },
      notches: [], grainLine: { angleDeg: 90 },
      sourceMethod: 'hand-drawn',
    });
  };

  // Grid — faint every gridSize cm, bold every 10cm; origin (x=0) doubles as
  // the CF/fold reference line when mirroring.
  const gridLines = [];
  const spanX = CW / SC, spanY = CH / SC;
  for (let gx = -Math.ceil(spanX/2); gx <= Math.ceil(spanX/2); gx += gridSize) {
    const bold = Math.round(gx) % 10 === 0;
    gridLines.push(<line key={'gx'+gx} x1={tx(gx)} y1={0} x2={tx(gx)} y2={CH} stroke={bold?'#CED4DA':'#F4F6F8'} strokeWidth={bold?1:0.5}/>);
  }
  for (let gy = 0; gy <= Math.ceil(spanY); gy += gridSize) {
    const bold = Math.round(gy) % 10 === 0;
    gridLines.push(<line key={'gy'+gy} x1={0} y1={ty(gy)} x2={CW} y2={ty(gy)} stroke={bold?'#CED4DA':'#F4F6F8'} strokeWidth={bold?1:0.5}/>);
  }

  // Open (still-drawing) path — buildPathD always closes its loop, so build
  // the open preview by hand, consuming each point's real smooth/hIn/hOut
  // the same way buildPathD does for the closed case (exact same curve
  // logic, just without wrapping back to the start).
  const buildOpenPathD = (list) => {
    if (list.length < 2) return '';
    let d = `M ${tx(list[0].x).toFixed(1)} ${ty(list[0].y).toFixed(1)} `;
    for (let i = 1; i < list.length; i++) {
      const mi = list[i-1], mj = list[i];
      const hOut = mi.smooth && mi.hOut ? mi.hOut : null;
      const hIn  = mj.smooth && mj.hIn  ? mj.hIn  : null;
      if (hOut || hIn) {
        const c1x = mi.x + (hOut ? hOut[0] : (mj.x-mi.x)*0.33), c1y = mi.y + (hOut ? hOut[1] : (mj.y-mi.y)*0.33);
        const c2x = mj.x + (hIn  ? hIn[0]  : (mi.x-mj.x)*0.33), c2y = mj.y + (hIn  ? hIn[1]  : (mi.y-mj.y)*0.33);
        d += `C ${tx(c1x).toFixed(1)} ${ty(c1y).toFixed(1)} ${tx(c2x).toFixed(1)} ${ty(c2y).toFixed(1)} ${tx(mj.x).toFixed(1)} ${ty(mj.y).toFixed(1)} `;
      } else {
        d += `L ${tx(mj.x).toFixed(1)} ${ty(mj.y).toFixed(1)} `;
      }
    }
    return d;
  };
  const closedD = (closed && pts.length>=3)
    ? buildPathD(pts.map(p=>[p.x,p.y]), pts.map(p=>({smooth:p.smooth,hIn:p.hIn,hOut:p.hOut})), tx, ty)
    : null;
  const openD = (!closed && pts.length>=2) ? buildOpenPathD(pts) : null;
  const mirrorPreviewD = (mirror && !closed && pts.length>=2)
    ? buildOpenPathD(pts.map(p => ({ ...p, x: -p.x, hOut: p.hIn?[-p.hIn[0],p.hIn[1]]:null, hIn: p.hOut?[-p.hOut[0],p.hOut[1]]:null })))
    : null;

  const modal = (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'white', borderRadius:0, padding:12, width:'100vw', height:'100vh', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', display:'flex', flexDirection:'column' }}>
        <div style={{ flexShrink:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div style={{ fontSize:14, fontWeight:'bold', color:'#1A2A3A' }}>✎ Draw New Piece — measurement-accurate canvas</div>
          <button onClick={onCancel} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#6C757D' }}>✕</button>
        </div>

        {/* Reference image (trace-over) controls */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:6, padding:'5px 10px', background:'#eff6ff', border:'1px solid #90CAF9', borderRadius:6 }}>
          {!img ? (
            <label style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', background:'white', border:'1px solid #93c5fd', borderRadius:5, cursor:'pointer', fontSize:10, color:'#0D47A1', fontWeight:'bold' }}>
              <Upload size={12}/> Upload reference image to trace
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onImgUpload}/>
            </label>
          ) : (
            <>
              <span style={{ fontSize:10, fontWeight:'bold', color:'#0D47A1' }}>Image size (cm):</span>
              <input type="number" value={imgW} onChange={e=>{
                  const w = +e.target.value||1; const ratio = imgH/imgW; setImgW(w); setImgH(+(w*ratio).toFixed(1));
                }} style={{ width:56, padding:'3px 6px', fontSize:10, border:'1px solid #90CAF9', borderRadius:4 }}/>
              <span style={{fontSize:9,color:'#6C757D'}}>W ×</span>
              <input type="number" value={imgH} onChange={e=>setImgH(+e.target.value||1)}
                style={{ width:56, padding:'3px 6px', fontSize:10, border:'1px solid #90CAF9', borderRadius:4 }}/>
              <span style={{fontSize:9,color:'#6C757D'}}>H — set this to the piece's real waist/length so clicks trace at true scale</span>
              <div style={{ width:1, alignSelf:'stretch', background:'#90CAF9' }}/>
              <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#0D47A1' }}>
                Opacity
                <input type="range" min={0.1} max={1} step={0.05} value={imgOpacity} onChange={e=>setImgOpacity(+e.target.value)} style={{width:60}}/>
              </label>
              <button onClick={()=>setMode(m=>m==='moveImage'?'draw':'moveImage')}
                style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:'pointer',
                  background:mode==='moveImage'?'#0D47A1':'white', color:mode==='moveImage'?'white':'#0D47A1', border:'1px solid #93c5fd' }}>
                {mode==='moveImage' ? '✓ Positioning (drag canvas)' : '⤡ Move Image'}
              </button>
              <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#0D47A1' }}>
                <input type="checkbox" checked={imgLocked} onChange={e=>setImgLocked(e.target.checked)}/> Lock
              </label>
              <button onClick={()=>setImg(null)}
                style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:'pointer', background:'white', color:'#C62828', border:'1px solid #fecaca' }}>
                Remove
              </button>
            </>
          )}
        </div>

        {/* Start from a template — few, meaningful, pre-tuned handles
            (waist/hip/crotch/knee/hem) instead of a blank canvas you have
            to click 30+ points onto. Only offered before you've placed
            anything, so it doesn't clobber work in progress. */}
        {pts.length === 0 && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8, padding:'6px 10px', background:'#f0fdf4', border:'1px solid #A5D6A7', borderRadius:6 }}>
            <span style={{fontSize:9,fontWeight:'bold',color:'#166534'}}>Start from a template:</span>
            {Object.keys(SPARSE_TEMPLATES).map(name => (
              <button key={name} onClick={()=>loadTemplate(name)}
                style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:'pointer', background:'white', border:'1px solid #A5D6A7', color:'#166534' }}>
                {name}
              </button>
            ))}
            <span style={{fontSize:8.5,color:'#ADB5BD'}}>— or just start clicking below for a blank canvas</span>
          </div>
        )}

        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8, padding:'6px 10px', background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius:6 }}>
          <span style={{fontSize:9,fontWeight:'bold',color:'#4338ca'}}>Canvas mode:</span>
          {[
            {id:'draw', label:'✎ Draw'},
            {id:'pan', label:'🖐 Pan View'},
            {id:'moveSketch', label:'⤡ Move Sketch'},
          ].map(m => (
            <button key={m.id} onClick={()=>setMode(m.id)}
              style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:'pointer',
                background:mode===m.id?'#4338ca':'white', color:mode===m.id?'white':'#4338ca', border:'1px solid #c7d2fe' }}>
              {m.label}
            </button>
          ))}
          <span style={{fontSize:8.5,color:'#ADB5BD'}}>(hold Space to pan from any mode)</span>
          {pts.length>=2 && (
            <>
              <div style={{ width:1, alignSelf:'stretch', background:'#c7d2fe' }}/>
              <button onClick={()=>setMode(m=>m==='addDart'?'draw':'addDart')}
                title="Click on the line where the dart goes — inserts a real sharp V-notch, not a rounded bump"
                style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:'pointer',
                  background:mode==='addDart'?'#be123c':'#fff1f2', color:mode==='addDart'?'white':'#be123c', border:'1px solid #fecdd3' }}>
                📐 Add Dart
              </button>
            </>
          )}
          {img && (
            <>
              <div style={{ width:1, alignSelf:'stretch', background:'#c7d2fe' }}/>
              <button onClick={()=>setMode(m=>m==='autoTrace'?'draw':'autoTrace')} disabled={autoTraceBusy}
                title="Click once inside the garment in the reference image — extracts the outline from the photo automatically"
                style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:autoTraceBusy?'wait':'pointer',
                  background:mode==='autoTrace'?'#6A1B9A':'#EDE7F6', color:mode==='autoTrace'?'white':'#6A1B9A', border:'1px solid #ddd6fe' }}>
                {autoTraceBusy ? '⟳ Tracing…' : '🪄 Auto-Trace'}
              </button>
              {mode==='autoTrace' && (
                <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:9, color:'#6A1B9A' }}>
                  Sensitivity
                  <input type="range" min={10} max={90} value={traceThreshold} onChange={e=>setTraceThreshold(+e.target.value)} style={{width:70}}/>
                  <span style={{width:20}}>{traceThreshold}</span>
                </label>
              )}
            </>
          )}
          <span style={{fontSize:9,color:'#ADB5BD'}}>{mode==='autoTrace' ? 'Click once inside the garment outline in the image' : 'Pan/Move Sketch let you reposition after zooming without losing your trace'}</span>
        </div>
        {mode==='autoTrace' && !autoTraceBusy && pts.length>0 && (
          <div style={{ fontSize:9, color:'#b45309', marginTop:-4, marginBottom:8 }}>
            ⚠ This replaces your current {pts.length}-point sketch with the auto-traced outline. Undo (⌘Z) if it doesn't come out right.
          </div>
        )}

        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8, padding:'6px 10px', background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:6 }}>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A', fontWeight:'bold' }}>
            <input type="checkbox" checked={snapGrid} onChange={e=>setSnapGrid(e.target.checked)}/> Snap to grid
          </label>
          <select value={gridSize} onChange={e=>setGridSize(+e.target.value)} disabled={!snapGrid}
            style={{ padding:'3px 6px', fontSize:10, border:'1px solid #ddd6fe', borderRadius:4 }}>
            <option value={0.5}>0.5 cm</option>
            <option value={1}>1 cm</option>
            <option value={2}>2 cm</option>
          </select>
          <div style={{ width:1, alignSelf:'stretch', background:'#e9d5ff' }}/>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A', fontWeight:'bold' }}
            title="Off by default: a plain click is a hard corner, click-and-drag sets an exact handle you control. Turning this on makes plain clicks guess a curve too — faster for a rough sketch, less precise.">
            <input type="checkbox" checked={autoSmooth} onChange={e=>{ setAutoSmooth(e.target.checked); if (e.target.checked) smoothAllNow(); }}/> Ambient auto-smooth (optional)
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A', fontWeight:'bold' }}
            title="Dragging a point pulls nearby points along with it, like tugging a piece of rope, instead of only bending the curve right at that one point">
            <input type="checkbox" checked={elastic} onChange={e=>setElastic(e.target.checked)}/> 🪢 Elastic drag
          </label>
          {elastic && (
            <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:9, color:'#6A1B9A' }}>
              Reach
              <input type="range" min={1} max={8} value={elasticRadius} onChange={e=>setElasticRadius(+e.target.value)} style={{width:60}}/>
              <span style={{width:14}}>{elasticRadius}</span>
            </label>
          )}
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A', fontWeight:'bold' }}>
            <input type="checkbox" checked={showNodes} onChange={e=>setShowNodes(e.target.checked)}/> Show points
          </label>
          <div style={{ width:1, alignSelf:'stretch', background:'#e9d5ff' }}/>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A' }}>
            Merge points closer than
            <input type="number" step="0.5" min="0.5" value={mergeDist} onChange={e=>setMergeDist(+e.target.value||1)}
              style={{width:44,padding:'3px 5px',fontSize:10,border:'1px solid #ddd6fe',borderRadius:4}}/>
            cm
          </label>
          <button onClick={mergeNearby} disabled={pts.length<4}
            style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:pts.length>=4?'pointer':'not-allowed',
              background:pts.length>=4?'#E1BEE7':'#f3f4f6', color:pts.length>=4?'#6A1B9A':'#9E9E9E', border:'1px solid #ddd6fe' }}>
            🧹 Clean Up
          </button>
          <button onClick={smoothPositions} disabled={pts.length<4}
            title="Relaxes each point toward its neighbours' average — fixes stray bumps/dents from an imprecise click without flattening the curve"
            style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:pts.length>=4?'pointer':'not-allowed',
              background:pts.length>=4?'#E1BEE7':'#f3f4f6', color:pts.length>=4?'#6A1B9A':'#9E9E9E', border:'1px solid #ddd6fe' }}>
            🧽 Relax Curve
          </button>
          <div style={{ width:1, alignSelf:'stretch', background:'#e9d5ff' }}/>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6A1B9A', fontWeight:'bold' }}>
            <input type="checkbox" checked={mirror} onChange={e=>setMirror(e.target.checked)} disabled={pts.length>0 && closed}/> Mirror across CF
          </label>
          <button onClick={undo} disabled={history.length===0}
            title="⌘/Ctrl+Z"
            style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:history.length?'pointer':'not-allowed', background:history.length?'#FFCDD2':'#f3f4f6', color:history.length?'#C62828':'#9E9E9E', border:'1px solid #fecaca' }}>
            ↺ Undo (⌘Z)
          </button>
          <button onClick={redo} disabled={future.length===0}
            title="⌘/Ctrl+Shift+Z"
            style={{ padding:'3px 9px', fontSize:10, fontWeight:'bold', borderRadius:4, cursor:future.length?'pointer':'not-allowed', background:future.length?'#BBDEFB':'#f3f4f6', color:future.length?'#0D47A1':'#9E9E9E', border:'1px solid #90CAF9' }}>
            ↻ Redo (⌘⇧Z)
          </button>
          <span style={{ fontSize:9, color:'#ADB5BD', marginLeft:'auto' }}>{pts.length} point{pts.length===1?'':'s'} placed</span>
          <div style={{ width:1, alignSelf:'stretch', background:'#e9d5ff' }}/>
          <button onClick={()=>setZoom(z=>Math.max(0.3,+(z-0.15).toFixed(2)))} style={{padding:'3px 8px',fontSize:11,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #ddd6fe',color:'#6A1B9A'}}>−</button>
          <span style={{fontSize:9,color:'#6A1B9A',minWidth:32,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
          <button onClick={()=>setZoom(z=>Math.min(2.5,+(z+0.15).toFixed(2)))} style={{padding:'3px 8px',fontSize:11,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #ddd6fe',color:'#6A1B9A'}}>+</button>
          <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} style={{padding:'3px 8px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #ddd6fe',color:'#6A1B9A'}}>Reset</button>
        </div>
        </div>

        <div style={{ border:'1px solid #DEE2E6', borderRadius:8, overflow:'hidden', background:'#fcfcfd', flex:1, minHeight:0 }}>
          <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="xMidYMid meet"
            style={{ cursor: mode==='autoTrace' ? (autoTraceBusy?'wait':'crosshair') : mode==='pan' ? 'grab' : mode==='moveImage' ? 'move' : mode==='moveSketch' ? 'move' : (closed ? 'default' : 'crosshair'), display:'block' }}
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onDoubleClick={onDblClick} onWheel={onWheel}>
            {img && (
              <image href={img} x={tx(imgPos.x)} y={ty(imgPos.y + imgH)} width={imgW*SC} height={imgH*SC}
                opacity={imgOpacity} preserveAspectRatio="none"/>
            )}
            {gridLines}
            {/* Origin / CF fold line */}
            <line x1={tx(0)} y1={0} x2={tx(0)} y2={CH} stroke={mirror ? '#a855f7' : '#ADB5BD'} strokeWidth={mirror?1.4:0.8} strokeDasharray={mirror?'none':'4,3'}/>
            <line x1={0} y1={ty(0)} x2={CW} y2={ty(0)} stroke="#ADB5BD" strokeWidth={0.8} strokeDasharray="4,3"/>

            {mirrorPreviewD && <path d={mirrorPreviewD} fill="none" stroke="#c4b5fd" strokeWidth={1.3} strokeDasharray="3,2"/>}
            {closedD && <path d={closedD} fill="rgba(124,58,237,0.08)" stroke="#6A1B9A" strokeWidth={1.1}/>}
            {openD   && <path d={openD} fill="none" stroke="#6A1B9A" strokeWidth={1.1}/>}

            {/* Rubber-band preview line to the cursor */}
            {liveSeg && pts.length>0 && mode==='draw' && (
              <g>
                <line x1={tx(pts[pts.length-1].x)} y1={ty(pts[pts.length-1].y)} x2={tx(hoverCm.x)} y2={ty(hoverCm.y)}
                  stroke="#a855f7" strokeWidth={1.2} strokeDasharray="3,2" opacity={0.7}/>
                <text x={tx(hoverCm.x)+8} y={ty(hoverCm.y)-8} fontSize="10" fill="#6A1B9A" fontWeight="bold">
                  {liveSeg.len.toFixed(1)} cm, {liveSeg.angle.toFixed(0)}°
                </text>
              </g>
            )}

            {/* Placed nodes — small precise markers; selected node gets a thin outer ring instead of a bigger dot, so exact placement isn't obscured. Smooth points show BOTH curve handles as small draggable squares. */}
            {showNodes && pts.map((p,i) => (
              <g key={i}>
                {p.smooth && ['hOut','hIn'].map(side => {
                  const h = p[side]; if (!h) return null;
                  const hx = tx(p.x+h[0]), hy = ty(p.y+h[1]);
                  return (
                    <g key={side}>
                      <line x1={tx(p.x)} y1={ty(p.y)} x2={hx} y2={hy} stroke="#a855f7" strokeWidth={0.8} strokeDasharray="2,2" opacity={0.6}/>
                      <rect x={hx-3} y={hy-3} width={6} height={6} fill="white" stroke="#a855f7" strokeWidth={1.2} style={{cursor:'pointer'}}/>
                    </g>
                  );
                })}
                {i===selIdx && <circle cx={tx(p.x)} cy={ty(p.y)} r={DOT_R+3.5} fill="none" stroke="#FF8F00" strokeWidth={1.4}/>}
                <circle cx={tx(p.x)} cy={ty(p.y)} r={i===0?4:DOT_R}
                  fill={i===0 ? '#FFB300' : (p.corner ? '#fca5a5' : (p.smooth ? '#c4b5fd' : 'white'))}
                  stroke="#6A1B9A" strokeWidth={i===0?1.5:1}/>
              </g>
            ))}
          </svg>
        </div>

        <div style={{ display:'flex', gap:12, alignItems:'center', marginTop:8, flexWrap:'wrap' }}>
          <div style={{ fontSize:10, color:'#6C757D' }}>
            {mode==='addDart'
              ? 'Click on the outline where the dart should sit, then drag to set its depth and release'
              : 'Click = straight corner · click-and-drag = smooth point with a handle you set exactly · drag the small square handles to reshape a curve later · double-click a point to toggle corner ↔ smooth · click the first (gold) point to close'}
          </div>
        </div>

        {selIdx != null && (
          <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8, padding:'6px 10px', background:'#FFF8E1', border:'1px solid #fde68a', borderRadius:6, flexWrap:'wrap' }}>
            <span style={{fontSize:10,fontWeight:'bold',color:'#92400e'}}>Point {selIdx+1} selected:</span>
            <button onClick={()=>toggleCorner(selIdx)}
              style={{padding:'3px 9px',fontSize:10,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #fde68a',color:'#92400e'}}>
              {pts[selIdx]?.corner ? '◠ Make Smooth' : '◇ Make Corner'}
            </button>
            <button onClick={()=>{ setClipboard({...pts[selIdx]}); }}
              title="⌘/Ctrl+C" style={{padding:'3px 9px',fontSize:10,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #fde68a',color:'#92400e'}}>
              ⧉ Copy (⌘C)
            </button>
            {clipboard && (
              <button onClick={()=>{
                  pushHistory();
                  const insertAt = selIdx + 1;
                  const newPt = { ...clipboard, x:+(clipboard.x+1.5).toFixed(2), y:+(clipboard.y+1.5).toFixed(2) };
                  setPts(prev => { const next=[...prev]; next.splice(insertAt,0,newPt); return autoSmooth?recomputeAutoTangents(next,closed):next; });
                  setSelIdx(insertAt);
                }}
                title="⌘/Ctrl+V" style={{padding:'3px 9px',fontSize:10,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #fde68a',color:'#92400e'}}>
                📋 Paste (⌘V)
              </button>
            )}
            <button onClick={deleteSelected} title="Delete / Backspace"
              style={{padding:'3px 9px',fontSize:10,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'#FFCDD2',border:'1px solid #fecaca',color:'#C62828'}}>
              ✕ Delete (⌫)
            </button>
            <button onClick={()=>setSelIdx(null)}
              style={{padding:'3px 9px',fontSize:10,borderRadius:4,cursor:'pointer',background:'white',border:'1px solid #DEE2E6',color:'#6C757D'}}>
              Deselect
            </button>
          </div>
        )}

        {!closed && pts.length>0 && (
          <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8, padding:'6px 10px', background:'#f0fdf4', border:'1px solid #A5D6A7', borderRadius:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:10, fontWeight:'bold', color:'#166534' }}>Pin last point to exact measurement:</span>
            <input type="number" placeholder="length cm" value={manualLen} onChange={e=>setManualLen(e.target.value)}
              style={{ width:80, padding:'3px 6px', fontSize:10, border:'1px solid #A5D6A7', borderRadius:4 }}/>
            <span style={{ fontSize:10, color:'#166534' }}>cm at angle</span>
            <input type="number" placeholder="angle °" value={manualAngle} onChange={e=>setManualAngle(e.target.value)}
              style={{ width:70, padding:'3px 6px', fontSize:10, border:'1px solid #A5D6A7', borderRadius:4 }}/>
            <span style={{ fontSize:9, color:'#6C757D' }}>° (0=right, 90=up) from the previous point</span>
            <button onClick={applyManual}
              style={{ padding:'3px 10px', fontSize:10, fontWeight:'bold', background:'#166534', color:'white', border:'none', borderRadius:4, cursor:'pointer' }}>
              Apply
            </button>
          </div>
        )}

        {closed && (
          <div style={{ display:'flex', gap:10, alignItems:'center', marginTop:10, padding:10, background:'#F8F9FA', border:'1px solid #DEE2E6', borderRadius:8, flexWrap:'wrap' }}>
            <input placeholder="Piece name (e.g. Front Panel)" value={pieceName} onChange={e=>setPieceName(e.target.value)}
              style={{ padding:'6px 10px', fontSize:11, border:'1px solid #CED4DA', borderRadius:5, minWidth:180 }}/>
            <select value={category} onChange={e=>setCategory(e.target.value)}
              style={{ padding:'6px 10px', fontSize:11, border:'1px solid #CED4DA', borderRadius:5 }}>
              <option value="panel">Panel</option>
              <option value="front">Front</option>
              <option value="back">Back</option>
              <option value="sleeve">Sleeve</option>
              <option value="waistband">Waistband</option>
              <option value="pocket">Pocket</option>
              <option value="collar">Collar</option>
              <option value="cuff">Cuff</option>
              <option value="other">Other</option>
            </select>
            {['top','right','bottom','left'].map(side => (
              <label key={side} style={{ display:'flex', alignItems:'center', gap:3, fontSize:10, color:'#495057' }}>
                SA-{side}
                <input type="number" step="0.1" value={seam[side]} onChange={e=>setSeam(s=>({...s,[side]:+e.target.value||0}))}
                  style={{ width:44, padding:'3px 5px', fontSize:10, border:'1px solid #CED4DA', borderRadius:4 }}/>
              </label>
            ))}
            <button onClick={finish}
              style={{ padding:'7px 16px', fontSize:11, fontWeight:'bold', background:'#6A1B9A', color:'white', border:'none', borderRadius:6, cursor:'pointer', marginLeft:'auto' }}>
              ✓ Save Piece
            </button>
          </div>
        )}
        {!closed && (
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:10 }}>
            <button onClick={onCancel} style={{ padding:'7px 16px', fontSize:11, background:'#F4F6F8', color:'#495057', border:'1px solid #CED4DA', borderRadius:6, cursor:'pointer' }}>Cancel</button>
            <button onClick={finish} disabled={pts.length<3}
              style={{ padding:'7px 16px', fontSize:11, fontWeight:'bold', background:pts.length>=3?'#6A1B9A':'#CED4DA', color:'white', border:'none', borderRadius:6, cursor:pts.length>=3?'pointer':'not-allowed' }}>
              Close Shape
            </button>
          </div>
        )}
        <div style={{ fontSize:9, color:'#ADB5BD', marginTop:8 }}>
          After saving, you can grade this piece normally (auto-grades by overall width/height), or open it in the Reshape tool on the Patterns step to fine-tune individual curve points and assign per-point grading.
        </div>
      </div>
    </div>
  );
  // Rendered via a portal straight onto <body> — position:fixed breaks (and
  // silently mispositions instead of erroring) if ANY ancestor in the page
  // has a CSS transform/filter/perspective on it, which is common for sticky
  // headers, dropdown/animation wrappers, etc. A portal sidesteps that
  // entirely by mounting outside the app's own DOM tree.
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
};

const PatternLoader = ({ style, db, patterns, setPatterns, gradingRules, setGradingRules }) => {
  const [localParts, setLocalParts] = useState(db?.masterParts || []);
  const [loading, setLoading]       = useState(false);
  const [loadMsg, setLoadMsg]       = useState('');
  const [showLibrary, setShowLibrary] = useState(false);
  const [showDraw, setShowDraw]       = useState(false);
  const [showDxfImporter, setShowDxfImporter] = useState(false);

  // Self-load parts if parent db hasn't populated them yet
  useEffect(() => {
    if ((db?.masterParts || []).length > 0) { setLocalParts(db.masterParts); return; }
    fetch(`${API_BASE}/get_all_data.php`)
      .then(r => r.json())
      .then(data => setLocalParts(data.parts || data.masterParts || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if ((db?.masterParts || []).length > 0) setLocalParts(db.masterParts);
  }, [db?.masterParts]);

  const masterParts = localParts;

  // Fetch patterns from part_patterns table for all parts in this style.
  // Uses get_patterns.php?style_id=X which reads the style's workstation_data
  // to find part IDs, then returns all their stored patterns.
  useEffect(() => {
    if (!style?.id) return;
    setLoading(true);
    setLoadMsg('Loading patterns from database…');
    fetch(`${API_BASE}/get_patterns.php?style_id=${style.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.patterns?.length > 0) {
          setPatterns(data.patterns);
          setLoadMsg(`✓ ${data.patterns.length} pattern(s) loaded from part library`);
        } else {
          setLoadMsg(`No patterns found in DB for this style's parts. Add below or import .npf files.`);
        }
      })
      .catch(() => setLoadMsg('Could not reach DB — add patterns manually below.'))
      .finally(() => setLoading(false));
  }, [style?.id]);

  const addFromPart = (part) => {
    const exists = patterns.find(p => p.partId === part.id);
    if (exists) { alert(`Pattern for "${part.name}" already loaded.`); return; }
    const newPat = createEmptyPattern(
      part.id, part.name,
      part.specs?.template || '',
      part.specs?.measurements || {},
    );
    setPatterns(prev => [...prev, newPat]);
  };

  const removePattern = (patId) => {
    if (!window.confirm('Remove this pattern?')) return;
    setPatterns(prev => prev.filter(p => p.id !== patId));
  };

  const importNpf = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const pat = JSON.parse(ev.target.result);
        if (pat.format !== 'npf-v1') throw new Error('Not a valid .npf file');
        setPatterns(prev => [...prev, { ...pat, id: `pat-${Date.now()}` }]);
      } catch (err) {
        alert('Import failed: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Parts associated with this style that don't have a pattern yet
  const styleParts = useMemo(() => {
    const stylePartNames = (style?.parts || []).map(p => p.name?.toLowerCase());
    return masterParts.filter(mp =>
      stylePartNames.some(n => mp.name?.toLowerCase().includes(n) || n.includes(mp.name?.toLowerCase()))
    );
  }, [style, masterParts]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 4 }}>
        📐 Patterns for: <span style={{ color: '#6A1B9A' }}>{style?.name || style?.styleCode}</span>
      </div>
      <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 12 }}>
        {loading
          ? <span style={{ color: '#6A1B9A' }}>⟳ {loadMsg}</span>
          : <span style={{ color: loadMsg.startsWith('✓') ? '#2E7D32' : '#6C757D' }}>{loadMsg || `${patterns.length} pattern(s) loaded.`}</span>}
      </div>

      {/* ── PRODUCT LIBRARY PANEL ── */}
      {showLibrary ? (
        <div style={{ background: '#0D1B2A', borderRadius: 10, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>📚 Product Library — select a product to load all standard patterns</span>
            <button onClick={() => setShowLibrary(false)}
              style={{ background: 'none', border: 'none', color: '#ADB5BD', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
            {Object.entries(PRODUCT_LIBRARY).map(([key, prod]) => (
              <button key={key} onClick={() => {
                const newPats = buildProductPatterns(key, style?.id || 'new', style?.name || '');
                const newRules = buildProductGradingRules(key, newPats);
                setPatterns(newPats);
                if (setGradingRules) setGradingRules(newRules);
                setShowLibrary(false);
                setLoadMsg(`✓ ${newPats.length} standard patterns loaded for ${prod.label}`);
              }}
                style={{ background: '#1A2A3A', border: '1px solid #2C3E50', borderRadius: 8, padding: '14px 12px',
                  cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#6A1B9A'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2C3E50'}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{prod.icon}</div>
                <div style={{ fontWeight: 'bold', fontSize: 12, color: 'white', marginBottom: 3 }}>{prod.label}</div>
                <div style={{ fontSize: 10, color: '#ADB5BD' }}>
                  Base size {prod.baseSize} · {prod.buildParts(prod.baseM).length} pattern pieces
                </div>
                <div style={{ fontSize: 9, color: '#6366f1', marginTop: 3 }}>
                  {prod.sizes.join(' · ')}
                </div>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: '#6C757D', borderTop: '1px solid #1A2A3A', paddingTop: 8 }}>
            ℹ Patterns load with industry-standard measurements, correct polygon shapes, and pre-configured grading rules (ISO/AATCC basis).
            You can add custom parts below or edit any pattern in Step 5.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, background: '#EDE7F6', border: '1px solid #ddd6fe', borderRadius: 8, padding: '10px 14px' }}>
          <span style={{ fontSize: 14 }}>📚</span>
          <span style={{ fontSize: 11, color: '#4c1d95', flex: 1 }}>
            Load all standard pattern pieces, shapes and grading rules from the product library:
          </span>
          <button onClick={() => setShowLibrary(true)}
            style={{ padding: '7px 16px', background: '#6A1B9A', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold', flexShrink: 0 }}>
            📚 Load from Library
          </button>
        </div>
      )}

      {/* Loaded patterns grid */}
      {!showLibrary && patterns.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          {patterns.map(p => (
            <div key={p.id} style={{ background: 'white', border: '1px solid #DEE2E6', borderRadius: 8, padding: 8, textAlign: 'center' }}>
              <PatternSvg pattern={p} width={140} height={120} />
              <div style={{ fontSize: 10, fontWeight: 'bold', marginTop: 4, color: '#1A2A3A' }}>{p.partName}</div>
              <div style={{ fontSize: 9, color: '#ADB5BD' }}>{p.template}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'center' }}>
                <button onClick={() => exportPatternFile(p)} title="Export .npf"
                  style={{ padding: '2px 6px', fontSize: 9, background: '#f0fdf4', border: '1px solid #A5D6A7', borderRadius: 4, cursor: 'pointer', color: '#15803d' }}>
                  ↓ NPF
                </button>
                <button onClick={() => removePattern(p.id)}
                  style={{ padding: '2px 6px', fontSize: 9, background: '#FFEBEE', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', color: '#C62828' }}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add from PartDNA */}
      <div style={{ background: '#F8F9FA', border: '1px solid #DEE2E6', borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 'bold', color: '#495057', marginBottom: 8 }}>
          Add patterns from Part DNA library:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {masterParts.filter(p => p.specs?.template).map(p => (
            <button key={p.id} onClick={() => addFromPart(p)}
              style={{
                padding: '5px 10px', fontSize: 10, background: 'white', border: '1px solid #CED4DA',
                borderRadius: 20, cursor: 'pointer', color: '#2C3E50',
                opacity: patterns.find(pt => pt.partId === p.id) ? 0.4 : 1,
              }}>
              {patterns.find(pt => pt.partId === p.id) ? '✓ ' : '+ '}{p.name}
            </button>
          ))}
          {masterParts.filter(p => p.specs?.template).length === 0 && (
            <span style={{ fontSize: 11, color: '#ADB5BD' }}>No spec-built parts in Part DNA yet.</span>
          )}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '5px 10px', background: '#eff6ff', border: '1px solid #90CAF9', borderRadius: 6, cursor: 'pointer', fontSize: 10, color: '#0D47A1' }}>
          <Upload size={12} /> Import .npf file
          <input type="file" accept=".npf,.json" hidden onChange={importNpf} />
        </label>
        <button onClick={() => setShowDraw(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, marginLeft: 8, padding: '5px 10px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 6, cursor: 'pointer', fontSize: 10, color: '#6A1B9A', fontWeight: 'bold' }}>
          ✎ Draw New Piece
        </button>
        <button onClick={() => setShowDxfImporter(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, marginLeft: 8, padding: '5px 10px', background: '#f0fdf4', border: '1px solid #A5D6A7', borderRadius: 6, cursor: 'pointer', fontSize: 10, color: '#2E7D32', fontWeight: 'bold' }}>
          📐 Import DXF Pattern
        </button>
        {showDraw && (
          <DrawFromScratch
            onCancel={() => setShowDraw(false)}
            onSave={(newPat) => { setPatterns(prev => [...prev, newPat]); setShowDraw(false); }}
          />
        )}
        {showDxfImporter && (
          <DxfImporter
            onImportPattern={(pattern) => {
              setPatterns(prev => [...prev, pattern]);
              setShowDxfImporter(false);
              alert(`✓ Imported pattern: ${pattern.partName}`);
            }}
            onClose={() => setShowDxfImporter(false)}
          />
        )}
        {/* ── Load standard cut parts for a product ── */}
        {(() => {
          const [selProd, setSelProd] = React.useState('');
          return (
            <div style={{ marginTop:8, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', padding:'8px 10px', background:'#f0fdf4', border:'1px solid #A5D6A7', borderRadius:6 }}>
              <span style={{ fontSize:10, fontWeight:'bold', color:'#166534' }}>📋 Load standard pieces:</span>
              <select value={selProd} onChange={e=>setSelProd(e.target.value)}
                style={{ padding:'4px 8px', border:'1px solid #A5D6A7', borderRadius:4, fontSize:10, background:'white' }}>
                <option value="">── Product ──</option>
                {Object.entries(GARMENT_STANDARDS).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={()=>{
                if (!selProd) return;
                const pcs = buildStandardPatterns(selProd, 'M', '');
                setPatterns(selProd === 'TROUSER' ? attachTrouserTracedShapes(pcs) : pcs);
              }} disabled={!selProd}
                style={{ padding:'4px 12px', background:selProd?'#166534':'#ADB5BD', color:'white', border:'none', borderRadius:4, cursor:selProd?'pointer':'not-allowed', fontSize:10, fontWeight:'bold' }}>
                Load {selProd ? GARMENT_STANDARDS[selProd]?.pieces.length + ' pieces' : ''}
              </button>
              <span style={{ fontSize:9, color:'#6C757D' }}>Loads standard cut parts with base (M) measurements + notch positions pre-set</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// ============================================================================
//  STEP 3 — SPEC SHEET IMPORTER
//  Import from Style Registry (CSV / JSON / paste). Parses and stores.
// ============================================================================
const SpecSheetImporter = ({ style, specData, setSpecData, onImportComplete }) => {
  const [parsed,      setParsed]      = useState(null);    // full parsed result
  const [unitMode,    setUnitMode]    = useState('auto');  // 'auto'|'in'|'cm'
  const [baseSize,    setBaseSize]    = useState('M');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // Convert a single value: inches→cm if needed
  const toDisplay = (v, isInches) => {
    const mode = unitMode === 'auto' ? (isInches ? 'in' : 'cm') : unitMode;
    return mode === 'in' ? +(v * 2.54).toFixed(2) : +v.toFixed(2);
  };
  const unitLabel = (isInches) =>
    (unitMode === 'auto' ? (isInches ? 'in→cm' : 'cm') : (unitMode === 'in' ? 'in→cm' : 'cm'));

  const doParse = (text) => {
    setError('');
    const result = parseSpecSheet(text);
    if (!result || Object.keys(result.base || {}).length === 0) {
      setError('Could not parse. Make sure it is a valid spec sheet CSV, or paste key: value lines.');
      return;
    }
    setParsed(result);
    setBaseSize(result.baseSize || 'M');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = ev => { doParse(ev.target.result); setLoading(false); };
    reader.onerror = () => { setError('File read error.'); setLoading(false); };
    reader.readAsText(f);
    e.target.value = '';
  };

  // Converts Style Registry's row-table measurements
  // ({ headers:['pom','tolerance','S','M','L',...], rows:[{pom,tolerance,S,M,L,...}] })
  // into the { base, sizeData, sizes, meta, isInches } shape the rest of this
  // importer (confirm(), SpecCrossCheck, etc.) already expects — the same
  // shape produced by the CSV branch of parseSpecSheet().
  const parseRegistryMeasurements = (ms) => {
    setError('');
    const SIZE_RE = /^(XXS|XS|S|M|L|XL|2XL|XXL|3XL|XXXL|\d{2}|\d{2}\/\d{2})$/i;
    const headers = (ms.headers || []).filter(h => h !== 'pom' && h !== 'tolerance');
    const sizes   = headers.filter(h => SIZE_RE.test(String(h).trim()));
    if (sizes.length === 0) {
      setError('Could not parse stored spec sheet — no recognizable size columns.');
      return;
    }

    const sizeData = {}; sizes.forEach(sz => (sizeData[sz] = {}));
    const tols = {};
    (ms.rows || []).forEach(row => {
      const name = row.pom || row[Object.keys(row)[0]] || '';
      if (!name) return;
      const t = parseFloat(row.tolerance);
      if (!isNaN(t)) tols[name] = t;
      sizes.forEach(sz => {
        const v = parseFloat(row[sz]);
        if (!isNaN(v)) sizeData[sz][name] = v;
      });
    });

    const baseSz = sizes.includes('M') ? 'M' : sizes[Math.floor(sizes.length / 2)];
    const base = { ...sizeData[baseSz] };
    const sample = Object.values(base)[0] || 100;
    const isInches = sample > 0 && sample < 50;

    const result = { base, sizeData, sizes, tolerances: tols, meta: {}, baseSize: baseSz, isInches, _format: 'registry_measurements' };
    setParsed(result);
    setBaseSize(result.baseSize || 'M');
  };

  const loadFromStyle = async () => {
    if (!style?.id) return;
    setLoading(true); setError('');
    try {
      // Style Registry never writes a 'spec_sheet' category — graded
      // measurements live on the style record itself as `style.measurements`
      // ({ headers, rows }), saved under category 'style'. Re-fetch the style
      // fresh (in case it changed since this wizard loaded it), falling back
      // to the `style` prop we already have.
      let ms = style.measurements;
      try {
        const res = await fetch(`${LOAD_URL}?category=style&id=${style.id}`);
        const j   = await res.json();
        const fresh = j?.data?.measurements;
        if (fresh?.rows?.length) ms = fresh;
      } catch (_) { /* fall back to style.measurements below */ }

      if (!ms?.rows?.length) { setError('No spec sheet stored for this style yet — extract or add one in Style Registry first.'); return; }
      parseRegistryMeasurements(ms);
    } catch { setError('Could not load from registry.'); }
    finally { setLoading(false); }
  };

  const confirm = () => {
    if (!parsed) return;
    const isIn = unitMode === 'auto' ? parsed.isInches : unitMode === 'in';
    // Build base and sizeData in cm
    const base = {};
    Object.entries(parsed.base || {}).forEach(([k, v]) => { base[k] = toDisplay(v, isIn); });
    const sizeData = {};
    (parsed.sizes || []).forEach(sz => {
      sizeData[sz] = {};
      Object.entries(parsed.sizeData?.[sz] || {}).forEach(([k, v]) => { sizeData[sz][k] = toDisplay(v, isIn); });
    });
    // IMPORTANT: `sizes` must stay the array of size-name labels (e.g.
    // ['S','M','L']) and the per-size measurement values must go in the
    // separate `sizeData` key — GradingView.autoGrade() (the "Auto-grade
    // from spec" button) depends on this exact shape. This previously
    // overwrote `sizes` with the sizeData dict and omitted `sizeData`
    // entirely, so autoGrade() always found 0 sizes / 0 measurements and
    // silently failed — every marker size ended up packing an identical,
    // ungraded shape (hence the low 36-48% fabric utilization).
    const result = { base, sizes: parsed.sizes || [], sizeData, meta: parsed.meta || {}, sourceUnit: isIn ? 'in' : 'cm' };
    setSpecData(result);
    onImportComplete(result);
  };

  const sizes   = parsed?.sizes || [];
  const measKeys = Object.keys(parsed?.base || {});
  const isIn    = unitMode === 'auto' ? !!parsed?.isInches : unitMode === 'in';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 8 }}>
        📋 Spec Sheet Import — <span style={{ color: '#6A1B9A' }}>{style?.name || style?.styleCode || '—'}</span>
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={loadFromStyle} disabled={loading || !style?.id}
          style={{ padding: '7px 14px', background: '#6A1B9A', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
          {loading ? '⟳' : '⬇'} Load from Style Registry
        </button>
        <label style={{ padding: '7px 14px', background: '#f0fdf4', border: '1px solid #A5D6A7', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#15803d' }}>
          📂 Upload Spec Sheet (.csv / .txt / .json)
          <input type="file" accept=".csv,.json,.txt,.xls,.xlsx" hidden onChange={handleFile} />
        </label>
        <textarea
          rows={1}
          placeholder="Or paste raw CSV / key:value text here and press Enter…"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doParse(e.target.value); } }}
          style={{ flex: 1, minWidth: 200, padding: '7px 10px', border: '1px solid #CED4DA', borderRadius: 6, fontSize: 11, resize: 'none' }}
        />
      </div>

      {error && (
        <div style={{ background: '#FFEBEE', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontSize: 11, color: '#C62828' }}>
          ⚠ {error}
        </div>
      )}

      {parsed && (
        <>
          {/* Metadata strip */}
          {Object.keys(parsed.meta || {}).length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, background: '#F8F9FA', border: '1px solid #DEE2E6', borderRadius: 6, padding: '6px 12px' }}>
              {Object.entries(parsed.meta).map(([k, v]) => (
                <span key={k} style={{ fontSize: 10, color: '#495057' }}>
                  <strong style={{ color: '#1A2A3A' }}>{k}:</strong> {v}
                </span>
              ))}
            </div>
          )}

          {/* Controls */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#495057', fontWeight: 'bold' }}>
              ✓ {measKeys.length} measurements · {sizes.length} sizes
              {parsed.isInches && <span style={{ color: '#FF8F00', marginLeft: 6 }}>⚠ values detected in INCHES</span>}
            </span>
            {/* Unit toggle */}
            <div style={{ display: 'flex', gap: 4, background: '#F4F6F8', borderRadius: 6, padding: 3 }}>
              {[['auto', `Auto (${parsed.isInches ? 'in→cm' : 'cm'})`], ['in', 'Force in→cm'], ['cm', 'Keep as cm']].map(([v, label]) => (
                <button key={v} onClick={() => setUnitMode(v)}
                  style={{ padding: '3px 10px', background: unitMode === v ? '#1565C0' : 'transparent', color: unitMode === v ? 'white' : '#6C757D', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}>
                  {label}
                </button>
              ))}
            </div>
            {/* Base size selector */}
            {sizes.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: '#6C757D' }}>Base size:</span>
                <select value={baseSize} onChange={e => setBaseSize(e.target.value)}
                  style={{ padding: '3px 8px', border: '1px solid #CED4DA', borderRadius: 4, fontSize: 11 }}>
                  {sizes.map(sz => <option key={sz} value={sz}>{sz}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Measurement table */}
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #DEE2E6', marginBottom: 12 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#1A2A3A', color: 'white' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', minWidth: 220, position: 'sticky', left: 0, background: '#1A2A3A' }}>
                    POINT OF MEASUREMENT
                  </th>
                  <th style={{ padding: '8px 8px', textAlign: 'center', color: '#ADB5BD', width: 50 }}>TOL</th>
                  {sizes.map(sz => (
                    <th key={sz} style={{ padding: '8px 10px', textAlign: 'center', color: sz === baseSize ? '#34d399' : '#DEE2E6', minWidth: 70 }}>
                      {sz}{sz === baseSize ? ' ★' : ''}
                    </th>
                  ))}
                  {/* If no sizes, show single value column */}
                  {sizes.length === 0 && (
                    <th style={{ padding: '8px 10px', textAlign: 'center', color: '#34d399', minWidth: 80 }}>
                      Value ({unitLabel(isIn)})
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {measKeys.map((k, ri) => (
                  <tr key={k} style={{ background: ri % 2 === 0 ? 'white' : '#F8F9FA', borderBottom: '1px solid #F4F6F8' }}>
                    <td style={{ padding: '6px 12px', color: '#1A2A3A', fontWeight: '500', position: 'sticky', left: 0, background: ri%2===0?'white':'#F8F9FA' }}>{k}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ADB5BD', fontSize: 10 }}>
                      ±{parsed.tolerances?.[k] ?? '—'}
                    </td>
                    {sizes.length > 0 ? sizes.map(sz => {
                      const rawV = parsed.sizeData?.[sz]?.[k];
                      const dispV = rawV != null ? toDisplay(rawV, isIn) : '—';
                      const isBase = sz === baseSize;
                      const diff   = (rawV != null && parsed.base?.[k] != null && sz !== baseSize)
                        ? (rawV - (parsed.base?.[k] || 0)) : null;
                      return (
                        <td key={sz} style={{ padding: '6px 10px', textAlign: 'center',
                          color: isBase ? '#2E7D32' : '#2C3E50',
                          fontWeight: isBase ? 'bold' : 'normal',
                          background: isBase ? '#f0fdf4' : 'transparent' }}>
                          {dispV}
                          {diff !== null && diff !== 0 && (
                            <span style={{ fontSize: 9, color: diff > 0 ? '#1565C0' : '#C62828', marginLeft: 3 }}>
                              ({diff > 0 ? '+' : ''}{diff.toFixed(2)})
                            </span>
                          )}
                        </td>
                      );
                    }) : (
                      <td style={{ padding: '6px 10px', textAlign: 'center', color: '#2E7D32', fontWeight: 'bold' }}>
                        {toDisplay(parsed.base[k], isIn)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={confirm}
            style={{ padding: '10px 24px', background: '#2E7D32', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
            ✓ Use This Spec Sheet — Proceed to Cross-Check →
          </button>
        </>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 4 — SPEC → PATTERN CROSS-CHECK
//  Compares spec-sheet measurements against stored pattern specs.
//  Applies brand rules to adjust.
// ============================================================================
const SpecCrossCheck = ({ patterns, specData, onAdjust }) => {
  const [results, setResults] = useState([]);
  const [adjustedPatterns, setAdjustedPatterns] = useState(patterns);
  const base = specData?.base || {};

  useEffect(() => {
    const res = patterns.map(pat => {
      const m = pat.measurements || {};
      const mapping = fuzzyMapSpec(base, Object.keys(m));
      const diffs = Object.entries(mapping).map(([specKey, patKey]) => ({
        specKey, patKey,
        specValue: base[specKey],
        patValue: m[patKey],
        delta: (base[specKey] || 0) - (m[patKey] || 0),
        status: Math.abs((base[specKey] || 0) - (m[patKey] || 0)) <= 0.5 ? 'ok' : 'mismatch',
      }));
      return { pattern: pat, diffs, mapping };
    });
    setResults(res);
    setAdjustedPatterns(patterns);
  }, [patterns, specData]);

  const applyAdjustment = (patIdx, specKey, patKey, newValue) => {
    setAdjustedPatterns(prev => prev.map((p, i) => {
      if (i !== patIdx) return p;
      return { ...p, measurements: { ...p.measurements, [patKey]: newValue }, updatedOn: new Date().toISOString() };
    }));
  };

  const applyAll = () => {
    let updated = [...patterns];
    results.forEach((r, patIdx) => {
      r.diffs.filter(d => d.status === 'mismatch').forEach(d => {
        updated[patIdx] = {
          ...updated[patIdx],
          measurements: { ...updated[patIdx].measurements, [d.patKey]: d.specValue },
          updatedOn: new Date().toISOString(),
        };
      });
    });
    setAdjustedPatterns(updated);
    onAdjust(updated);
  };

  const totalMismatches = results.reduce((s, r) => s + r.diffs.filter(d => d.status === 'mismatch').length, 0);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 4 }}>
        ✅ Spec → Pattern Cross-Check
      </div>
      <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 12 }}>
        Comparing {Object.keys(base).length} spec measurements against {patterns.length} patterns.
        {totalMismatches > 0 ? (
          <span style={{ color: '#C62828', fontWeight: 'bold', marginLeft: 6 }}>⚠ {totalMismatches} mismatch(es) found.</span>
        ) : (
          <span style={{ color: '#388E3C', fontWeight: 'bold', marginLeft: 6 }}>✓ All measurements match.</span>
        )}
      </div>

      {totalMismatches > 0 && (
        <button onClick={applyAll}
          style={{ marginBottom: 12, padding: '7px 16px', background: '#FF8F00', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
          ⚡ Auto-adjust all patterns to match spec sheet
        </button>
      )}

      {results.map((r, patIdx) => (
        <div key={r.pattern.id} style={{ background: 'white', border: '1px solid #DEE2E6', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1A2A3A', marginBottom: 8 }}>{r.pattern.partName}</div>
          {r.diffs.length === 0 ? (
            <div style={{ fontSize: 11, color: '#ADB5BD' }}>No matching spec measurements found for this pattern.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#F8F9FA' }}>
                  {['Spec Field', 'Spec Value', 'Pattern Key', 'Pattern Value', 'Delta', 'Status', 'Adjust'].map(h => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontSize: 10, color: '#6C757D', borderBottom: '1px solid #DEE2E6' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.diffs.map((d, di) => (
                  <tr key={di} style={{ borderBottom: '1px solid #F4F6F8', background: d.status === 'mismatch' ? '#FFF8E1' : 'white' }}>
                    <td style={{ padding: '4px 8px' }}>{d.specKey}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>{d.specValue?.toFixed(1)}</td>
                    <td style={{ padding: '4px 8px', color: '#6366f1' }}>{d.patKey}</td>
                    <td style={{ padding: '4px 8px' }}>{d.patValue?.toFixed(1)}</td>
                    <td style={{ padding: '4px 8px', color: d.delta !== 0 ? '#FF8F00' : '#43A047', fontWeight: 'bold' }}>
                      {d.delta > 0 ? '+' : ''}{d.delta.toFixed(1)}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {d.status === 'ok'
                        ? <span style={{ color: '#388E3C', fontSize: 10 }}>✓ OK</span>
                        : <span style={{ color: '#C62828', fontSize: 10 }}>⚠ Mismatch</span>}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {d.status === 'mismatch' && (
                        <button onClick={() => applyAdjustment(patIdx, d.specKey, d.patKey, d.specValue)}
                          style={{ padding: '2px 8px', background: '#FF8F00', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 9 }}>
                          Apply
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      <button onClick={() => onAdjust(adjustedPatterns)}
        style={{ padding: '8px 20px', background: '#1A2A3A', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
        Confirm patterns & continue →
      </button>
    </div>
  );
};

// ============================================================================
//  STEP 5 — PATTERN EDITOR
//  • Uses tracedPoints (real shape) when available, generic shape as fallback
//  • Notch snaps to nearest polygon edge (correct position every time)
//  • Measurements stored as raw SVG coords — rendered directly, no transform
//  • Zoom / pan via SVG viewBox
//  • Inline spec editing
//  • Scale indicator
// ============================================================================

// ── Helpers shared by PatternEditor + PatternSvgInline ─────────────────────

// Build display polygon from a pattern.
// Returns { pts (cm, centred), W, H, hasTp } where pts is the polygon as [x,y] pairs.
// Case-insensitive, substring-match measurement lookup.
// Tries exact case-insensitive match first, then substring match.
// This means "Width"/"width"/"chest width"/"Bottom Width" all work.
const mGet = (m, ...keys) => {
  const entries = Object.entries(m || {});
  if (!entries.length) return undefined;
  for (const key of keys) {
    const kl = key.toLowerCase();
    const hit = entries.find(([k]) => k.toLowerCase() === kl);
    if (hit != null && hit[1] != null) return +hit[1];
  }
  for (const key of keys) {
    const kl = key.toLowerCase();
    const hit = entries.find(([k]) => k.toLowerCase().includes(kl));
    if (hit != null && hit[1] != null) return +hit[1];
  }
  return undefined;
};

// ── Quadratic Bezier sampler — generates n+1 smooth polygon points ──────────
const qbez = (p0, p1, p2, n = 8) => {
  const r = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, q = 1 - t;
    r.push([q*q*p0[0]+2*q*t*p1[0]+t*t*p2[0], q*q*p0[1]+2*q*t*p1[1]+t*t*p2[1]]);
  }
  return r;
};

// Remove consecutive (near-)duplicate points from a closed point loop.
// qbez-chained curve construction re-emits its start point every segment,
// which duplicates the previous segment's endpoint at every junction —
// creating zero-length edges. offsetPolygon's miter-join math divides by
// (near-)zero at those edges, producing wild multi-cm spikes in the seam
// allowance outline instead of a clean parallel offset. Dedupe before any
// offset/geometry operation, and at the source so it isn't just papered
// over at render time.
const dedupeLoop = (pts, eps = 0.03) => {
  if (!pts || pts.length < 3) return pts;
  const out = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const last = out[out.length - 1];
    if (Math.hypot(pts[i][0]-last[0], pts[i][1]-last[1]) >= eps) out.push(pts[i]);
  }
  // wraparound: last point duplicating the first
  if (out.length > 3 && Math.hypot(out[out.length-1][0]-out[0][0], out[out.length-1][1]-out[0][1]) < eps) out.pop();
  return out;
};

// ── Detect piece type from template string or part name ──────────────────────
const detectTemplate = (pattern) => {
  const t  = (pattern.template  || '').toLowerCase();
  const pn = (pattern.partName  || '').toLowerCase();
  const sm = (pattern.sourceMethod || '').toLowerCase();
  const s  = t + ' ' + pn + ' ' + sm;

  // ── Direct ShapeBuilder template passthrough ─────────────────────────────
  // If pattern.template is a known ShapeBuilder key, use ShapeBuilder's build()
  // function directly (most accurate — uses same math that created the shape).
  if (GARMENT_TEMPLATES[pattern.template]) return pattern.template;

  // ── ShapeBuilder template → MarkerMaker template name mapping ───────────
  const SB_MAP = {
    'tee_front':'body_front', 'tee_back':'body_back',
    'shirt_front':'body_front', 'shirt_back':'body_back',
    'pant_front':'trouser_front', 'pant_back':'trouser_back',
    'sleeve_set_in':'sleeve', 'sleeve':'sleeve',
    'collar':'collar', 'collar_stand':'collar_stand',
    'cuff':'cuff', 'yoke':'yoke',
  };
  if (SB_MAP[t]) return SB_MAP[t];
  // ── Small utility pieces FIRST — must be checked before the broad
  // trouser-front/back patterns below, since a template string like
  // "trouser_front_pocket_bag" or "trouser_fly_piece" contains "trouser"
  // + "front"/"back" as substrings and would otherwise be misdetected as
  // the main panel, inheriting its full crotch-curve silhouette. ──────────
  if (/waistband|waist.band/i.test(s))                         return 'waistband';
  if (/welt|jetting/i.test(s))                                 return 'welt_pocket';
  if (/fly/i.test(s))                                          return 'fly';
  if (/pocket|bag/i.test(s))                                   return 'pocket';
  if (/trouser.*(front|panel)|front.*(trouser|pant)/i.test(s)) return 'trouser_front';
  if (/trouser.*(back|panel)|back.*(trouser|pant)/i.test(s))  return 'trouser_back';
  if (/collar[\s_-]?stand|neck[\s_-]?band/i.test(s))           return 'collar_stand';
  if (/polo.collar|polo/i.test(s))                             return 'collar_polo';
  if (/collar/i.test(s))                                       return 'collar';
  if (/cuff/i.test(s))                                         return 'cuff';
  if (/yoke/i.test(s))                                         return 'yoke';
  if (/placket|\bband\b/i.test(s))                             return 'placket';
  if (/\brib\b|neck[\s_-]?rib/i.test(s))                       return 'rib';
  if (/sleeve/i.test(s))                                       return 'sleeve';
  if (/back.*body|back.*panel|body.*back/i.test(s))            return 'body_back';
  if (/front.*body|front.*panel|body.*front/i.test(s))         return 'body_front';
  return 'generic';
};

// ═══════════════════════════════════════════════════════════════════════════
//  SHAPE GENERATORS
//  All return polygon points as [[x,y], ...] in cm, centred at x=0
//  y=0 = hem/bottom,  y=H = top/shoulder/waistband
//  Coordinate conventions match reference industry patterns.
// ═══════════════════════════════════════════════════════════════════════════

const shapeBodyFront = (m) => {
  // 'width' intentionally excluded — it matches narrow pieces (placket, cuff, collar).
  // For body shapes always use halfChest / chestWidth / chest.
  const rawW = mGet(m,'halfChest','chestWidth','chest width','chest') || 0;
  const W    = rawW >= 15 ? rawW : 26;   // min 15 cm half-chest; 26 cm default
  const H   = mGet(m,'bodyLength','height','length') || 74;
  const AD  = mGet(m,'armholeDepth') || Math.min(H * 0.32, 24);
  const SHf = (mGet(m,'shoulder','shoulderWidth') || W * 1.72);
  const SH  = SHf / 2;                                          // half shoulder
  const NW  = mGet(m,'neckWidth') || W * 0.34;
  const ND  = mGet(m,'neckDepth','neckDepthFront') || W * 0.32;
  const uY  = H - AD;  // underarm Y

  // Armhole: concave S-curve from underarm to shoulder (sampled Bezier)
  const armholeRight = [
    ...qbez([W, uY], [W + 1.2, uY + AD * 0.35], [W * 0.78, uY + AD * 0.65], 5),
    ...qbez([W * 0.78, uY + AD * 0.65], [W * 0.58, uY + AD * 0.88], [SH, H - 2], 5),
  ];
  // Neckline: quarter-circle arc from shoulder to CF
  const neckRight = qbez([NW, H - 0.5], [NW * 0.2, H + ND * 0.1], [0, H - ND], 6);

  const pts = [
    [-W, 0], [W, 0], [W, uY],
    ...armholeRight,
    [SH, H - 2], [SH * 0.85, H - 0.5],
    ...neckRight,
    ...neckRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-SH * 0.85, H - 0.5], [-SH, H - 2],
    ...armholeRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-W, uY],
  ];
  return { pts, W, H: H + ND * 0.3, hasTp: false, template: 'body_front',
    guides: [
      { y: uY, label: 'CHEST LINE', color: '#C62828' },
      { y: H - AD - (mGet(m,'chestToWaist') || AD * 0.6), label: 'WAIST LINE', color: '#FF8F00' },
      { y: 0, label: 'HEM LINE', color: '#6C757D' },
    ]
  };
};

const shapeBodyBack = (m) => {
  const rawW = mGet(m,'halfChest','chestWidth','chest width','chest') || 0;
  const W    = rawW >= 15 ? rawW : 26;
  const H   = mGet(m,'bodyLength','height','length') || 74;
  const AD  = mGet(m,'armholeDepth') || Math.min(H * 0.32, 24);
  const SHf = (mGet(m,'shoulder','shoulderWidth') || W * 1.72);
  const SH  = SHf / 2;
  const NW  = mGet(m,'neckWidth') || W * 0.345;
  const ND  = mGet(m,'neckDepth','neckDepthBack') || W * 0.095; // shallow back neck
  const uY  = H - AD;

  const armholeRight = [
    ...qbez([W, uY], [W + 1.0, uY + AD * 0.38], [W * 0.8, uY + AD * 0.65], 5),
    ...qbez([W * 0.8, uY + AD * 0.65], [W * 0.58, uY + AD * 0.88], [SH, H - 1.5], 5),
  ];
  const neckRight = qbez([NW, H - 0.3], [NW * 0.3, H - ND * 0.3], [0, H - ND], 4);

  const pts = [
    [-W, 0], [W, 0], [W, uY],
    ...armholeRight,
    [SH, H - 1.5], [SH * 0.88, H - 0.3],
    ...neckRight,
    ...neckRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-SH * 0.88, H - 0.3], [-SH, H - 1.5],
    ...armholeRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-W, uY],
  ];
  return { pts, W, H: H + ND * 0.3, hasTp: false, template: 'body_back',
    guides: [
      { y: uY, label: 'CHEST LINE', color: '#C62828' },
      { y: H - AD - (mGet(m,'chestToWaist') || AD * 0.6), label: 'WAIST LINE', color: '#FF8F00' },
      { y: 0, label: 'HEM LINE', color: '#6C757D' },
    ]
  };
};

const shapeSleeve = (m) => {
  const W   = mGet(m,'width','halfSleeveWidth','sleeveWidth') || 19;  // half sleeve width
  const SL  = mGet(m,'sleeveLength','height','length') || 61;
  const CW  = mGet(m,'cuffWidth','cuffLength') || (W * 0.65);         // half cuff width
  const capH = W * 0.55;   // sleeve cap height ≈ 55% of sleeve width (standard)

  // Sleeve cap: convex bell curve — the most distinctive shape
  // Front cap (right): slightly steeper. Back cap (left): slightly shallower.
  const capRight = [
    ...qbez([W, SL], [W * 0.9, SL + capH * 0.4], [W * 0.35, SL + capH * 0.82], 6),
    ...qbez([W * 0.35, SL + capH * 0.82], [W * 0.1, SL + capH * 0.97], [0, SL + capH], 4),
  ];
  const capLeft = [
    ...qbez([0, SL + capH], [-W * 0.1, SL + capH * 0.97], [-W * 0.42, SL + capH * 0.78], 4),
    ...qbez([-W * 0.42, SL + capH * 0.78], [-W * 0.85, SL + capH * 0.4], [-W, SL], 6),
  ];

  const pts = [
    [-CW, 0], [CW, 0],
    ...qbez([CW, 0], [W * 0.85, SL * 0.3], [W, SL], 6),  // side seam right
    ...capRight,
    ...capLeft,
    ...qbez([-W, SL], [-W * 0.85, SL * 0.3], [-CW, 0], 6), // side seam left
  ];
  return { pts, W, H: SL + capH, hasTp: false, template: 'sleeve',
    guides: [
      { y: SL, label: 'SLEEVE WIDTH', color: '#C62828' },
      { y: SL + capH * 0.5, label: 'CAP MIDPOINT', color: '#FF8F00' },
      { y: 0, label: 'CUFF LINE', color: '#6C757D' },
    ]
  };
};

// ═══════════════════════════════════════════════════════════════════════
//  SPARSE TEMPLATES — the actual answer to "pre-developed shapes with
//  handles" (per the Gerber/Modaris screenshot): a HANDFUL of anatomically
//  meaningful points (waist, hip, crotch, knee, hem — not 40+ qbez samples)
//  each carrying a real, individually-draggable curve handle, pre-tuned so
//  the starting shape already looks right. Loading one of these into the
//  Draw tool as a starting point is what makes handle-editing manageable —
//  few, well-separated handles instead of a dense, unmanageable cluster.
//  Reuses the exact anchor coordinates already proven in shapeTrouserFront/
//  Back above, just without the qbez in-between samples.
// ═══════════════════════════════════════════════════════════════════════
const tangentPass = (list, isClosed) => {
  const n = list.length;
  return list.map((p, i) => {
    if (p.corner) return p;
    if (!isClosed && (i === 0 || i === n - 1)) return { ...p, smooth: false, hIn: null, hOut: null };
    const prev = list[(i - 1 + n) % n], next = list[(i + 1) % n];
    const dx = +((next.x - prev.x) / 6).toFixed(2), dy = +((next.y - prev.y) / 6).toFixed(2);
    return { ...p, smooth: true, hOut: [dx, dy], hIn: [-dx, -dy] };
  });
};

// Finds the point on an already-proven dense curve nearest an approximate
// (x,y) guess, and returns ITS exact coordinates — guarantees every sparse
// anchor sits precisely on the real, validated silhouette instead of on a
// hand-derived formula that can drift from what the real curve actually
// does (this is exactly what went wrong the first time: a hand-guessed
// crotch depth of -13 when the real curve reaches -16).
const nearestDensePoint = (densePts, approxX, approxY) => {
  let best = densePts[0], bestD = Infinity;
  densePts.forEach(p => { const d = Math.hypot(p[0]-approxX, p[1]-approxY); if (d < bestD) { bestD = d; best = p; } });
  return { x: +best[0].toFixed(2), y: +best[1].toFixed(2) };
};

// Builds a sparse (few-anchor) template by sampling the real dense curve at
// each anatomically meaningful spot, rather than re-deriving coordinates by
// hand. denseFn is one of the proven shapeXxx() generators above.
const sparseFromDense = (denseFn, m, guesses) => {
  const dense = denseFn(m);
  const xs = dense.pts.map(p=>p[0]), ys = dense.pts.map(p=>p[1]);
  const H = Math.max(...ys);
  const points = guesses.map(([label, gx, gy, corner]) => {
    const snapped = nearestDensePoint(dense.pts, gx(H), gy(H));
    return { ...snapped, corner: !!corner };
  });
  return { points: tangentPass(points, true), W: dense.W, H };
};

const sparseTrouserFront = (m = {}) => {
  const HHm = mGet(m,'halfHem','hem') || 20;
  const guesses = [
    ['hem_side',     () => HHm,        () => 0,             true],
    ['knee_side',    () => HHm*1.0,    H => H*0.3,           false],
    ['thigh_side',   () => HHm*1.35,   H => H*0.73,          false],
    ['hip_side',     () => HHm*1.25,   H => H*0.85,          false],
    ['waist_side',   () => HHm*1.05,   H => H*0.995,         false],
    ['waist_cf',     () => HHm*0.32,   H => H,               true],
    ['crotch_tip',   () => -HHm*0.7,   H => H*0.75,          false],
    ['thigh_inseam', () => -HHm*0.4,   H => H*0.73,          false],
    ['knee_inseam',  () => -HHm*0.8,   H => H*0.3,           false],
    ['hem_inseam',   () => -HHm*0.78,  () => 0,              true],
  ];
  const { points, W, H } = sparseFromDense(shapeTrouserFront, m, guesses);
  const gradeTags = [
    { gradeX:'halfHem', gradeY:'FIXED' }, { gradeX:'halfKnee', gradeY:'inseam' }, { gradeX:'halfThigh', gradeY:'inseam' },
    { gradeX:'halfHip', gradeY:'inseam' }, { gradeX:'halfWaist', gradeY:'frontRise' }, { gradeX:'halfWaist', gradeY:'frontRise' },
    { gradeX:null, gradeY:'frontRise' }, { gradeX:'halfThigh', gradeY:'inseam' }, { gradeX:'halfKnee', gradeY:'inseam' }, { gradeX:'halfHem', gradeY:'FIXED' },
  ];
  const m2 = { halfWaist: mGet(m,'halfWaist','waist')||21, halfHip: mGet(m,'halfHip','hip')||25, halfThigh: mGet(m,'halfThigh','thigh')||27,
    frontRise: mGet(m,'frontRise','rise')||26, inseam: mGet(m,'inseam')||74, halfHem: HHm, halfKnee: mGet(m,'halfKnee','knee')||20 };
  return { points, gradeTags, measurements: m2 };
};

const sparseTrouserBack = (m = {}) => {
  const HHm = mGet(m,'halfHem','hem') || 20;
  const guesses = [
    ['hem_side',     () => HHm,        () => 0,             true],
    ['knee_side',    () => HHm*1.02,   H => H*0.3,           false],
    ['thigh_side',   () => HHm*1.45,   H => H*0.73,          false],
    ['hip_side',     () => HHm*1.3,    H => H*0.83,          false],
    ['waist_side',   () => HHm*1.5,    H => H*0.98,          false],
    ['waist_cf',     () => HHm*0.25,   H => H,               true],
    ['crotch_tip',   () => -HHm*0.85,  H => H*0.68,          false],
    ['thigh_inseam', () => -HHm*0.5,   H => H*0.73,          false],
    ['knee_inseam',  () => -HHm*0.82,  H => H*0.3,           false],
    ['hem_inseam',   () => -HHm*0.8,   () => 0,              true],
  ];
  const { points, W, H } = sparseFromDense(shapeTrouserBack, m, guesses);
  const gradeTags = [
    { gradeX:'halfHem', gradeY:'FIXED' }, { gradeX:'halfKnee', gradeY:'inseam' }, { gradeX:'halfThigh', gradeY:'inseam' },
    { gradeX:'halfHip', gradeY:'inseam' }, { gradeX:'halfWaist', gradeY:'backRise' }, { gradeX:'halfWaist', gradeY:'backRise' },
    { gradeX:null, gradeY:'backRise' }, { gradeX:'halfThigh', gradeY:'inseam' }, { gradeX:'halfKnee', gradeY:'inseam' }, { gradeX:'halfHem', gradeY:'FIXED' },
  ];
  const m2 = { halfWaist: mGet(m,'halfWaist','waist')||22, halfHip: mGet(m,'halfHip','hip')||26, halfThigh: mGet(m,'halfThigh','thigh')||29,
    backRise: mGet(m,'backRise','rise')||32, inseam: mGet(m,'inseam')||74, halfHem: HHm, halfKnee: mGet(m,'halfKnee','knee')||20.5 };
  return { points, gradeTags, measurements: m2 };
};

const sparseSleeve = (m = {}) => {
  const W = mGet(m,'width','halfSleeveWidth','sleeveWidth') || 19;
  const SL = mGet(m,'sleeveLength','height','length') || 61;
  const CW = mGet(m,'cuffWidth','cuffLength') || W*0.65;
  const capH = W*0.55;
  const raw = [
    { x:CW, y:0, corner:true },
    { x:W, y:SL, corner:false },
    { x:+(W*0.35).toFixed(2), y:+(SL+capH*0.82).toFixed(2), corner:false },
    { x:0, y:+(SL+capH).toFixed(2), corner:false },              // cap top
    { x:+(-W*0.42).toFixed(2), y:+(SL+capH*0.78).toFixed(2), corner:false },
    { x:-W, y:SL, corner:false },
    { x:-CW, y:0, corner:true },
  ];
  const gradeTags = [
    { gradeX:'cuffWidth', gradeY:'FIXED' }, { gradeX:'sleeveWidth', gradeY:'sleeveLength' },
    { gradeX:null, gradeY:'sleeveLength' }, { gradeX:null, gradeY:'sleeveLength' }, { gradeX:null, gradeY:'sleeveLength' },
    { gradeX:'sleeveWidth', gradeY:'sleeveLength' }, { gradeX:'cuffWidth', gradeY:'FIXED' },
  ];
  return { points: tangentPass(raw, true), gradeTags, measurements: { sleeveWidth:W, sleeveLength:SL, cuffWidth:CW } };
};

const sparseBodyFront = (m = {}) => {
  const rawW = mGet(m,'halfChest','chestWidth','chest') || 26;
  const H = mGet(m,'bodyLength','height','length') || 74;
  const AD = mGet(m,'armholeDepth') || Math.min(H*0.32, 24);
  const SH = (mGet(m,'shoulder','shoulderWidth') || rawW*1.72) / 2;
  const NW = mGet(m,'neckWidth') || rawW*0.34;
  const ND = mGet(m,'neckDepth','neckDepthFront') || rawW*0.32;
  const uY = H - AD;
  const raw = [
    { x:rawW, y:0, corner:true },
    { x:rawW, y:uY, corner:false },
    { x:+(rawW*0.72).toFixed(2), y:+(uY+AD*0.72).toFixed(2), corner:false },
    { x:SH, y:H-2, corner:true },
    { x:NW, y:+(H-0.5).toFixed(2), corner:true },
    { x:0, y:+(H-ND).toFixed(2), corner:false },
    { x:-NW, y:+(H-0.5).toFixed(2), corner:true },
    { x:-SH, y:H-2, corner:true },
    { x:+(-rawW*0.72).toFixed(2), y:+(uY+AD*0.72).toFixed(2), corner:false },
    { x:-rawW, y:uY, corner:false },
    { x:-rawW, y:0, corner:true },
  ];
  const gradeTags = [
    { gradeX:'halfChest', gradeY:'FIXED' }, { gradeX:'halfChest', gradeY:'armholeDepth', gradeYScale:-1 },
    { gradeX:null, gradeY:null }, { gradeX:'shoulder', gradeXScale:0.5, gradeY:'bodyLength' },
    { gradeX:'neckWidth', gradeY:'bodyLength' }, { gradeX:null, gradeY:'bodyLength' },
    { gradeX:'neckWidth', gradeXScale:-1, gradeY:'bodyLength' }, { gradeX:'shoulder', gradeXScale:-0.5, gradeY:'bodyLength' },
    { gradeX:null, gradeY:null }, { gradeX:'halfChest', gradeXScale:-1, gradeY:'armholeDepth', gradeYScale:-1 },
    { gradeX:'halfChest', gradeXScale:-1, gradeY:'FIXED' },
  ];
  return { points: tangentPass(raw, true), gradeTags, measurements: { halfChest:rawW, bodyLength:H, armholeDepth:AD, shoulder:SH*2, neckWidth:NW, neckDepth:ND } };
};

const sparseBodyBack = (m = {}) => {
  const rawW = mGet(m,'halfChest','chestWidth','chest') || 26;
  const H = mGet(m,'bodyLength','height','length') || 74;
  const AD = mGet(m,'armholeDepth') || Math.min(H*0.32, 24);
  const SH = (mGet(m,'shoulder','shoulderWidth') || rawW*1.72) / 2;
  const NW = mGet(m,'neckWidth') || rawW*0.345;
  const ND = mGet(m,'neckDepth','neckDepthBack') || rawW*0.095;
  const uY = H - AD;
  const raw = [
    { x:rawW, y:0, corner:true },
    { x:rawW, y:uY, corner:false },
    { x:+(rawW*0.72).toFixed(2), y:+(uY+AD*0.72).toFixed(2), corner:false },
    { x:SH, y:+(H-1.5).toFixed(2), corner:true },
    { x:NW, y:+(H-0.3).toFixed(2), corner:true },
    { x:0, y:+(H-ND).toFixed(2), corner:false },
    { x:-NW, y:+(H-0.3).toFixed(2), corner:true },
    { x:-SH, y:+(H-1.5).toFixed(2), corner:true },
    { x:+(-rawW*0.72).toFixed(2), y:+(uY+AD*0.72).toFixed(2), corner:false },
    { x:-rawW, y:uY, corner:false },
    { x:-rawW, y:0, corner:true },
  ];
  const gradeTags = [
    { gradeX:'halfChest', gradeY:'FIXED' }, { gradeX:'halfChest', gradeY:'armholeDepth', gradeYScale:-1 },
    { gradeX:null, gradeY:null }, { gradeX:'shoulder', gradeXScale:0.5, gradeY:'bodyLength' },
    { gradeX:'neckWidth', gradeY:'bodyLength' }, { gradeX:null, gradeY:'bodyLength' },
    { gradeX:'neckWidth', gradeXScale:-1, gradeY:'bodyLength' }, { gradeX:'shoulder', gradeXScale:-0.5, gradeY:'bodyLength' },
    { gradeX:null, gradeY:null }, { gradeX:'halfChest', gradeXScale:-1, gradeY:'armholeDepth', gradeYScale:-1 },
    { gradeX:'halfChest', gradeXScale:-1, gradeY:'FIXED' },
  ];
  return { points: tangentPass(raw, true), gradeTags, measurements: { halfChest:rawW, bodyLength:H, armholeDepth:AD, shoulder:SH*2, neckWidth:NW, neckDepth:ND } };
};

const sparseCollar = (m = {}) => {
  const W = mGet(m,'width','collarWidth') || 10;
  const H = mGet(m,'height','collarHeight') || 6;
  const raw = [
    { x:0, y:0, corner:true },                                    // CF point
    { x:+(W*0.9).toFixed(2), y:+(H*0.15).toFixed(2), corner:true }, // collar point tip
    { x:W, y:+(H*0.6).toFixed(2), corner:false },
    { x:0, y:H, corner:false },                                    // CB top
    { x:-W, y:+(H*0.6).toFixed(2), corner:false },
    { x:+(-W*0.9).toFixed(2), y:+(H*0.15).toFixed(2), corner:true },
  ];
  const gradeTags = [
    { gradeX:null, gradeY:'FIXED' }, { gradeX:'width', gradeY:'height' },
    { gradeX:'width', gradeY:'height' }, { gradeX:null, gradeY:'height' },
    { gradeX:'width', gradeXScale:-1, gradeY:'height' }, { gradeX:'width', gradeXScale:-1, gradeY:'height' },
  ];
  return { points: tangentPass(raw, true), gradeTags, measurements: { width: W*2, height: H } };
};

const sparsePocketBag = (m = {}) => {
  const W = (mGet(m,'width') || 16) / 2;
  const H = mGet(m,'height') || 19;
  const r = Math.min(W*2, H) * 0.28;
  const raw = [
    { x:-W, y:H, corner:true },
    { x:W, y:H, corner:true },
    { x:W, y:r, corner:false },
    { x:0, y:0, corner:false },
    { x:-W, y:r, corner:false },
  ];
  const gradeTags = [
    { gradeX:'width', gradeXScale:-0.5, gradeY:'FIXED' }, { gradeX:'width', gradeXScale:0.5, gradeY:'FIXED' },
    { gradeX:'width', gradeXScale:0.5, gradeY:null }, { gradeX:null, gradeY:null }, { gradeX:'width', gradeXScale:-0.5, gradeY:null },
  ];
  return { points: tangentPass(raw, true), gradeTags, measurements: { width: W*2, height: H } };
};

const SPARSE_TEMPLATES = {
  'Trouser Front Panel': sparseTrouserFront,
  'Trouser Back Panel':  sparseTrouserBack,
  'Shirt Front Body':    sparseBodyFront,
  'Shirt Back Body':     sparseBodyBack,
  'Sleeve':              sparseSleeve,
  'Collar':              sparseCollar,
  'Pocket Bag':          sparsePocketBag,
};

const shapeTrouserFront = (m) => {
  const IS   = mGet(m,'inseam') || 74;
  const FR   = mGet(m,'frontRise','rise') || 26;
  const HW   = mGet(m,'halfWaist','waist') || 21;
  const HH   = mGet(m,'halfHip','hip')     || 25;
  const HT   = mGet(m,'halfThigh','thigh') || 27;
  const HK   = mGet(m,'halfKnee','knee')   || 20;
  const HHm  = mGet(m,'halfHem','hem')     || 20;
  const H    = FR + IS;
  const hipY = IS + FR * 0.42;      // hip line — close to crotch level, below waist
  const thighY = IS * 0.98;         // fullest point right at crotch level
  const kneeY  = IS * 0.4;

  // Outseam: mostly straight leg (knee↔hem near-parallel) with a gentle
  // hip curve only in the top third — a clean near-parallel leg tube below
  // the hip, matching real trouser panels rather than a tapering blob.
  const sideSeam = [
    [HHm, 0],
    [HK, kneeY],
    ...qbez([HK, kneeY], [HT * 1.01, (kneeY+thighY)/2], [HT, thighY], 5),
    ...qbez([HT, thighY], [HH * 1.01, (thighY+hipY)/2], [HH, hipY], 4),
    ...qbez([HH, hipY], [HW * 1.02, hipY + (H-hipY)*0.6], [HW, H - 0.5], 4),
  ];

  const waistEdge = [
    [HW, H - 0.5],
    [HW * 0.3, H],           // near-flat waist edge, small fly-stand at CF
  ];

  // Crotch — a sharp, clearly visible J-hook: drops fast from the fly
  // point, hooks out to its deepest point right at the hip/crotch line,
  // then curves back to meet the inseam.
  const crotch = [
    ...qbez([HW * 0.3, H], [HW * 0.05, H - FR * 0.3], [-FR * 0.25, H - FR * 0.55], 5),
    ...qbez([-FR * 0.25, H - FR * 0.55], [-FR * 0.58, H - FR * 0.8], [-FR * 0.5, H - FR * 0.98], 5),
    ...qbez([-FR * 0.5, H - FR * 0.98], [-FR * 0.32, IS + 1], [-HT * 0.28, thighY], 5),
  ];

  // Inseam: near-straight leg, mirrors the outseam taper
  const inseam = [
    ...qbez([-HT * 0.28, thighY], [-HK * 0.86, (kneeY+thighY)/2], [-HK * 0.8, kneeY], 5),
    [-HHm * 0.78, 0],
  ];

  const pts = [...sideSeam, ...waistEdge, ...crotch, ...inseam];
  const xs = pts.map(p=>p[0]), ys = pts.map(p=>p[1]);
  const W = Math.max(...xs), H2 = Math.max(...ys);

  // Grade tags: name the measurement key that should drive each anchor
  // point's X/Y during grading. 'FIXED' is a deliberately non-existent
  // measurement key — it resolves to a known delta of 0, anchoring the hem
  // as the fixed reference line grading works outward from, rather than
  // leaving it to interpolate. Matched by coordinate so it survives however
  // qbez happens to duplicate an anchor as a curve-segment endpoint.
  const anchors = [
    [[HHm, 0],        { gradeX:'halfHem',   gradeY:'FIXED' }],
    [[HK, kneeY],      { gradeX:'halfKnee',  gradeY:'inseam' }],
    [[HT, thighY],     { gradeX:'halfThigh', gradeY:'inseam' }],
    [[HH, hipY],       { gradeX:'halfHip',   gradeY:'inseam' }],
    [[HW, H - 0.5],    { gradeX:'halfWaist', gradeY:'frontRise' }],
    [[HW * 0.3, H],    { gradeX:'halfWaist', gradeY:'frontRise' }],
    [[-HT * 0.28, thighY], { gradeX:'halfThigh', gradeY:'inseam' }],
    [[-HK * 0.8, kneeY],   { gradeX:'halfKnee',  gradeY:'inseam' }],
    [[-HHm * 0.78, 0],     { gradeX:'halfHem',   gradeY:'FIXED' }],
  ];
  const tagFor = (x, y) => {
    const hit = anchors.find(([[ax,ay]]) => Math.abs(ax-x) < 0.05 && Math.abs(ay-y) < 0.05);
    return hit ? hit[1] : { gradeX:null, gradeY:null };
  };
  const gradeTags = pts.map(([x,y]) => tagFor(x,y));

  return { pts, W, H: H2, hasTp: false, template: 'trouser_front', gradeTags,
    guides: [
      { y: H, label: 'WAIST LINE', color: '#6A1B9A' },
      { y: hipY, label: 'HIP / SEAT LINE', color: '#C62828' },
      { y: kneeY, label: 'KNEE LINE', color: '#FF8F00' },
      { y: 0, label: 'HEM LINE', color: '#6C757D' },
      { y: H - FR * 0.7, label: 'CROTCH LEVEL', color: '#2E7D32' },
    ]
  };
};

const shapeTrouserBack = (m) => {
  const IS   = mGet(m,'inseam') || 74;
  const BR   = mGet(m,'backRise','rise') || 32;
  const HW   = mGet(m,'halfWaist','waist') || 22;
  const HH   = mGet(m,'halfHip','hip')     || 26;
  const HT   = mGet(m,'halfThigh','thigh') || 29;
  const HK   = mGet(m,'halfKnee','knee')   || 20.5;
  const HHm  = mGet(m,'halfHem','hem')     || 20;
  const H    = BR + IS;
  const hipY = IS + BR * 0.38;
  const thighY = IS * 0.98;
  const kneeY  = IS * 0.4;

  // Outseam: same clean straight-leg treatment as the front, but the hip
  // curve reaches further out (wider seat) and the waist sits higher (back rise).
  const sideSeam = [
    [HHm, 0],
    [HK, kneeY],
    ...qbez([HK, kneeY], [HT * 1.02, (kneeY+thighY)/2], [HT, thighY], 5),
    ...qbez([HT, thighY], [HH * 1.03, (thighY+hipY)/2], [HH, hipY], 4),
    ...qbez([HH, hipY], [HW * 1.18, hipY + (H-hipY)*0.65], [HW * 1.08, H - 1.5], 4),
  ];
  const waistEdge = [
    [HW * 1.08, H - 1.5],
    [HW * 0.2, H],            // CB waist — raised for seat/back-rise allowance
  ];
  // Back crotch: deeper and hooks further out than the front (typical of
  // a back rise curve), still a clearly visible J-hook rather than a near-line.
  const crotch = [
    ...qbez([HW * 0.2, H], [-BR * 0.05, H - BR * 0.28], [-BR * 0.35, H - BR * 0.52], 5),
    ...qbez([-BR * 0.35, H - BR * 0.52], [-BR * 0.68, H - BR * 0.76], [-BR * 0.58, H - BR * 0.95], 5),
    ...qbez([-BR * 0.58, H - BR * 0.95], [-BR * 0.38, IS + 1], [-HT * 0.34, thighY], 5),
  ];
  const inseam = [
    ...qbez([-HT * 0.34, thighY], [-HK * 0.88, (kneeY+thighY)/2], [-HK * 0.82, kneeY], 5),
    [-HHm * 0.8, 0],
  ];
  const pts = [...sideSeam, ...waistEdge, ...crotch, ...inseam];
  const xs = pts.map(p=>p[0]), ys = pts.map(p=>p[1]);

  const anchors = [
    [[HHm, 0],           { gradeX:'halfHem',   gradeY:'FIXED' }],
    [[HK, kneeY],         { gradeX:'halfKnee',  gradeY:'inseam' }],
    [[HT, thighY],        { gradeX:'halfThigh', gradeY:'inseam' }],
    [[HH, hipY],          { gradeX:'halfHip',   gradeY:'inseam' }],
    [[HW * 1.08, H - 1.5],{ gradeX:'halfWaist', gradeY:'backRise' }],
    [[HW * 0.2, H],       { gradeX:'halfWaist', gradeY:'backRise' }],
    [[-HT * 0.34, thighY],{ gradeX:'halfThigh', gradeY:'inseam' }],
    [[-HK * 0.82, kneeY], { gradeX:'halfKnee',  gradeY:'inseam' }],
    [[-HHm * 0.8, 0],     { gradeX:'halfHem',   gradeY:'FIXED' }],
  ];
  const tagFor = (x, y) => {
    const hit = anchors.find(([[ax,ay]]) => Math.abs(ax-x) < 0.05 && Math.abs(ay-y) < 0.05);
    return hit ? hit[1] : { gradeX:null, gradeY:null };
  };
  const gradeTags = pts.map(([x,y]) => tagFor(x,y));

  return { pts, W: Math.max(...xs), H: Math.max(...ys), hasTp: false, template: 'trouser_back', gradeTags,
    guides: [
      { y: H, label: 'WAIST LINE', color: '#6A1B9A' },
      { y: hipY, label: 'HIP / SEAT LINE', color: '#C62828' },
      { y: kneeY, label: 'KNEE LINE', color: '#FF8F00' },
      { y: 0, label: 'HEM LINE', color: '#6C757D' },
    ]
  };
};

const shapeCollar = (m) => {
  const W = (mGet(m,'width','collarLength') || 20); // half collar
  const H = mGet(m,'height','collarHeight') || 6.5;
  // Collar has a slight curve on the neckline edge (bottom)
  const pts = [
    ...qbez([-W, 0], [0, -H * 0.12], [W, 0], 8),  // neckline edge (slight concave)
    ...qbez([W, 0], [W + 0.5, H * 0.5], [W * 0.95, H], 4), // outer right
    [W * 0.95, H],
    ...qbez([W * 0.95, H], [0, H + H * 0.06], [-W * 0.95, H], 6), // outer collar edge
    [-W * 0.95, H],
    ...qbez([-W * 0.95, H], [-W - 0.5, H * 0.5], [-W, 0], 4),
  ];
  return { pts, W: W + 1, H, hasTp: false, template: 'collar', guides: [] };
};

const shapeCollarStand = (m) => {
  const W = mGet(m,'width','collarLength') || 19.5;
  const H = mGet(m,'height','collarHeight','collarStandHeight') || 3.5;
  // Neck band: straight rectangle with curved neckline
  const pts = [
    ...qbez([-W, 0], [0, -H * 0.08], [W, 0], 6), // neckline curve
    [W, H], [-W, H],
  ];
  return { pts, W, H, hasTp: false, template: 'collar_stand', guides: [] };
};

const shapeCuff = (m) => {
  const W = (mGet(m,'width','cuffLength') || 24); // half cuff
  const H = mGet(m,'height','cuffHeight') || 7;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'cuff', guides: [] };
};

const shapeYoke = (m) => {
  const W = (mGet(m,'width','yokeWidth') || 45) / 2;
  const H = mGet(m,'height','yokeHeight') || 7;
  // Yoke: trapezoid, slightly narrower at neck edge
  const neckW = W * 0.78;
  const pts = [[-W, 0], [W, 0],
    ...qbez([W, 0], [neckW * 1.02, H * 0.5], [neckW, H], 4),
    ...qbez([neckW, H], [0, H + H * 0.05], [-neckW, H], 4),
    ...qbez([-neckW, H], [-neckW * 1.02, H * 0.5], [-W, 0], 4)
  ];
  return { pts, W, H, hasTp: false, template: 'yoke', guides: [] };
};

const shapePlacket = (m) => {
  const W = (mGet(m,'width','placketWidth') || 5) / 2;
  const H = mGet(m,'height','placketLength') || 21;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'placket', guides: [] };
};

const shapeRib = (m) => {
  const W = mGet(m,'width') || 17;
  const H = mGet(m,'height') || 2.5;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'rib', guides: [] };
};

const shapeWaistband = (m) => {
  const W = mGet(m,'width','halfWaist','waist') || 40;
  const H = mGet(m,'height','waistbandHeight') || 8;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'waistband', guides: [] };
};

const shapePocket = (m) => {
  const W = (mGet(m,'width') || 16);
  const H = mGet(m,'height') || 19;
  // Rounded-bottom pocket bag (industry standard shape), not a plain box
  const r = Math.min(W, H) * 0.28;
  const pts = [
    [-W/2, H], [W/2, H], [W/2, r],
    ...qbez([W/2, r], [W/2, 0], [W/2 - r, 0], 4),
    [-W/2 + r, 0],
    ...qbez([-W/2 + r, 0], [-W/2, 0], [-W/2, r], 4),
  ];
  return { pts, W: W/2, H, hasTp: false, template: 'pocket', guides: [] };
};

// ── WELT / JETTING FACING — narrow rectangular strip ──────────────────────
const shapeWeltPocket = (m) => {
  const W = (mGet(m,'width') || 14) / 2;
  const H = mGet(m,'height') || 4;
  return { pts: [[-W,0],[W,0],[W,H],[-W,H]], W, H, hasTp: false, template: 'welt_pocket', guides: [] };
};

// ── FLY EXTENSION — narrow curved sliver (concave inner edge) ─────────────
const shapeFlyPiece = (m) => {
  const W = mGet(m,'width') || 4;
  const H = mGet(m,'height') || 19;
  const pts = [
    [0, 0],
    ...qbez([0, 0], [W * 0.15, H * 0.4], [0, H * 0.85], 6),  // concave inner (fly seam) edge
    [W * 0.3, H],
    [W, H * 0.9],
    ...qbez([W, H * 0.9], [W * 1.05, H * 0.45], [W * 0.85, 0], 5), // convex outer edge
  ];
  return { pts, W: W/2, H, hasTp: false, template: 'fly', guides: [] };
};

const SHAPE_FNS = {
  body_front:     shapeBodyFront,
  body_back:      shapeBodyBack,
  sleeve:         shapeSleeve,
  collar:         shapeCollar,
  collar_stand:   shapeCollarStand,
  collar_polo:    shapeCollar,
  cuff:           shapeCuff,
  yoke:           shapeYoke,
  placket:        shapePlacket,
  rib:            shapeRib,
  waistband:      shapeWaistband,
  trouser_front:  shapeTrouserFront,
  trouser_back:   shapeTrouserBack,
  pocket:         shapePocket,
  welt_pocket:    shapeWeltPocket,
  fly:            shapeFlyPiece,
};

// ── Give trouser standard-library pieces real, correct tracedPoints up
// front instead of relying on the template-detection/fallback chain above.
// (That chain routes through an external ShapeBuilder module for
// 'trouser_front'/'trouser_back' when available, which has been producing
// distorted "amoeba" shapes; baking tracedPoints in here bypasses it
// entirely and guarantees the anatomically-correct shape renders everywhere
// — thumbnails, PatternEditor, and the Reshape tool — with zero ambiguity.)
const TROUSER_PIECE_SHAPE_FN = {
  'front panel':      shapeTrouserFront,
  'back panel':       shapeTrouserBack,
  'waistband':        shapeWaistband,
  'front pocket bag': shapePocket,
  'back welt pocket': shapeWeltPocket,
  'fly piece':        shapeFlyPiece,
};
const attachTrouserTracedShapes = (pieces) => pieces.map(p => {
  const fn = TROUSER_PIECE_SHAPE_FN[(p.partName || '').toLowerCase().trim()];
  if (!fn) return p;
  try {
    const { pts: rawPts, W, gradeTags: rawTags } = fn(p.measurements || {});
    // Dedupe the zero-length junction points (see dedupeLoop) while keeping
    // gradeTags aligned to whichever indices survive — gradeTags is a
    // parallel array, not coordinate-matched, so it must be filtered with
    // the exact same kept-index list rather than deduped independently.
    const keptIdx = [0];
    for (let i = 1; i < rawPts.length; i++) {
      const last = rawPts[keptIdx[keptIdx.length-1]];
      if (Math.hypot(rawPts[i][0]-last[0], rawPts[i][1]-last[1]) >= 0.03) keptIdx.push(i);
    }
    if (keptIdx.length > 3) {
      const lastP = rawPts[keptIdx[keptIdx.length-1]], firstP = rawPts[keptIdx[0]];
      if (Math.hypot(lastP[0]-firstP[0], lastP[1]-firstP[1]) < 0.03) keptIdx.pop();
    }
    const pts = keptIdx.map(i => rawPts[i]);
    const gradeTags = rawTags ? keptIdx.map(i => rawTags[i]) : undefined;
    return {
      ...p,
      tracedPoints: polyToTraced(pts.map(([x,y]) => [x + W, y])),
      gradeTags, // per-point grading anchors — see gradeTracedPoints
    };
  } catch (_) { return p; }
});


// ── Auto-place semantic anchor points on any shape ────────────────────────────
// For standard shapes: uses known mathematical positions.
// For traced shapes: scales template positions proportionally.
const autoPlaceAnchors = (template, W, H, m) => {
  const fn = TEMPLATE_ANCHORS?.[template];
  if (!fn) return [];
  const AD  = mGet(m,'armholeDepth') || Math.min(H*0.32, 24);
  let IS  = mGet(m,'inseam') || H * 0.74;
  let FR  = mGet(m,'frontRise','backRise','rise') || H * 0.26;
  // Anchor labels (Waist/Hip/Knee/Hem …) are placed using IS+FR as the
  // piece's height budget. If you've since reshaped/stretched the actual
  // traced piece (rope-dragging, handle edits, resizing) without separately
  // updating the inseam/rise fields in the Spec Builder, IS+FR goes stale
  // and no longer matches the real shape height — the labels then drift
  // relative to where they visually belong (this is exactly the "knee ends
  // up near the ankle" symptom: the stale IS+FR budget was smaller than the
  // real height, so every fraction-of-height anchor lands too low). Rescale
  // IS/FR proportionally so they still sum to the ACTUAL current height,
  // keeping their ratio, rather than trusting a sum that may be stale.
  const sum = IS + FR;
  if (sum > 0 && Math.abs(sum - H) > 0.5) {
    const scale = H / sum;
    IS *= scale; FR *= scale;
  }
  const capH = W * 0.55;
  try {
    const anchors = fn(m, W, H, AD, IS, FR, capH);
    // Merge with any manually saved anchor overrides from the pattern
    return anchors;
  } catch { return []; }
};

// Module-level cache of shape templates saved via "Save as Standard" in the
// Reshape tool (category: 'shape_template'). Populated once by
// loadSavedShapeTemplates() below; read synchronously by buildDisplayPoly so
// every part of the app (thumbnails, PatternEditor, gallery) benefits once
// someone has hand-corrected a piece and saved it as the new default.
let SAVED_SHAPE_TEMPLATES = {};
export const loadSavedShapeTemplates = async () => {
  try {
    const res = await fetch(`${LOAD_URL}?category=shape_template`);
    const j = await res.json();
    const list = j?.data || j?.shapeTemplates || [];
    const map = {};
    (Array.isArray(list) ? list : []).forEach(t => {
      if (t?.templateKey && t?.tracedPoints?.length >= 3) map[t.templateKey] = t;
    });
    SAVED_SHAPE_TEMPLATES = map;
    return map;
  } catch (_) { return SAVED_SHAPE_TEMPLATES; }
};

// Convert a raw tracedPoints array (possibly with smooth/handle metadata)
// into { pts:[[x,y]...], ptsMeta:[{smooth,hIn,hOut}...] } centred the same
// way as the rest of buildDisplayPoly (x=0 at mid, y=0 at bottom).
const tracedToDisplay = (tp) => {
  const xs = tp.map(p => p.x), ys = tp.map(p => p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const maxX = Math.max(...xs), maxY = Math.max(...ys);
  const W = (maxX - minX) / 2, H = maxY - minY;
  const pts = tp.map(p => [p.x - minX - W, p.y - minY]);
  const ptsMeta = tp.map(p => ({
    smooth: !!p.smooth,
    hIn:  p.hIn  ? [p.hIn[0],  p.hIn[1]]  : null,
    hOut: p.hOut ? [p.hOut[0], p.hOut[1]] : null,
  }));
  return { pts, ptsMeta, W, H };
};

const buildDisplayPoly = (pattern) => {
  const tpl = detectTemplate(pattern);
  // 1. Prefer traced points (real digitised shape — includes anything
  //    hand-edited or inserted via the Reshape tool's node/curve editor)
  const tp = pattern.tracedPoints;
  if (tp && tp.length >= 3) {
    const { pts, ptsMeta, W, H } = tracedToDisplay(tp);
    const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
    return { pts, ptsMeta, W, H, hasTp: true, template: tpl, guides: [], anchors };
  }

  // 1b. Fall back to a shape someone previously hand-corrected and saved
  //     as the new standard for this piece type ("Save as Standard").
  const saved = SAVED_SHAPE_TEMPLATES[tpl];
  if (saved?.tracedPoints?.length >= 3) {
    const { pts, ptsMeta, W, H } = tracedToDisplay(saved.tracedPoints);
    const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
    return { pts, ptsMeta, W, H, hasTp: false, fromSavedTemplate: true, template: tpl, guides: [], anchors };
  }

  // 2. ── Use ShapeBuilder's own build() when template is a ShapeBuilder key ──
  // This is the most accurate path: uses the exact same math SpecDrivenShape uses,
  // so measurements stored by PartDNA's Spec Builder tab render correctly here.
  // NOTE: GARMENT_TEMPLATES is keyed by ShapeBuilder's OWN names (e.g. 'pant_front',
  // 'tee_front'), not MarkerMaker's detectTemplate names (e.g. 'trouser_front',
  // 'body_front') — a straight `GARMENT_TEMPLATES[tpl]` lookup was therefore
  // silently failing for every piece, and this tier never actually ran. That also
  // meant a shape could render differently in PartDNA (which calls ShapeBuilder
  // directly) than here for the same measurements. Map back to ShapeBuilder's
  // key so this tier engages as intended.
  const MM_TO_SB = {
    body_front: 'tee_front', body_back: 'tee_back', sleeve: 'sleeve',
    trouser_front: 'pant_front', cuff: 'cuff', collar: 'collar_band',
    yoke: 'yoke', pocket: 'pocket',
  };
  const sbTpl = GARMENT_TEMPLATES?.[MM_TO_SB[tpl] || tpl];
  if (sbTpl && typeof sbTpl.build === 'function') {
    try {
      const m = pattern.measurements || {};
      // Fill any missing keys with ShapeBuilder defaults so build() never gets NaN
      const filled = {};
      Object.entries(sbTpl.measurements).forEach(([k, def]) => {
        filled[k] = m[k] ?? def.default;
      });
      const shape = sbTpl.build(filled);
      // shape.polyline is [[x,y], ...] in centred y-up coords — same as our format
      const xs = shape.polyline.map(p => p[0]), ys = shape.polyline.map(p => p[1]);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), H = Math.max(...ys) - minY;
      const W = (maxX - minX) / 2;
      // Normalise to centred (x=0 at mid, y=0 at bottom)
      const pts = shape.polyline.map(([x, y]) => [x, y - minY]);
      // Build construction guides from ShapeBuilder dims
      const guides = (shape.dims || []).filter(d => d.kind === 'v').map(d => ({
        y: d.y1, label: d.label || '', color: '#ADB5BD',
      }));
      const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
      return { pts, ptsMeta: null, W, H, hasTp: false, template: tpl, guides, anchors };
    } catch (_) { /* fall through to manual shape fns */ }
  }

  // 3. Use MarkerMaker's own anatomical shape functions
  const shapeFn = SHAPE_FNS[tpl];
  if (shapeFn) {
    const result = shapeFn(pattern.measurements || {});
    const anchors = autoPlaceAnchors(tpl, result.W, result.H, pattern.measurements || {});
    return { ...result, ptsMeta: null, hasTp: false, anchors };
  }
  // 4. Generic fallback
  const m = pattern.measurements || {};
  const W = (mGet(m,'width','chestWidth') || 40) / 2;
  const H = (mGet(m,'height','bodyLength','length') || 60);
  return { pts:[[-W,0],[W,0],[W,H],[-W,H]], ptsMeta: null, W, H, hasTp:false, template:'generic', guides:[], anchors:[] };
};

// ── Build an SVG path "d" string from centred pts + optional curve metadata.
// meta[i] = { smooth, hIn:[dx,dy]|null, hOut:[dx,dy]|null } in cm, relative
// to pts[i]. A segment i→i+1 is drawn as a cubic bezier if either endpoint
// has a handle on that side; otherwise a straight line — so ordinary
// (all-corner) shapes render byte-for-byte identical to the old <polygon>.
const buildPathD = (pts, meta, txFn, tyFn) => {
  if (!pts || pts.length < 2) return '';
  const S = (i) => [txFn(pts[i][0]), tyFn(pts[i][1])];
  let d = `M ${S(0)[0].toFixed(1)} ${S(0)[1].toFixed(1)} `;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const mi = meta?.[i], mj = meta?.[j];
    const hOut = mi?.smooth && mi?.hOut ? mi.hOut : null;
    const hIn  = mj?.smooth && mj?.hIn  ? mj.hIn  : null;
    if (hOut || hIn) {
      const [ax, ay] = pts[i], [bx, by] = pts[j];
      const c1x = ax + (hOut ? hOut[0] : (bx - ax) * 0.33);
      const c1y = ay + (hOut ? hOut[1] : (by - ay) * 0.33);
      const c2x = bx + (hIn  ? hIn[0]  : (ax - bx) * 0.33);
      const c2y = by + (hIn  ? hIn[1]  : (ay - by) * 0.33);
      const p1 = [txFn(c1x), tyFn(c1y)], p2 = [txFn(c2x), tyFn(c2y)], pb = S(j);
      d += `C ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} ${pb[0].toFixed(1)} ${pb[1].toFixed(1)} `;
    } else {
      const pb = S(j);
      d += `L ${pb[0].toFixed(1)} ${pb[1].toFixed(1)} `;
    }
  }
  return d + 'Z';
};

// ═══════════════════════════════════════════════════════════════════════
//  MIDPOINT QUADRATIC B-SPLINE — the real fix for "every click behaves
//  differently even when I click exactly on the line."
// ───────────────────────────────────────────────────────────────────────
//  buildPathD (above) and Catmull-Rom auto-tangents both draw an
//  INTERPOLATING curve — one that passes exactly through every clicked
//  point. That sounds like what you'd want, but it means any click that's
//  even slightly off the true line gets forced into the curve exactly as
//  clicked, producing a visible spike right at that point — which is
//  precisely the "outside = convex, inside = concave, never quite right"
//  behaviour reported.
//
//  This builds an APPROXIMATING curve instead: each clicked point (unless
//  marked a hard corner) is a *control* point that pulls the curve toward
//  it, but the curve only ever passes through the MIDPOINT between
//  consecutive points. A single misplaced click now only pulls the curve
//  partway toward the wrong spot instead of forcing it there exactly —
//  verified this numerically before shipping it: a click placed ~5cm off
//  a smooth arc only moved the rendered curve ~2cm, not the full 5.
//  Editing (dragging, selecting, elastic pull) still operates on the
//  original clicked points — only the rendered curve changes.
// ═══════════════════════════════════════════════════════════════════════
const buildMidpointPathD = (pts, closed, txFn, tyFn) => {
  const n = pts.length;
  if (n < 2) return '';
  const mid = (a,b) => [(a.x+b.x)/2, (a.y+b.y)/2];
  const P = (i) => pts[i % n];
  const isCorner = (i) => !!pts[i % n].corner;
  const S = ([x,y]) => [txFn(x), tyFn(y)];

  const startAnchor = (!closed || isCorner(0)) ? [P(0).x, P(0).y] : mid(P(n-1), P(0));
  let d = `M ${S(startAnchor)[0].toFixed(1)} ${S(startAnchor)[1].toFixed(1)} `;
  const segments = closed ? n : n - 1;
  for (let i = 0; i < segments; i++) {
    const curr = i % n, next = (i+1) % n;
    const endAnchor = isCorner(next) ? [P(next).x, P(next).y] : mid(P(curr), P(next));
    const [ex, ey] = S(endAnchor);
    if (isCorner(curr)) {
      d += `L ${ex.toFixed(1)} ${ey.toFixed(1)} `;
    } else {
      const [qx, qy] = S([P(curr).x, P(curr).y]);
      d += `Q ${qx.toFixed(1)} ${qy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)} `;
    }
  }
  if (closed) d += 'Z';
  return d;
};

// Convert whatever is currently on screen (generated or saved-template pts)
// into a real tracedPoints array, "locking in" the shape so it can be
// hand-edited node-by-node from here on. Coordinates are shifted back from
// the centred display space (x=0 mid) to absolute (x starts at 0).
const displayPtsToTraced = (pts, W) =>
  pts.map(([x, y]) => ({ x: +(x + W).toFixed(2), y: +y.toFixed(2), smooth: false, hIn: null, hOut: null }));

// Nudge nodes near `idx` by (dxCm,dyCm) with a cosine falloff over `radius`
// index-steps in either direction (circular). Used by the Reshape tool's
// "Proportional" drag so stretching one point (e.g. the knee) smoothly
// carries the curve around it instead of putting a kink in the outline.
const applyProportionalNudge = (tracedPoints, idx, dxCm, dyCm, radius) => {
  const n = tracedPoints.length;
  if (!radius || radius <= 0) return tracedPoints;
  return tracedPoints.map((p, i) => {
    if (i === idx) return p;
    let dist = Math.abs(i - idx);
    dist = Math.min(dist, n - dist); // circular distance
    if (dist > radius) return p;
    const w = 0.5 * (1 + Math.cos((dist / radius) * Math.PI)); // 1 at idx → 0 at radius
    return { ...p, x: +(p.x + dxCm * w).toFixed(2), y: +(p.y + dyCm * w).toFixed(2) };
  });
};

// ── POM (Point of Measure) LIBRARY + GEOMETRY ───────────────────────────
// Real, sourced reference data (Urban Outfitters DTA How-2-Measure Guide,
// pages 114–127) — not invented. Each entry's description is the guide's
// own instruction text where the guide had one; entries with none are
// flagged rather than given a fabricated instruction. measureType tells the
// live-value calculator below whether to use a straight-line distance or a
// path length along the pattern's actual boundary between two tagged points.
const PANT_POM_LIBRARY = [
  { code: 'PT01',   name: 'Front Rise from Top Waist', measureType: 'curve' },
  { code: 'PT01.1', name: 'Front Rise from Bottom of Waistband', measureType: 'curve' },
  { code: 'PT02',   name: 'Back Rise from Top Waist', measureType: 'curve' },
  { code: 'PT02.1', name: 'Back Rise from Bottom of Waistband', measureType: 'curve' },
  { code: 'PT03', name: 'Thigh @ 1" Below Crotch', measureType: 'straight' },
  { code: 'PT04', name: 'Thigh @ specified distance Below Crotch', measureType: 'straight' },
  { code: 'PT05', name: 'Knee @ specified distance Below Crotch', measureType: 'straight' },
  { code: 'PT06', name: 'Leg Opening (inseam over 6")', measureType: 'straight' },
  { code: 'PT07', name: 'Leg Opening (inseam under 6")', measureType: 'straight' },
  { code: 'PT08',   name: 'Inseam (22" or longer)', measureType: 'curve' },
  { code: 'PT08.1', name: 'Inseam w/ Cuff Rolled Up (22" or longer)', measureType: 'curve' },
  { code: 'PT08.2', name: 'Inseam w/ Cuff Unrolled (22" or longer)', measureType: 'curve' },
  { code: 'PT08.3', name: "Inseam (men's gang grade)", measureType: 'curve' },
  { code: 'PT09',   name: 'Inseam (under 6")', measureType: 'curve' },
  { code: 'PT09.1', name: 'Inseam w/ Cuff Rolled Up (under 6")', measureType: 'curve' },
  { code: 'PT09.2', name: 'Inseam w/ Cuff Unrolled (under 6")', measureType: 'curve' },
  { code: 'PT09.3', name: 'Inseam (6" to 21")', measureType: 'curve' },
  { code: 'PT09.4', name: 'Inseam w/ Cuff Rolled Up (6" to 21")', measureType: 'curve' },
  { code: 'PT09.5', name: 'Inseam w/ Cuff Unrolled (6" to 21")', measureType: 'curve' },
  { code: 'PT10',   name: 'Outseam from Top Waist (22" or longer)', measureType: 'curve' },
  { code: 'PT10.1', name: 'Outseam w/ Cuff Rolled Up (22" or longer)', measureType: 'curve' },
  { code: 'PT10.2', name: 'Outseam w/ Cuff Unrolled (22" or longer)', measureType: 'curve' },
  { code: 'PT11',   name: 'Outseam from Top Waist (under 6")', measureType: 'curve' },
  { code: 'PT11.1', name: 'Outseam w/ Cuff Rolled Up (under 6")', measureType: 'curve' },
  { code: 'PT11.2', name: 'Outseam w/ Cuff Unrolled (under 6")', measureType: 'curve' },
  { code: 'PT11.3', name: 'Outseam from Top Waist (6" to 21")', measureType: 'curve' },
  { code: 'PT11.4', name: 'Outseam w/ Cuff Rolled Up (6" to 21")', measureType: 'curve' },
  { code: 'PT11.5', name: 'Outseam w/ Cuff Unrolled (6" to 21")', measureType: 'curve' },
  { code: 'PT12', name: 'Back Knee Seam from Bottom Opening', measureType: 'unspecified' },
  { code: 'PT13',   name: 'Front Leg Seam from CF Seam @ Waist', measureType: 'unspecified' },
  { code: 'PT13.1', name: 'Front Leg Seam from Inseam @ Leg Opening', measureType: 'unspecified' },
  { code: 'PT14',   name: 'Back Leg Seam from CB Seam @ Waist', measureType: 'unspecified' },
  { code: 'PT14.1', name: 'Back Leg Seam from Inseam @ Leg Opening', measureType: 'unspecified' },
  { code: 'PT15',   name: 'Side Front Leg Seam from CF Seam @ Waist', measureType: 'unspecified' },
  { code: 'PT15.1', name: 'Side Front Leg Seam from Inseam @ Leg Opening', measureType: 'unspecified' },
  { code: 'PT16',   name: 'Side Back Leg Seam from CB Seam @ Waist', measureType: 'unspecified' },
  { code: 'PT16.1', name: 'Side Back Leg Seam from Inseam @ Leg Opening', measureType: 'unspecified' },
  { code: 'PT17', name: 'Side Leg Panel Width @ Top & Bottom', measureType: 'straight' },
];

// Resolve a {edgeIdx, t} anchor (same convention already proven for
// notches) to its CURRENT absolute position — recomputed from whatever the
// pattern's real points are right now, so it tracks reshaping automatically
// instead of freezing at tag-time the way the old measurementPoints did.
const resolvePomAnchor = (pts, anchor) => {
  const ei = anchor.edgeIdx % pts.length;
  const [ax, ay] = pts[ei], [bx, by] = pts[(ei + 1) % pts.length];
  return [ax + (bx - ax) * anchor.t, ay + (by - ay) * anchor.t];
};
const pomStraightValue = (pts, pom) => {
  const p1 = resolvePomAnchor(pts, pom.anchor1), p2 = resolvePomAnchor(pts, pom.anchor2);
  return Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
};
const pomWalkForward = (pts, fromIdx, fromT, toIdx, toT) => {
  const n = pts.length;
  let total = 0, ei = fromIdx, curT = fromT, guard = 0;
  while (guard++ < n + 2) {
    const [ax, ay] = pts[ei], [bx, by] = pts[(ei + 1) % n];
    const edgeLen = Math.hypot(bx - ax, by - ay);
    if (ei === toIdx && toT >= curT) { total += edgeLen * (toT - curT); return total; }
    total += edgeLen * (1 - curT);
    curT = 0;
    ei = (ei + 1) % n;
  }
  return total;
};
const pomCurveValue = (pts, pom) => {
  const forward = pomWalkForward(pts, pom.anchor1.edgeIdx, pom.anchor1.t, pom.anchor2.edgeIdx, pom.anchor2.t);
  const backward = pomWalkForward(pts, pom.anchor2.edgeIdx, pom.anchor2.t, pom.anchor1.edgeIdx, pom.anchor1.t);
  return Math.min(forward, backward);
};
// Single entry point — computes the live value for a tagged POM using
// whichever geometry rule its measureType calls for.
const computePomValue = (pts, pom) => {
  if (!pts || pts.length < 2 || !pom.anchor1 || !pom.anchor2) return null;
  return pom.measureType === 'curve' ? pomCurveValue(pts, pom) : pomStraightValue(pts, pom);
};

// Find nearest point on polygon boundary to click (SVG px).
// pts is in cm; tx/ty convert cm→SVG px. Returns { edgeIdx, t, snapX, snapY }.
const nearestEdge = (pts, cx, cy, txFn, tyFn) => {
  let bestEdge = 0, bestT = 0, bestDist = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const ax = txFn(pts[i][0]), ay = tyFn(pts[i][1]);
    const bx = txFn(pts[j][0]), by = tyFn(pts[j][1]);
    const dx = bx - ax, dy = by - ay, len2 = dx*dx + dy*dy;
    if (len2 < 0.001) continue;
    let t = ((cx-ax)*dx + (cy-ay)*dy) / len2;
    t = Math.max(0.01, Math.min(0.99, t));
    const nx = ax+t*dx, ny = ay+t*dy;
    const d = Math.hypot(cx-nx, cy-ny);
    if (d < bestDist) { bestDist = d; bestEdge = i; bestT = +t.toFixed(4); }
  }
  const [ax,ay] = pts[bestEdge], [bx,by] = pts[(bestEdge+1)%pts.length];
  const snapX = txFn(ax + (bx-ax)*bestT);
  const snapY = tyFn(ay + (by-ay)*bestT);
  return { edgeIdx: bestEdge, t: bestT, snapX, snapY };
};

// ── PatternSvgInline ────────────────────────────────────────────────────────
// Rendered inside a <svg> element with viewBox set by PatternEditor.
// SCALE and OX/OY must match what PatternEditor uses for the same pattern.
const PatternSvgInline = ({ pattern, showSA, measurePts, measureResult, SCALE, OX, OY }) => {
  const SC  = SCALE || 3.5;
  const { pts, ptsMeta, W, H, guides = [] } = buildDisplayPoly(pattern);
  const tx = x => (OX||0) + x * SC;
  const ty = y => (OY||0) + (H + 10) * SC - y * SC;
  const outlineD = buildPathD(pts, ptsMeta, tx, ty);
  const fSz = Math.max(6, Math.min(11, SC * 1.1));

  // SA: proper miter-join polygon offset (ShapeBuilder's offsetPolygon) —
  // handles concave corners (e.g. the crotch curve notch) correctly, unlike
  // a naive per-vertex bisector average which can spike or self-intersect
  // at sharp concave points.
  const saVal = Math.max(...Object.values(pattern.seamAllowances || {top:1,right:1,bottom:1,left:1}));
  const saOffsetPts = pts.length >= 3 ? offsetPolygon(dedupeLoop(pts), saVal) : pts;
  const saStr = saOffsetPts.map(p => `${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ');


  // Grain line: use the real imported grain line if this pattern has one
  // (e.g. from DXF import, confirmed against the source file), transformed
  // into the same centred display frame tracedToDisplay uses for the
  // outline. Falls back to the previous hardcoded vertical line for
  // patterns with no stored grain data (manually drawn / measurement-built) —
  // so existing patterns render exactly as before.
  let grainX1, grainY1, grainX2, grainY2;
  if (pattern.grainLine && pattern.grainLine.length === 2 && pattern.grainLine[0].fx !== undefined) {
    // Fractional format (current): grain position is a % of the outline's
    // own bounding box, recomputed against the CURRENT W/H — so it tracks
    // correctly if the pattern is later reshaped, instead of staying frozen.
    const [g1, g2] = pattern.grainLine;
    grainX1 = tx(-W + g1.fx * (2 * W)); grainY1 = ty(g1.fy * H);
    grainX2 = tx(-W + g2.fx * (2 * W)); grainY2 = ty(g2.fy * H);
  } else if (pattern.grainLine && pattern.grainLine.length === 2 && pattern.tracedPoints && pattern.tracedPoints.length >= 3) {
    // Legacy absolute-coordinate format, for any pattern imported before this
    // fix — kept working exactly as it did, using the import-time bbox.
    const gxs = pattern.tracedPoints.map(p => p.x), gys = pattern.tracedPoints.map(p => p.y);
    const gMinX = Math.min(...gxs), gMinY = Math.min(...gys);
    const gW = (Math.max(...gxs) - gMinX) / 2;
    const [g1, g2] = pattern.grainLine;
    grainX1 = tx(g1.x - gMinX - gW); grainY1 = ty(g1.y - gMinY);
    grainX2 = tx(g2.x - gMinX - gW); grainY2 = ty(g2.y - gMinY);
  } else {
    const grainX = tx(0);
    grainX1 = grainX; grainX2 = grainX;
    grainY1 = ty(H * 0.15); grainY2 = ty(H * 0.85);
  }
  // Dimension guides (width + height arrows on the pattern)
  const dimY    = ty(-1.5);          // width guide below pattern
  const dimX    = tx(W + 1.5);       // height guide right of pattern
  // ── 1cm background grid ──────────────────────────────────────────────────
  const gridLines = [];
  const gSpan = Math.ceil(W) + 4;
  for (let gx = -gSpan; gx <= gSpan; gx++) {
    const isMaj = gx % 5 === 0;
    gridLines.push(<line key={`gx${gx}`} x1={tx(gx)} y1={ty(-2)} x2={tx(gx)} y2={ty(H+8)}
      stroke={isMaj ? '#d1d5db' : '#F4F6F8'} strokeWidth={isMaj ? 0.5 : 0.3} />);
  }
  for (let gy = -2; gy <= H + 8; gy++) {
    const isMaj = gy % 5 === 0;
    gridLines.push(<line key={`gy${gy}`} x1={tx(-gSpan)} y1={ty(gy)} x2={tx(gSpan)} y2={ty(gy)}
      stroke={isMaj ? '#d1d5db' : '#F4F6F8'} strokeWidth={isMaj ? 0.5 : 0.3} />);
  }

  return (
    <g>
      {/* Grid */}
      {gridLines}
      {/* Construction guide lines from shape generator */}
      {guides.map((g, i) => (
        <g key={`guide${i}`}>
          <line x1={tx(-W - 2)} y1={ty(g.y)} x2={tx(W + 2)} y2={ty(g.y)}
            stroke={g.color} strokeWidth={Math.max(0.5, SC * 0.12)}
            strokeDasharray={`${SC * 0.8},${SC * 0.5}`} opacity="0.7"/>
          <text x={tx(-W - 1.5)} y={ty(g.y) - 2} fontSize={fSz * 0.75}
            fill={g.color} fontWeight="bold" opacity="0.85">{g.label}</text>
        </g>
      ))}
      {/* SA outline follows actual polygon shape */}
      {showSA && <polygon points={saStr} fill="none" stroke="#C62828" strokeWidth={Math.max(0.5,SC*0.15)} strokeDasharray={`${SC*1.2},${SC*0.8}`} />}
      {/* Pattern outline — path supports smooth (bezier) nodes from the Reshape tool;
          renders identically to the old straight-line polygon when no node is smooth */}
      <path d={outlineD} fill="rgba(59,130,246,0.07)" stroke="#1A2A3A" strokeWidth={Math.max(1,SC*0.3)} strokeLinejoin="round" />
      {/* Grain line */}
      <line x1={grainX1} y1={grainY1} x2={grainX2} y2={grainY2} stroke="#2E7D32" strokeWidth={Math.max(0.6,SC*0.2)} strokeDasharray={`${SC},${SC*0.6}`} />
      <text x={grainX1 + fSz*0.5} y={(grainY1+grainY2)/2} fontSize={fSz*0.8} fill="#2E7D32">GRAIN</text>

      {/* ── Semantic anchor points — named anatomical measurement markers ── */}
      {(pattern.anchors || []).map(a => {
        const ax = tx(a.x), ay = ty(a.y);
        // Anchor: 0.25cm radius in pattern space — always a small crisp dot
        const r  = Math.max(2, SC * 0.25);
        const arm = SC * 0.45;  // crosshair arm = 0.45 cm in pattern space
        return (
          <g key={a.id}>
            {/* Crosshair arms */}
            <line x1={ax-arm} y1={ay} x2={ax+arm} y2={ay} stroke={a.color||'#EF5350'} strokeWidth={Math.max(0.6,SC*0.14)} opacity="0.85"/>
            <line x1={ax} y1={ay-arm} x2={ax} y2={ay+arm} stroke={a.color||'#EF5350'} strokeWidth={Math.max(0.6,SC*0.14)} opacity="0.85"/>
            {/* Dot */}
            <circle cx={ax} cy={ay} r={r} fill={a.color||'#EF5350'} opacity="0.95"/>
            {/* Label */}
            <rect x={ax+r*1.2} y={ay-fSz*0.6} width={fSz*0.7*a.label.length*0.55+6} height={fSz*0.9} rx="2"
              fill="rgba(255,255,255,0.88)" />
            <text x={ax+r*1.5} y={ay+fSz*0.25} fontSize={fSz*0.72} fill={a.color||'#EF5350'} fontWeight="bold">
              {a.label}
            </text>
          </g>
        );
      })}
      {/* ── Dimension guides ── */}
      {/* Width arrow */}
      <line x1={tx(-W)} y1={dimY} x2={tx(W)} y2={dimY} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={tx(-W)} y1={dimY-fSz*0.5} x2={tx(-W)} y2={dimY+fSz*0.5} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={tx( W)} y1={dimY-fSz*0.5} x2={tx( W)} y2={dimY+fSz*0.5} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <text x={tx(0)} y={dimY + fSz*1.3} textAnchor="middle" fontSize={fSz} fill="#495057" fontWeight="bold">{(W*2).toFixed(1)} cm</text>
      {/* Height arrow */}
      <line x1={dimX} y1={ty(0)} x2={dimX} y2={ty(H)} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={dimX-fSz*0.5} y1={ty(0)} x2={dimX+fSz*0.5} y2={ty(0)} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={dimX-fSz*0.5} y1={ty(H)} x2={dimX+fSz*0.5} y2={ty(H)} stroke="#6C757D" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <text x={dimX + fSz*1.4} y={(ty(0)+ty(H))/2} textAnchor="middle" fontSize={fSz} fill="#495057" fontWeight="bold"
        transform={`rotate(-90 ${dimX + fSz*1.4} ${(ty(0)+ty(H))/2})`}>{H.toFixed(1)} cm</text>

      {/* Notches — edgeIdx + t on actual polygon */}
      {(pattern.notches || []).map((n, i) => {
        const ei_ = (n.edgeIdx ?? 0) % pts.length;
        const [ax,ay] = pts[ei_], [bx,by] = pts[(ei_+1)%pts.length];
        const t = n.t ?? 0.5;
        const npx = tx(ax+(bx-ax)*t), npy = ty(ay+(by-ay)*t);
        return (
          <g key={n.id||i}>
            {/* Industry-standard chevron notch pointing outward from the seam edge */}
            {(() => {
              const ei_ = (n.edgeIdx ?? 0) % pts.length;
              const [ax_,ay_] = pts[ei_], [bx_,by_] = pts[(ei_+1)%pts.length];
              // Edge tangent in cm space
              const eDx = bx_-ax_, eDy = by_-ay_;
              const eLen = Math.hypot(eDx,eDy)||1;
              // Left normal (CCW y-up polygon): (-eDy, eDx)/len
              const lnx = -eDy/eLen, lny = eDx/eLen;
              // Outward = points away from pattern centroid (~0, H/2)
              const mx = ax_+(bx_-ax_)*(n.t??0.5), my = ay_+(by_-ay_)*(n.t??0.5);
              const dot = lnx*(0-mx) + lny*(H/2-my);
              const onx = dot>0 ? -lnx : lnx;
              const ony = dot>0 ? -lny : lny;
              // Convert outward normal to SVG space (y flipped: ony → -ony in SVG)
              const snx = onx*SC, sny = -ony*SC;
              const snLen = Math.hypot(snx,sny)||1;
              const depth = SC*0.55, arm = SC*0.3;
              // Tip of chevron (outward)
              const tx_ = npx + snx/snLen*depth, ty_ = npy + sny/snLen*depth;
              // Arms (perpendicular to normal)
              const px_ = -sny/snLen, py_ = snx/snLen;
              const sw = Math.max(1, SC*0.18);
              if (n.type === 'mark') {
                // Free mark = diamond ◇
                return <>
                  <line x1={npx-SC*0.25} y1={npy} x2={npx} y2={npy-SC*0.25} stroke="#FF8F00" strokeWidth={sw}/>
                  <line x1={npx} y1={npy-SC*0.25} x2={npx+SC*0.25} y2={npy} stroke="#FF8F00" strokeWidth={sw}/>
                  <line x1={npx+SC*0.25} y1={npy} x2={npx} y2={npy+SC*0.25} stroke="#FF8F00" strokeWidth={sw}/>
                  <line x1={npx} y1={npy+SC*0.25} x2={npx-SC*0.25} y2={npy} stroke="#FF8F00" strokeWidth={sw}/>
                </>;
              }
              return <>
                <line x1={npx+px_*arm} y1={npy+py_*arm} x2={tx_} y2={ty_} stroke="#FF8F00" strokeWidth={sw*1.4} strokeLinecap="round"/>
                <line x1={tx_} y1={ty_} x2={npx-px_*arm} y2={npy-py_*arm} stroke="#FF8F00" strokeWidth={sw*1.4} strokeLinecap="round"/>
                {/* Base line at seam edge */}
                <line x1={npx+px_*arm} y1={npy+py_*arm} x2={npx-px_*arm} y2={npy-py_*arm} stroke="#FF8F00" strokeWidth={sw*0.7} strokeLinecap="round"/>
              </>;
            })()}
          </g>
        );
      })}

      {/* Measurement lines — stored in cm space, converted via tx/ty */}
      {(pattern.measurementPoints || []).map(mp => {
        const ax=tx(mp.x1), ay=ty(mp.y1), bx=tx(mp.x2), by=ty(mp.y2);
        const midX=(ax+bx)/2, midY=(ay+by)/2;
        return (
          <g key={mp.id}>
            <line x1={ax} y1={ay} x2={bx} y2={by} stroke="#1976D2" strokeWidth="1.5"/>
            <line x1={ax-5} y1={ay-5} x2={ax+5} y2={ay+5} stroke="#1565C0" strokeWidth="1.5"/>
            <line x1={bx-5} y1={by-5} x2={bx+5} y2={by+5} stroke="#1565C0" strokeWidth="1.5"/>
            <rect x={midX-28} y={midY-8} width={56} height={13} rx="3"
              fill="white" stroke="#1976D2" strokeWidth="0.8" opacity="0.95"/>
            <text x={midX} y={midY+2} textAnchor="middle" fontSize={fSz*0.85} fill="#0D47A1" fontWeight="bold">
              {mp.label}: {mp.dist?.toFixed(1)} cm
            </text>
          </g>
        );
      })}

      {/* Live drag measurement line */}
      {measurePts?.liveLine && (
        <g>
          <line x1={measurePts.liveLine.from.x} y1={measurePts.liveLine.from.y}
                x2={measurePts.liveLine.to.x}   y2={measurePts.liveLine.to.y}
            stroke="#0ea5e9" strokeWidth={Math.max(1,SC*0.2)} strokeDasharray={`${SC*1.2},${SC*0.7}`} opacity="0.8"/>
          <text x={(measurePts.liveLine.from.x+measurePts.liveLine.to.x)/2}
                y={(measurePts.liveLine.from.y+measurePts.liveLine.to.y)/2-fSz*0.7}
            fontSize={fSz*0.9} fill="#0ea5e9" textAnchor="middle" fontWeight="bold">
            {measurePts.liveLine.dist} cm
          </text>
        </g>
      )}
      {measurePts?.confirmed && (
        <g>
          <line x1={measurePts.confirmed.from.x} y1={measurePts.confirmed.from.y}
                x2={measurePts.confirmed.to.x}   y2={measurePts.confirmed.to.y}
            stroke="#43A047" strokeWidth={Math.max(1.2,SC*0.25)}/>
          <text x={(measurePts.confirmed.from.x+measurePts.confirmed.to.x)/2}
                y={(measurePts.confirmed.from.y+measurePts.confirmed.to.y)/2-fSz*0.7}
            fontSize={fSz*0.9} fill="#388E3C" textAnchor="middle" fontWeight="bold">
            ✓ {measurePts.confirmed.dist} cm
          </text>
        </g>
      )}
      <text x={tx(0)} y={ty(-4)} textAnchor="middle" fontSize={fSz*1.1} fill="#2C3E50" fontWeight="bold">
        {pattern.partName}
      </text>
    </g>
  );
};

// ── PatternEditor ────────────────────────────────────────────────────────────
const PatternEditor = ({ patterns, setPatterns }) => {
  const [selIdx, setSelIdx]           = useState(0);
  const [tool, setTool]               = useState('select');
  const [showSA, setShowSA]           = useState(true);
  const [zoom, setZoom]               = useState(1);         // 0.5 – 4
  const [pan, setPan]                 = useState({x:0,y:0});
  const [isPanning, setIsPanning]     = useState(false);
  const [panStart, setPanStart]       = useState({x:0,y:0});
  const [snapLine, setSnapLine]       = useState(null);
  const [measureDrag, setMeasureDrag] = useState(null);  // {from:{x,y}} while dragging
  const [liveMeasure, setLiveMeasure] = useState(null);  // {from,to,dist} live preview
  const [measureResult, setMeasureResult] = useState(null); // completed, pending save
  const [pomPending, setPomPending] = useState(null);       // first anchor clicked, waiting on the second
  const [pomPickerAnchors, setPomPickerAnchors] = useState(null); // both anchors clicked — show the name/type picker
  const [pomPickerCode, setPomPickerCode] = useState('');
  const [pomPickerCustomName, setPomPickerCustomName] = useState('');
  const [pomPickerType, setPomPickerType] = useState('straight');
  const [hoverXY, setHoverXY]         = useState(null);
  const [editSpecKey, setEditSpecKey]   = useState('');
  const [editSpecVal, setEditSpecVal]   = useState('');
  const [showAnchors, setShowAnchors]   = useState(true);
  const [calcResults, setCalcResults]   = useState(null); // auto-calculated measurements
  // Undo history — each entry is a snapshot of patterns[]
  const [history, setHistory]           = useState([]);
  // ── Reshape tool: node/curve editor state ──────────────────────────────
  const [reshapeSel, setReshapeSel]     = useState(null);  // selected node index
  const [dragNode, setDragNode]         = useState(null);  // {idx, part:'point'|'hIn'|'hOut'}
  const [proportional, setProportional] = useState(false);
  const [propRadius, setPropRadius]     = useState(2);      // node-index falloff radius
  const [tplVersion, setTplVersion]     = useState(0);      // bumps when saved templates load
  const svgRef = useRef(null);
  const pat = patterns[selIdx];

  // Load any "Save as Standard" shape templates once, so pieces without their
  // own tracedPoints can use a hand-corrected shape instead of the generic one.
  useEffect(() => { loadSavedShapeTemplates().then(() => setTplVersion(v => v + 1)); }, []);

  // Push current patterns to undo stack
  const pushHistory = useCallback(() => {
    setHistory(h => [...h.slice(-29), patterns.map(p => ({...p}))]);
  }, [patterns]);

  // Cmd+Z / Ctrl+Z undo handler
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        setHistory(h => {
          if (!h.length) return h;
          const prev = h[h.length - 1];
          setPatterns(prev);
          return h.slice(0, -1);
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setPatterns]);

  // ── Display geometry ────────────────────────────────────────────────────
  const { pts, ptsMeta, W, H, hasTp, fromSavedTemplate } = useMemo(
    () => pat ? buildDisplayPoly(pat) : { pts:[], ptsMeta:null, W:20, H:60, hasTp:false, fromSavedTemplate:false },
    [JSON.stringify(pat?.measurements), JSON.stringify(pat?.tracedPoints), tplVersion]
  );

  // BASE_SC: scale that fits the pattern in 80% of an 800×500 canvas at zoom=1
  const BASE_SC = useMemo(() => {
    if (!W || !H) return 3.5;
    const fitX = (800 * 0.80) / (W * 2);
    const fitY = (500 * 0.80) / H;
    return Math.max(1, Math.min(fitX, fitY, 12));
  }, [W, H]);

  const SC   = BASE_SC * zoom;
  const PAD  = 50;  // canvas padding in SVG user units
  const OX   = PAD + W * SC + pan.x;
  const OY   = PAD + pan.y;
  const txFn = x => OX + x * SC;
  const tyFn = y => OY + (H + 5) * SC - y * SC;

  // ViewBox sized to actual content — no min constraint
  // This ensures text scales proportionally with the pattern
  const VW = W * 2 * SC + PAD * 3;
  const VH = (H + 10) * SC + PAD * 2;

  // Auto-fit when pattern changes
  useEffect(() => { setZoom(1); setPan({x:0,y:0}); setReshapeSel(null); setDragNode(null); }, [selIdx]);

  // ── Reshape tool helpers ────────────────────────────────────────────────
  // Turn whatever is currently displayed (generated shape or saved template)
  // into a real, per-node-editable tracedPoints array on this pattern.
  const ensureTraced = () => {
    if (hasTp) return pat.tracedPoints;
    const traced = displayPtsToTraced(pts, W);
    setPatterns(prev => prev.map((p,i) => i===selIdx ? {...p, tracedPoints: traced, updatedOn:new Date().toISOString()} : p));
    return traced;
  };

  // NOTE: PatternSvgInline draws with ty = OY + (H+10)*SC − y*SC (see its own
  // tx/ty), which differs from this component's txFn/tyFn (H+5) used by the
  // notch/measure tools. To keep reshape nodes sitting exactly on the drawn
  // outline we mirror PatternSvgInline's transform here rather than reuse
  // txFn/tyFn.
  const txR = x => OX + x * SC;
  const tyR = y => OY + (H + 10) * SC - y * SC;
  const toCmR = (sx, sy) => ({ x: (sx - OX) / SC, y: (OY + (H + 10) * SC - sy) / SC });

  const nodeScreenPos = (i) => [txR(pts[i][0]), tyR(pts[i][1])];
  const handleScreenPos = (i, side) => {
    const m = ptsMeta?.[i]; const h = side === 'hOut' ? m?.hOut : m?.hIn;
    if (!m?.smooth || !h) return null;
    return [txR(pts[i][0] + h[0]), tyR(pts[i][1] + h[1])];
  };
  const HIT_R = 9; // px

  const hitTestReshape = (sx, sy) => {
    // Handles first (smaller targets, sit near their node)
    for (let i = 0; i < pts.length; i++) {
      for (const side of ['hOut','hIn']) {
        const hp = handleScreenPos(i, side);
        if (hp && Math.hypot(hp[0]-sx, hp[1]-sy) <= HIT_R) return { idx:i, part:side };
      }
    }
    for (let i = 0; i < pts.length; i++) {
      const [nx,ny] = nodeScreenPos(i);
      if (Math.hypot(nx-sx, ny-sy) <= HIT_R) return { idx:i, part:'point' };
    }
    return null;
  };

  const getSvgPt = (e) => {
    const svg = svgRef.current; if (!svg) return {x:0,y:0};
    // getScreenCTM() is the correct W3C method — handles CSS transforms,
    // aspect-ratio differences, devicePixelRatio, and browser zoom correctly.
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return {x:0,y:0};
    const sp = pt.matrixTransform(ctm.inverse());
    return { x: +sp.x.toFixed(2), y: +sp.y.toFixed(2) };
  };

  // Convert SVG-canvas coordinates to pattern cm space
  const toCm = (sx, sy) => ({
    x: +((sx - OX) / SC).toFixed(3),
    y: +((OY + (H + 5) * SC - sy) / SC).toFixed(3),
  });

  const onWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.max(0.4, Math.min(4, z + (e.deltaY < 0 ? 0.15 : -0.15))));
  };

  const onMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && tool === 'select')) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.preventDefault();
      return;
    }
    if (e.button === 0 && tool === 'measure') {
      const pt = getSvgPt(e);
      setMeasureDrag({ from: pt });
      setLiveMeasure(null);
      setMeasureResult(null);
      e.preventDefault();
    }
    if (e.button === 0 && tool === 'reshape') {
      const svgPt = getSvgPt(e);
      const hit = hitTestReshape(svgPt.x, svgPt.y);
      if (hit) {
        ensureTraced(); // lock in a real, editable shape the moment you actually start dragging a node
        pushHistory();
        setReshapeSel(hit.idx);
        setDragNode({ ...hit, lastCm: toCmR(svgPt.x, svgPt.y) });
      } else {
        setReshapeSel(null);
      }
      e.preventDefault();
    }
    if (e.button === 0 && tool === 'dart') {
      const svgPt = getSvgPt(e);
      ensureTraced();
      const traced = pat.tracedPoints || displayPtsToTraced(pts, W);
      const edge = nearestEdge(pts, svgPt.x, svgPt.y, txR, tyR);
      const a = traced[edge.edgeIdx], b = traced[(edge.edgeIdx+1) % traced.length];
      const mouth = { x: +(a.x+(b.x-a.x)*edge.t).toFixed(2), y: +(a.y+(b.y-a.y)*edge.t).toFixed(2) };
      const edgeLen = Math.hypot(b.x-a.x, b.y-a.y) || 1;
      const half = Math.min(edgeLen*0.06, 1.2);
      const dirx = (b.x-a.x)/edgeLen, diry = (b.y-a.y)/edgeLen;
      const mouthStart = { x:+(mouth.x-dirx*half).toFixed(2), y:+(mouth.y-diry*half).toFixed(2), smooth:false, hIn:null, hOut:null };
      const mouthEnd   = { x:+(mouth.x+dirx*half).toFixed(2), y:+(mouth.y+diry*half).toFixed(2), smooth:false, hIn:null, hOut:null };
      const tip        = { x: mouth.x, y: mouth.y, smooth:false, hIn:null, hOut:null };
      pushHistory();
      setPatterns(prev => prev.map((p,pi) => {
        if (pi !== selIdx) return p;
        const tp = [...(p.tracedPoints || traced)];
        tp.splice(edge.edgeIdx+1, 0, mouthStart, tip, mouthEnd);
        return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
      }));
      setReshapeSel(edge.edgeIdx+2); // the tip — ready to drag out to set depth
      setDragNode({ idx: edge.edgeIdx+2, part:'point', lastCm: toCmR(svgPt.x, svgPt.y) });
      e.preventDefault();
    }
  };

  const onMouseMove = (e) => {
    const {x,y} = getSvgPt(e);
    setHoverXY({x,y});
    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }
    if (tool === 'notch' && pts.length >= 3) {
      setSnapLine(nearestEdge(pts, x, y, txFn, tyFn));
    } else { setSnapLine(null); }
    // Live measurement drag
    if (tool === 'measure' && measureDrag) {
      const pxDist = Math.hypot(x - measureDrag.from.x, y - measureDrag.from.y);
      const distCm = +(pxDist / SC).toFixed(2);
      setLiveMeasure({ from: measureDrag.from, to: {x,y}, dist: distCm });
    }
    // Reshape tool: drag the selected node or curve handle
    if (tool === 'reshape' && dragNode) {
      const cm = toCmR(x, y);
      const dxCm = cm.x - dragNode.lastCm.x, dyCm = cm.y - dragNode.lastCm.y;
      if (Math.abs(dxCm) < 0.001 && Math.abs(dyCm) < 0.001) return;
      setPatterns(prev => prev.map((p,pi) => {
        if (pi !== selIdx) return p;
        let tp = [...(p.tracedPoints || [])];
        const node = { ...tp[dragNode.idx] };
        if (dragNode.part === 'point') {
          node.x = +(node.x + dxCm).toFixed(2);
          node.y = +(node.y + dyCm).toFixed(2);
          tp[dragNode.idx] = node;
          if (proportional) tp = applyProportionalNudge(tp, dragNode.idx, dxCm, dyCm, propRadius);
        } else {
          // Dragging a bezier handle: move that handle only (relative offset from the node)
          const key = dragNode.part; // 'hIn' | 'hOut'
          const cur = node[key] || [0, 0];
          node[key] = [+(cur[0] + dxCm).toFixed(2), +(cur[1] + dyCm).toFixed(2)];
          node.smooth = true;
          tp[dragNode.idx] = node;
        }
        return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
      }));
      setDragNode(d => ({ ...d, lastCm: cm }));
    }
  };

  const onMouseUp = (e) => {
    setIsPanning(false);
    if (tool === 'measure' && measureDrag) {
      const pt = getSvgPt(e);
      const pxDist = Math.hypot(pt.x - measureDrag.from.x, pt.y - measureDrag.from.y);
      if (pxDist > 4) { // ignore accidental micro-drags
        const distCm = +(pxDist / SC).toFixed(2);
        setMeasureResult({ from: measureDrag.from, to: pt, dist: distCm });
      }
      setMeasureDrag(null);
      setLiveMeasure(null);
    }
    if (tool === 'reshape' && dragNode) setDragNode(null);
  };

  const handleSvgClick = (e) => {
    if (isPanning) return;
    const {x,y} = getSvgPt(e);
    if (tool === 'notch' && pts.length >= 3) {
      pushHistory();
      const snap = nearestEdge(pts, x, y, txFn, tyFn);
      const snapDist = Math.hypot(x - snap.snapX, y - snap.snapY);
      let newNotch;
      if (snapDist <= SC * 1.5) {
        // Within 1.5 cm of an edge → snap to edge, render as chevron >
        newNotch = { id:`n-${Date.now()}`, edgeIdx: snap.edgeIdx, t: snap.t, type:'slit', depth:1 };
      } else {
        // Far from edge → free interior mark (dart point, pocket, etc.)
        const cm = toCm(x, y);
        newNotch = { id:`n-${Date.now()}`, edgeIdx: 0, t: 0,
          // Store interior position as normalised fractions so it's zoom-invariant
          nx: +(cm.x / (W * 2) + 0.5).toFixed(4), ny: +(cm.y / H).toFixed(4),
          type:'mark', depth:0 };
      }
      setPatterns(prev => prev.map((p,i) =>
        i===selIdx ? {...p, notches:[...(p.notches||[]), newNotch], updatedOn:new Date().toISOString()} : p
      ));
      setSnapLine(null);
    }
    if (tool === 'pom' && pts.length >= 3) {
      // Two clicks, each snapped to the nearest edge (same proven approach
      // notches use) — so the tagged points stay attached to the actual
      // outline and track it if the pattern is later reshaped, instead of
      // freezing at whatever position they were clicked at.
      const snap = nearestEdge(pts, x, y, txFn, tyFn);
      const anchor = { edgeIdx: snap.edgeIdx, t: snap.t };
      if (!pomPending) {
        setPomPending(anchor);
      } else {
        setPomPickerAnchors([pomPending, anchor]);
        setPomPending(null);
      }
    }
    // Measure handled entirely by drag (mousedown/move/up)
  };

  // Double-click to save measurement as permanent annotation, or (in Reshape)
  // to insert a new node on the nearest edge.
  const handleDblClick = (e) => {
    if (tool === 'measure' && measureResult) {
      pushHistory();
      addMeasurementAnnotation();
      return;
    }
    if (tool === 'reshape' && pts.length >= 3) {
      const svgPt = getSvgPt(e);
      if (hitTestReshape(svgPt.x, svgPt.y)) return; // double-click landed on an existing node/handle
      const snap = nearestEdge(pts, svgPt.x, svgPt.y, txR, tyR);
      const snapDist = Math.hypot(svgPt.x - snap.snapX, svgPt.y - snap.snapY);
      if (snapDist > SC * 2) return; // too far from any edge
      pushHistory();
      const traced = ensureTraced();
      const [ax, ay] = pts[snap.edgeIdx], [bx, by] = pts[(snap.edgeIdx + 1) % pts.length];
      const newNode = {
        x: +(ax + (bx - ax) * snap.t + W).toFixed(2),
        y: +(ay + (by - ay) * snap.t).toFixed(2),
        smooth: false, hIn: null, hOut: null,
      };
      setPatterns(prev => prev.map((p,i) => {
        if (i !== selIdx) return p;
        const tp = [...(p.tracedPoints || traced)];
        tp.splice(snap.edgeIdx + 1, 0, newNode);
        // Keep gradeTags aligned with tracedPoints (new node is untagged —
        // it'll interpolate between its neighbours during grading, same as
        // any other untagged point).
        const tags = p.gradeTags?.length === tp.length - 1
          ? [...p.gradeTags.slice(0, snap.edgeIdx+1), { gradeX:null, gradeY:null }, ...p.gradeTags.slice(snap.edgeIdx+1)]
          : p.gradeTags;
        return { ...p, tracedPoints: tp, gradeTags: tags, updatedOn: new Date().toISOString() };
      }));
      setReshapeSel(snap.edgeIdx + 1);
    }
  };

  // Toggle the selected node between a sharp corner and a smooth (bezier) curve.
  // Smoothing derives symmetric handles from the two neighbouring nodes so the
  // curve starts tangent-continuous; drag the little handle squares to refine.
  const toggleNodeSmooth = () => {
    if (reshapeSel == null) return;
    pushHistory();
    const traced = ensureTraced();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const tp = [...(p.tracedPoints || traced)];
      const n = tp.length, idx = reshapeSel;
      const node = { ...tp[idx] };
      if (node.smooth) {
        node.smooth = false; node.hIn = null; node.hOut = null;
      } else {
        const prevP = tp[(idx - 1 + n) % n], nextP = tp[(idx + 1) % n];
        const dx = (nextP.x - prevP.x) * 0.18, dy = (nextP.y - prevP.y) * 0.18;
        node.smooth = true; node.hIn = [-dx, -dy]; node.hOut = [dx, dy];
      }
      tp[idx] = node;
      return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
    }));
  };

  // Assign which measurement key drives this node's X/Y during grading — see
  // gradeTracedPoints. 'FIXED' pins the point (delta 0, e.g. the hem line);
  // '' clears the tag so the point interpolates between its tagged neighbours.
  const setNodeGrade = (axis, key) => {
    if (reshapeSel == null) return;
    const traced = ensureTraced();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const tp = p.tracedPoints || traced;
      const tags = (p.gradeTags?.length === tp.length ? [...p.gradeTags] : tp.map(()=>({gradeX:null,gradeY:null})));
      tags[reshapeSel] = { ...tags[reshapeSel], [axis]: key || null };
      return { ...p, gradeTags: tags };
    }));
  };

  const deleteNode = () => {
    if (reshapeSel == null) return;
    const traced = ensureTraced();
    if ((pat.tracedPoints || traced).length <= 3) { alert('A shape needs at least 3 points.'); return; }
    pushHistory();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const base = p.tracedPoints || traced;
      const tp = base.filter((_,ni) => ni !== reshapeSel);
      const tags = p.gradeTags?.length === base.length
        ? p.gradeTags.filter((_,ni) => ni !== reshapeSel)
        : p.gradeTags;
      return { ...p, tracedPoints: tp, gradeTags: tags, updatedOn: new Date().toISOString() };
    }));
    setReshapeSel(null);
  };

  const resetToGenerated = () => {
    if (!window.confirm('Discard hand-edited shape and revert to the generated/standard shape for this piece?')) return;
    pushHistory();
    setPatterns(prev => prev.map((p,i) => i===selIdx ? {...p, tracedPoints: null, updatedOn:new Date().toISOString()} : p));
    setReshapeSel(null);
  };

  // Quick, discoverable answer to "I dragged the rope too far, how do I
  // squeeze it back" — scales the WHOLE shape along one axis by a
  // percentage in one action, instead of nudging individual points back
  // (which the elastic/rope drag has no way to do — it can only push
  // points around, not shrink the overall length).
  const [scalePct, setScalePct] = useState(100);
  const scaleShape = (axis) => {
    const factor = scalePct / 100;
    if (!isFinite(factor) || factor <= 0) return;
    const traced = ensureTraced();
    pushHistory();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const tp = p.tracedPoints || traced;
      const scaled = tp.map(pt => {
        const np = { ...pt };
        if (axis === 'height') {
          np.y = +(pt.y * factor).toFixed(2);
          if (pt.hIn)  np.hIn  = [pt.hIn[0],  +(pt.hIn[1]*factor).toFixed(2)];
          if (pt.hOut) np.hOut = [pt.hOut[0], +(pt.hOut[1]*factor).toFixed(2)];
        } else {
          np.x = +(pt.x * factor).toFixed(2);
          if (pt.hIn)  np.hIn  = [+(pt.hIn[0]*factor).toFixed(2),  pt.hIn[1]];
          if (pt.hOut) np.hOut = [+(pt.hOut[0]*factor).toFixed(2), pt.hOut[1]];
        }
        return np;
      });
      const xs = scaled.map(pp=>pp.x), ys = scaled.map(pp=>pp.y);
      const newM = { ...p.measurements };
      if (axis === 'height' && newM.height !== undefined) newM.height = +(Math.max(...ys)-Math.min(...ys)).toFixed(2);
      if (axis === 'width'  && newM.width  !== undefined) newM.width  = +(Math.max(...xs)-Math.min(...xs)).toFixed(2);
      return { ...p, tracedPoints: scaled, measurements: newM, updatedOn: new Date().toISOString() };
    }));
  };

  // Persist the current piece's tracedPoints as the new default for this
  // piece type (template), so future "Load standard pieces" / new styles
  // start from this corrected shape instead of the generic placeholder.
  const [savingStandard, setSavingStandard] = useState(false);
  const saveAsStandard = async () => {
    const traced = ensureTraced();
    const tpKey = buildDisplayPoly({ ...pat, tracedPoints: pat.tracedPoints || traced }).template;
    if (!tpKey || tpKey === 'generic') { alert('Could not determine a piece type to save this shape under.'); return; }
    if (!window.confirm(`Save this shape as the new standard "${tpKey}"? It will be used for every future piece of this type.`)) return;
    setSavingStandard(true);
    try {
      const res = await fetch(SAVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: 'shape_template',
          data: {
            id: 0,
            templateKey: tpKey,
            pieceName: pat.partName,
            tracedPoints: pat.tracedPoints || traced,
            seamAllowances: pat.seamAllowances,
            notches: pat.notches,
            savedOn: new Date().toISOString(),
          },
        }),
      });
      const j = await res.json().catch(() => null);
      if (j && j.success === false) throw new Error(j.error || 'Save failed');
      await loadSavedShapeTemplates();
      setTplVersion(v => v + 1);
      alert(`✓ Saved as the new standard shape for "${tpKey}".`);
    } catch (e) {
      alert('Could not save to the standard library: ' + e.message + '\n\n(This needs a "shape_template" category on save_universal.php / load_universal.php — check with your backend if this is the first time saving one.)');
    } finally {
      setSavingStandard(false);
    }
  };

  const addMeasurementAnnotation = () => {
    if (!measureResult) return;
    const p1 = toCm(measureResult.from.x, measureResult.from.y);
    const p2 = toCm(measureResult.to.x,   measureResult.to.y);
    const newMp = {
      id:    `mp-${Date.now()}`,
      label: `Meas ${(pat.measurementPoints||[]).length+1}`,
      x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
      dist: +Math.hypot(p2.x-p1.x, p2.y-p1.y).toFixed(2),
    };
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, measurementPoints:[...(p.measurementPoints||[]), newMp]} : p
    ));
    setMeasureResult(null);
  };

  // Real, named POM tagging: unlike addMeasurementAnnotation above (which
  // freezes a generic-labelled distance forever), this stores edge/t
  // anchors — the live value is recomputed from the pattern's CURRENT
  // shape every time it's displayed, and the name comes from the real
  // sourced POM library, not an auto-generated "Meas N" placeholder.
  const savePomTag = () => {
    if (!pomPickerAnchors) return;
    const libEntry = PANT_POM_LIBRARY.find(p => p.code === pomPickerCode);
    const name = libEntry ? libEntry.name : (pomPickerCustomName.trim() || 'Custom POM');
    const newPom = {
      id: `pom-${Date.now()}`,
      code: libEntry ? libEntry.code : null,
      name,
      measureType: pomPickerType,
      anchor1: pomPickerAnchors[0],
      anchor2: pomPickerAnchors[1],
    };
    pushHistory();
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, poms:[...(p.poms||[]), newPom], updatedOn:new Date().toISOString()} : p
    ));
    setPomPickerAnchors(null);
    setPomPickerCode('');
    setPomPickerCustomName('');
    setPomPickerType('straight');
  };

  const updateSA = (side, val) => {
    pushHistory();
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, seamAllowances:{...p.seamAllowances,[side]:parseFloat(val)||0}, updatedOn:new Date().toISOString()} : p
    ));
  };

  const addSpec = () => {
    if (!editSpecKey.trim()) return;
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, measurements:{...p.measurements,[editSpecKey.trim()]:parseFloat(editSpecVal)||0}, updatedOn:new Date().toISOString()} : p
    ));
    setEditSpecKey(''); setEditSpecVal('');
  };

  // For a traced/hand-edited piece, changing "width" or "height" here now
  // actually rescales the real shape to hit that exact number — this is the
  // direct fix for "I stretched it too far with the rope tool, how do I
  // squeeze it back down": type the target length instead of fighting
  // individual points back into place one at a time. Any other measurement
  // key just updates the stored number (used for grading) without touching
  // geometry, same as before.
  const rescaleTracedShape = (tracedPoints, axis, factor) => {
    if (!tracedPoints?.length || !isFinite(factor) || factor <= 0) return tracedPoints;
    return tracedPoints.map(p => {
      const np = { ...p };
      if (axis === 'width') {
        np.x = +(p.x * factor).toFixed(2);
        if (p.hIn)  np.hIn  = [+(p.hIn[0]*factor).toFixed(2),  p.hIn[1]];
        if (p.hOut) np.hOut = [+(p.hOut[0]*factor).toFixed(2), p.hOut[1]];
      } else {
        np.y = +(p.y * factor).toFixed(2);
        if (p.hIn)  np.hIn  = [p.hIn[0],  +(p.hIn[1]*factor).toFixed(2)];
        if (p.hOut) np.hOut = [p.hOut[0], +(p.hOut[1]*factor).toFixed(2)];
      }
      return np;
    });
  };

  const updateSpec = (key, val) => setPatterns(prev => prev.map((p,i) => {
    if (i !== selIdx) return p;
    const newVal = parseFloat(val) || 0;
    const updated = { ...p, measurements: { ...p.measurements, [key]: newVal }, updatedOn: new Date().toISOString() };
    const axis = key === 'width' ? 'width' : key === 'height' ? 'height' : null;
    if (axis && p.tracedPoints?.length && newVal > 0) {
      const current = axis === 'width'
        ? Math.max(...p.tracedPoints.map(pt=>pt.x)) - Math.min(...p.tracedPoints.map(pt=>pt.x))
        : Math.max(...p.tracedPoints.map(pt=>pt.y)) - Math.min(...p.tracedPoints.map(pt=>pt.y));
      if (current > 0) updated.tracedPoints = rescaleTracedShape(p.tracedPoints, axis, newVal / current);
    }
    return updated;
  }));

  const scaleLabel = `1 cm = ${SC.toFixed(1)}px  |  ~1:${Math.round(28.35/SC)} scale`;

  if (!pat) return <div style={{padding:16,color:'#ADB5BD'}}>No patterns loaded.</div>;

  return (
    <div style={{padding:16,display:'flex',gap:12,height:'100%',overflow:'hidden'}}>

      {/* Left: part list */}
      <div style={{width:140,flexShrink:0,overflowY:'auto'}}>
        <div style={{fontSize:10,fontWeight:'bold',color:'#6C757D',textTransform:'uppercase',marginBottom:6}}>Parts</div>
        {patterns.map((p,i)=>(
          <button key={p.id} onClick={()=>{setSelIdx(i);setMeasureResult(null);setSnapLine(null);setReshapeSel(null);setDragNode(null);}}
            style={{width:'100%',textAlign:'left',padding:'6px 8px',marginBottom:4,borderRadius:6,
              border:i===selIdx?'1px solid #6A1B9A':'1px solid #DEE2E6',
              background:i===selIdx?'#EDE7F6':'white',cursor:'pointer',fontSize:11,fontWeight:i===selIdx?'bold':'normal'}}>
            {p.partName}
            {p.tracedPoints?.length>=3 && <span style={{marginLeft:4,fontSize:8,color:'#2E7D32'}}>●</span>}
          </button>
        ))}
      </div>

      {/* Centre: SVG canvas */}
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:6,minWidth:0}}>

        {/* Toolbar */}
        <div style={{display:'flex',gap:5,background:'#1A2A3A',padding:'5px 10px',borderRadius:8,flexWrap:'wrap',alignItems:'center'}}>
          {[
            {id:'select',  label:'↖ Pan',    color:'#6366f1'},
            {id:'notch',   label:'✂ Notch',  color:'#FF8F00'},
            {id:'measure', label:'📏 Measure',color:'#0ea5e9'},
            {id:'pom',     label:'📋 POM',    color:'#2E7D32'},
            {id:'reshape', label:'✎ Reshape',color:'#a855f7'},
            {id:'dart',    label:'📐 Dart',   color:'#be123c'},
          ].map(t=>(
            <button key={t.id} onClick={()=>{setTool(t.id);setMeasureResult(null);setSnapLine(null);setReshapeSel(null);setDragNode(null);}}
              style={{padding:'4px 9px',fontSize:10,fontWeight:tool===t.id?'bold':'normal',
                background:tool===t.id?t.color:'rgba(255,255,255,0.08)',color:tool===t.id?'white':'#ADB5BD',
                border:`1px solid ${tool===t.id?t.color:'rgba(255,255,255,0.1)'}`,borderRadius:5,cursor:'pointer'}}>
              {t.label}
            </button>
          ))}
          <div style={{marginLeft:4,display:'flex',gap:4}}>
            <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>+</button>
            <span style={{fontSize:9,color:'#ADB5BD',alignSelf:'center'}}>{Math.round(zoom*100)}%</span>
            <button onClick={()=>setZoom(z=>Math.max(0.4,z-0.25))}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>−</button>
            <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:9}}>⊡ Reset</button>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#ADB5BD',marginLeft:4}}>
            <input type="checkbox" checked={showSA} onChange={e=>setShowSA(e.target.checked)}/> SA
          </label>
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#FF8F00',marginLeft:2}}>
            <input type="checkbox" checked={showAnchors} onChange={e=>setShowAnchors(e.target.checked)}/> ⚓ Anchors
          </label>
          {/* Auto-calculate standard measurements from anchor positions */}
          <button onClick={() => {
            const poly = buildDisplayPoly(pat);
            const anchors = poly.anchors || [];
            const tpl = poly.template;
            const results = calcMeasurementsFromAnchors(anchors, tpl);
            if (Object.keys(results).length === 0) { alert('No measurement definitions available for this piece type.'); return; }
            setCalcResults(results);
            // Offer to push into spec builder
            if (window.confirm(`Calculated ${Object.keys(results).length} measurements from anchor points. Apply to Spec Builder?`)) {
              pushHistory();
              setPatterns(prev => prev.map((p,i) => {
                if (i !== selIdx) return p;
                const merged = { ...p.measurements };
                Object.entries(results).forEach(([k,v]) => {
                  // Only update if key looks like a spec key (no spaces)
                  if (!/\s/.test(k)) merged[k] = v;
                });
                return { ...p, measurements: merged, updatedOn: new Date().toISOString() };
              }));
            }
          }}
            style={{padding:'3px 9px',background:'rgba(245,158,11,0.15)',color:'#FF8F00',border:'1px solid rgba(245,158,11,0.3)',borderRadius:4,cursor:'pointer',fontSize:9,fontWeight:'bold',marginLeft:2}}>
            📐 Calc Measurements
          </button>
          {hoverXY && <span style={{marginLeft:'auto',fontSize:9,color:'#6C757D',fontFamily:'monospace'}}>
            x:{hoverXY.x.toFixed(0)} y:{hoverXY.y.toFixed(0)}
          </span>}
          {/* Undo button */}
          <button onClick={()=>{ setHistory(h=>{ if(!h.length) return h; setPatterns(h[h.length-1]); return h.slice(0,-1); }); }}
            title="Undo (Cmd+Z / Ctrl+Z)" disabled={history.length===0}
            style={{padding:'3px 7px',background:history.length?'rgba(239,68,68,0.15)':'rgba(255,255,255,0.04)',color:history.length?'#EF9A9A':'#495057',border:'none',borderRadius:4,cursor:history.length?'pointer':'not-allowed',fontSize:9,marginLeft:4}}>
            ↩ Undo ({history.length})
          </button>
          {tool==='notch' && <span style={{fontSize:9,color:'#FFB300',marginLeft:4}}>Click near edge → ▶ chevron notch · Click inside pattern → ◇ reference mark</span>}
          {tool==='measure' && !measureDrag && !measureResult && <span style={{fontSize:9,color:'#38bdf8',marginLeft:4}}>Drag to measure · double-click to save annotation</span>}
          {tool==='measure' && measureDrag  && <span style={{fontSize:9,color:'#38bdf8',marginLeft:4}}>↔ {liveMeasure?.dist ?? '…'} cm — release to confirm</span>}
          {tool==='measure' && measureResult && !measureDrag && <span style={{fontSize:9,color:'#43A047',marginLeft:4}}>✓ {measureResult.dist} cm — double-click canvas to save · drag again for new</span>}
          {tool==='reshape' && <span style={{fontSize:9,color:'#c084fc',marginLeft:4}}>Drag a node to move it · double-click an edge to add a node · select a node for more options →</span>}
          {tool==='pom' && !pomPending && !pomPickerAnchors && <span style={{fontSize:9,color:'#2E7D32',marginLeft:4}}>Click the first point on the outline</span>}
          {tool==='pom' && pomPending && <span style={{fontSize:9,color:'#2E7D32',marginLeft:4}}>Now click the second point</span>}
        </div>

        {/* POM name/type picker — appears once both anchor points are clicked */}
        {pomPickerAnchors && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10050}}>
            <div style={{background:'white',borderRadius:10,padding:18,width:380}}>
              <div style={{fontWeight:'bold',fontSize:13,marginBottom:10,color:'#1A2A3A'}}>What measurement is this?</div>
              <label style={{fontSize:10,color:'#6C757D',display:'block',marginBottom:3}}>From the pant POM library</label>
              <select value={pomPickerCode} onChange={e=>{
                const code = e.target.value; setPomPickerCode(code);
                const entry = PANT_POM_LIBRARY.find(p=>p.code===code);
                if (entry && entry.measureType !== 'unspecified') setPomPickerType(entry.measureType);
              }} style={{width:'100%',padding:'6px 8px',border:'1px solid #DEE2E6',borderRadius:6,fontSize:11,marginBottom:10}}>
                <option value="">— not in library / custom —</option>
                {PANT_POM_LIBRARY.map(p=><option key={p.code} value={p.code}>{p.code} — {p.name}</option>)}
              </select>
              {!pomPickerCode && (
                <>
                  <label style={{fontSize:10,color:'#6C757D',display:'block',marginBottom:3}}>Custom name</label>
                  <input value={pomPickerCustomName} onChange={e=>setPomPickerCustomName(e.target.value)}
                    placeholder="e.g. Pocket Bag Depth" style={{width:'100%',padding:'6px 8px',border:'1px solid #DEE2E6',borderRadius:6,fontSize:11,marginBottom:10,boxSizing:'border-box'}}/>
                </>
              )}
              <label style={{fontSize:10,color:'#6C757D',display:'block',marginBottom:3}}>How is this measured?</label>
              <div style={{display:'flex',gap:8,marginBottom:14}}>
                <button onClick={()=>setPomPickerType('straight')} style={{flex:1,padding:'6px',borderRadius:6,border:pomPickerType==='straight'?'2px solid #2E7D32':'1px solid #DEE2E6',background:pomPickerType==='straight'?'#f0fdf4':'white',cursor:'pointer',fontSize:10}}>Straight line</button>
                <button onClick={()=>setPomPickerType('curve')} style={{flex:1,padding:'6px',borderRadius:6,border:pomPickerType==='curve'?'2px solid #2E7D32':'1px solid #DEE2E6',background:pomPickerType==='curve'?'#f0fdf4':'white',cursor:'pointer',fontSize:10}}>Along the curve</button>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button onClick={()=>{setPomPickerAnchors(null);setPomPickerCode('');setPomPickerCustomName('');}}
                  style={{padding:'7px 14px',background:'white',border:'1px solid #DEE2E6',borderRadius:6,cursor:'pointer',fontSize:11}}>Cancel</button>
                <button onClick={savePomTag} disabled={!pomPickerCode && !pomPickerCustomName.trim()}
                  style={{padding:'7px 14px',background:(!pomPickerCode && !pomPickerCustomName.trim())?'#DEE2E6':'#2E7D32',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:'bold'}}>Save POM</button>
              </div>
            </div>
          </div>
        )}

        {/* Reshape sub-toolbar — only shown while the Reshape tool is active */}
        {tool==='reshape' && (
          <div style={{display:'flex',gap:8,background:'#faf5ff',border:'1px solid #e9d5ff',padding:'6px 10px',borderRadius:8,flexWrap:'wrap',alignItems:'center'}}>
            <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#6A1B9A',fontWeight:'bold'}}>
              <input type="checkbox" checked={proportional} onChange={e=>setProportional(e.target.checked)}/> Proportional drag
            </label>
            {proportional && (
              <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#6A1B9A'}}>
                Falloff
                <input type="number" min={1} max={8} value={propRadius}
                  onChange={e=>setPropRadius(Math.max(1,Math.min(8,+e.target.value||1)))}
                  style={{width:36,padding:'2px 4px',border:'1px solid #d8b4fe',borderRadius:4,fontSize:9}}/>
                nodes
              </label>
            )}
            <div style={{width:1,alignSelf:'stretch',background:'#e9d5ff'}}/>
            <button onClick={toggleNodeSmooth} disabled={reshapeSel==null}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:reshapeSel==null?'not-allowed':'pointer',
                background:reshapeSel==null?'#f3f4f6':'#E1BEE7',color:reshapeSel==null?'#9E9E9E':'#6A1B9A',border:'1px solid #ddd6fe'}}>
              {reshapeSel!=null && pat?.tracedPoints?.[reshapeSel]?.smooth ? '◇ Make Corner' : '◠ Make Smooth'}
            </button>
            <button onClick={deleteNode} disabled={reshapeSel==null}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:reshapeSel==null?'not-allowed':'pointer',
                background:reshapeSel==null?'#f3f4f6':'#FFCDD2',color:reshapeSel==null?'#9E9E9E':'#C62828',border:'1px solid #fecaca'}}>
              ✕ Delete Node
            </button>
            <div style={{width:1,alignSelf:'stretch',background:'#e9d5ff'}}/>
            <label style={{display:'flex',alignItems:'center',gap:3,fontSize:9,color:'#6A1B9A'}}>
              Scale
              <input type="number" min={10} max={300} value={scalePct} onChange={e=>setScalePct(+e.target.value||100)}
                style={{width:44,padding:'2px 4px',border:'1px solid #d8b4fe',borderRadius:4,fontSize:9}}/>
              %
            </label>
            <button onClick={()=>scaleShape('height')} title="Squeeze/stretch the whole shape's LENGTH to this percentage — fixes a rope-dragged section that's now the wrong overall length"
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'#E1BEE7',color:'#6A1B9A',border:'1px solid #ddd6fe'}}>
              ↕ Apply to Height
            </button>
            <button onClick={()=>scaleShape('width')} title="Squeeze/stretch the whole shape's WIDTH to this percentage"
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'#E1BEE7',color:'#6A1B9A',border:'1px solid #ddd6fe'}}>
              ↔ Apply to Width
            </button>
            <div style={{width:1,alignSelf:'stretch',background:'#e9d5ff'}}/>
            <button onClick={saveAsStandard} disabled={savingStandard}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'#6A1B9A',color:'white',border:'none'}}>
              {savingStandard ? '⟳ Saving…' : '💾 Save as Standard'}
            </button>
            <button onClick={resetToGenerated}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'rgba(100,116,139,0.1)',color:'#6C757D',border:'1px solid #CED4DA'}}>
              ↺ Reset Shape
            </button>
            {reshapeSel!=null && <span style={{fontSize:9,color:'#6A1B9A',marginLeft:'auto'}}>Node {reshapeSel+1} of {pts.length} selected</span>}
          </div>
        )}

        {/* Per-node grading assignment — lets ANY traced/hand-drawn piece grade
            correctly by size, not just the built-in generators. */}
        {tool==='reshape' && reshapeSel!=null && (
          <div style={{display:'flex',gap:8,background:'#f0fdf4',border:'1px solid #A5D6A7',padding:'6px 10px',borderRadius:8,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:9,fontWeight:'bold',color:'#166534'}}>Grading — this node moves with:</span>
            {['gradeX','gradeY'].map(axis => (
              <label key={axis} style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#166534'}}>
                {axis==='gradeX'?'X:':'Y:'}
                <select
                  value={pat?.gradeTags?.[reshapeSel]?.[axis] || ''}
                  onChange={e=>setNodeGrade(axis, e.target.value)}
                  style={{padding:'2px 5px',fontSize:9,border:'1px solid #A5D6A7',borderRadius:4,background:'white'}}>
                  <option value="">— interpolate —</option>
                  <option value="FIXED">Fixed (0)</option>
                  {Object.keys(pat?.measurements||{}).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
            ))}
            <span style={{fontSize:8.5,color:'#6C757D'}}>Untagged nodes interpolate between their nearest tagged neighbours automatically.</span>
          </div>
        )}

        {/* Scale indicator */}
        <div style={{background:'#f0fdf4',border:'1px solid #A5D6A7',borderRadius:5,padding:'3px 10px',fontSize:9,color:'#166534',display:'flex',gap:12,alignItems:'center'}}>
          <span>📐 <strong>Scale:</strong> {scaleLabel}</span>
          <span>{hasTp ? '✓ Using traced/hand-edited shape' : fromSavedTemplate ? '★ Using saved standard shape' : '⚠ Using generic shape (no tracedPoints)'}</span>
          <span>Pattern: {(W*2).toFixed(1)} × {H.toFixed(1)} cm</span>
        </div>

        {/* SVG canvas */}
        <div style={{flex:1,minHeight:0,border:'1px solid #2C3E50',borderRadius:8,overflow:'hidden',background:'#F8F9FA',cursor:tool==='select'?'grab':tool==='notch'?'crosshair':'cell'}}
          onWheel={onWheel}>
          <svg ref={svgRef} width="100%" height="100%"
            viewBox={`0 0 ${VW.toFixed(0)} ${VH.toFixed(0)}`}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onClick={handleSvgClick} onDoubleClick={handleDblClick}>

            {/* Grid */}
            {Array.from({length:Math.ceil(VW/SC/10)+1},(_,i)=>(
              <line key={`gx${i}`} x1={txFn(-W+i*10)} y1={0} x2={txFn(-W+i*10)} y2={VH}
                stroke="#DEE2E6" strokeWidth="0.4" strokeDasharray="2,4"/>
            ))}

            {/* Pattern */}
            <PatternSvgInline
              pattern={{ ...pat, anchors: showAnchors ? (buildDisplayPoly(pat).anchors || []) : [] }}
              showSA={showSA}
              measurePts={{ liveLine: liveMeasure, confirmed: measureResult }}
              measureResult={null}
              SCALE={SC} OX={OX} OY={OY} />

            {/* Snap preview dot on edge */}
            {snapLine && (
              <g>
                <circle cx={snapLine.snapX} cy={snapLine.snapY} r={Math.max(3,SC*0.5)}
                  fill="none" stroke="#FF8F00" strokeWidth={Math.max(1,SC*0.18)} strokeDasharray={`${SC*0.6},${SC*0.4}`} opacity="0.9"/>
                <circle cx={snapLine.snapX} cy={snapLine.snapY} r={Math.max(1.5,SC*0.18)} fill="#FF8F00"/>
                <line x1={snapLine.snapX-SC*0.7} y1={snapLine.snapY} x2={snapLine.snapX+SC*0.7} y2={snapLine.snapY} stroke="#FF8F00" strokeWidth={Math.max(0.6,SC*0.12)} opacity="0.7"/>
                <line x1={snapLine.snapX} y1={snapLine.snapY-SC*0.7} x2={snapLine.snapX} y2={snapLine.snapY+SC*0.7} stroke="#FF8F00" strokeWidth={Math.max(0.6,SC*0.12)} opacity="0.7"/>
              </g>
            )}

            {/* Reshape tool: draggable outline nodes + bezier handles */}
            {tool==='reshape' && pts.map((_, i) => {
              const [nx, ny] = nodeScreenPos(i);
              const meta = ptsMeta?.[i];
              const isSel = reshapeSel === i;
              const r = Math.max(3.5, Math.min(6, HIT_R * 0.6));
              return (
                <g key={`node${i}`}>
                  {/* Handle stems + squares for a smooth node */}
                  {meta?.smooth && ['hOut','hIn'].map(side => {
                    const hp = handleScreenPos(i, side);
                    if (!hp) return null;
                    return (
                      <g key={side}>
                        <line x1={nx} y1={ny} x2={hp[0]} y2={hp[1]} stroke="#a855f7" strokeWidth="1" strokeDasharray="2,2" opacity="0.7"/>
                        <rect x={hp[0]-3.5} y={hp[1]-3.5} width="7" height="7" fill="white" stroke="#a855f7" strokeWidth="1.3"
                          style={{cursor:'pointer'}}/>
                      </g>
                    );
                  })}
                  <circle cx={nx} cy={ny} r={r}
                    fill={isSel ? '#a855f7' : (meta?.smooth ? '#c4b5fd' : 'white')}
                    stroke="#6A1B9A" strokeWidth={isSel ? 2 : 1.3}
                    style={{cursor:'move'}}/>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Calculated measurements panel */}
        {calcResults && (
          <div style={{background:'#FFF8E1',border:'1px solid #fde68a',borderRadius:6,padding:'7px 12px',fontSize:10,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontWeight:'bold',color:'#92400e'}}>📐 Calculated:</span>
            {Object.entries(calcResults).filter(([k])=>/\s/.test(k)).map(([k,v])=>(
              <span key={k} style={{color:'#78350f'}}>
                <strong>{k}</strong>: {v} cm
              </span>
            ))}
            <button onClick={()=>setCalcResults(null)}
              style={{marginLeft:'auto',padding:'2px 8px',background:'#FFE082',border:'none',borderRadius:3,cursor:'pointer',fontSize:9}}>✕</button>
          </div>
        )}

        {/* Measurement save bar */}
        {measureResult && (
          <div style={{background:'#eff6ff',border:'1px solid #90CAF9',borderRadius:6,padding:'6px 10px',fontSize:11,display:'flex',gap:10,alignItems:'center'}}>
            📏 <strong>{measureResult.dist} cm</strong>
            <button onClick={addMeasurementAnnotation}
              style={{padding:'3px 10px',background:'#1976D2',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:10}}>
              Save annotation
            </button>
            <button onClick={()=>setMeasureResult(null)}
              style={{padding:'3px 8px',background:'#F4F6F8',border:'none',borderRadius:4,cursor:'pointer',fontSize:10}}>Discard</button>
          </div>
        )}
      </div>

      {/* Right: properties */}
      <div style={{width:210,flexShrink:0,overflowY:'auto',display:'flex',flexDirection:'column',gap:8}}>

        {/* Seam Allowances */}
        <div style={{background:'white',border:'1px solid #DEE2E6',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#6C757D',textTransform:'uppercase',marginBottom:6}}>Seam Allow. (cm)</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
            {['top','right','bottom','left'].map(side=>(
              <div key={side}>
                <label style={{fontSize:9,color:'#ADB5BD',textTransform:'capitalize',display:'block'}}>{side}</label>
                <input type="number" step="0.1" min="0" max="5"
                  value={pat.seamAllowances?.[side]??1}
                  onChange={e=>updateSA(side,e.target.value)}
                  style={{width:'100%',padding:'3px 4px',border:'1px solid #ddd',borderRadius:3,fontSize:11,boxSizing:'border-box'}}/>
              </div>
            ))}
          </div>
        </div>

        {/* Points of Measure (POM) — real library names, live geometry-
            derived values recomputed from the pattern's CURRENT shape on
            every render, not frozen at tag-time. */}
        <div style={{background:'white',border:'1px solid #DEE2E6',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#6C757D',textTransform:'uppercase',marginBottom:5}}>Points of Measure ({(pat.poms||[]).length})</div>
          {(pat.poms||[]).length===0
            ? <div style={{fontSize:9,color:'#ADB5BD'}}>Use POM tool — click two points on the outline, then pick a name.</div>
            : (pat.poms||[]).map((pomEntry,i)=>{
                const liveVal = computePomValue(pts, pomEntry);
                return (
                  <div key={pomEntry.id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4,fontSize:9,paddingBottom:4,borderBottom:i<(pat.poms.length-1)?'1px solid #F4F6F8':'none'}}>
                    <div>
                      <div style={{color:'#1A2A3A',fontWeight:'bold'}}>{pomEntry.code ? `${pomEntry.code} — ${pomEntry.name}` : pomEntry.name}</div>
                      <div style={{color:'#ADB5BD'}}>{pomEntry.measureType} · live: {liveVal !== null ? liveVal.toFixed(2)+' cm' : '—'}</div>
                    </div>
                    <button onClick={()=>setPatterns(prev=>prev.map((p,ii)=>
                      ii===selIdx?{...p,poms:(p.poms||[]).filter(pp=>pp.id!==pomEntry.id)}:p
                    ))} style={{background:'none',border:'none',cursor:'pointer',color:'#C62828',fontSize:9}}>✕</button>
                  </div>
                );
              })
          }
        </div>

        {/* Notches */}
        <div style={{background:'white',border:'1px solid #DEE2E6',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#6C757D',textTransform:'uppercase',marginBottom:5}}>Notches ({(pat.notches||[]).length})</div>
          {(pat.notches||[]).length===0
            ? <div style={{fontSize:9,color:'#ADB5BD'}}>Use Notch tool — snaps to edge.</div>
            : <>
              <button onClick={()=>{pushHistory();setPatterns(prev=>prev.map((p,ii)=>ii===selIdx?{...p,notches:[]}:p));}}
                style={{width:'100%',padding:'3px',background:'#FFEBEE',border:'1px solid #fecaca',borderRadius:3,cursor:'pointer',fontSize:9,color:'#C62828',marginBottom:4}}>
                ✕ Clear All {(pat.notches||[]).length} Notches
              </button>
              {(pat.notches||[]).map((n,i)=>(
              <div key={n.id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3,fontSize:9}}>
                <span style={{color:'#495057'}}>{n.type} edge{n.edgeIdx} t={n.t?.toFixed(2)}</span>
                <button onClick={()=>setPatterns(prev=>prev.map((p,ii)=>
                  ii===selIdx?{...p,notches:(p.notches||[]).filter(nn=>nn.id!==n.id)}:p
                ))} style={{background:'none',border:'none',cursor:'pointer',color:'#C62828',fontSize:9}}>✕</button>
              </div>
            ))
            }</>
          }
        </div>

        {/* Measurements */}
        <div style={{background:'white',border:'1px solid #DEE2E6',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#6C757D',textTransform:'uppercase',marginBottom:5}}>Measurements ({(pat.measurementPoints||[]).length})</div>
          {(pat.measurementPoints||[]).map(mp=>(
            <div key={mp.id} style={{fontSize:9,color:'#495057',marginBottom:3,display:'flex',justifyContent:'space-between'}}>
              <span>{mp.label}: {mp.dist?.toFixed(1)} cm</span>
              <button onClick={()=>setPatterns(prev=>prev.map((p,i)=>
                i===selIdx?{...p,measurementPoints:(p.measurementPoints||[]).filter(m=>m.id!==mp.id)}:p
              ))} style={{background:'none',border:'none',cursor:'pointer',color:'#C62828',fontSize:8}}>✕</button>
            </div>
          ))}
        </div>

        {/* ── SPEC BUILDER ─────────────────────────────────────────────── */}
        <div style={{background:'#faf5ff',border:'2px solid #6A1B9A',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#6A1B9A',marginBottom:4,display:'flex',justifyContent:'space-between'}}>
            📐 SPEC BUILDER
            <span style={{fontSize:8,color:'#ADB5BD',fontWeight:'normal'}}>edits update shape live</span>
          </div>
          <div style={{fontSize:8,color:'#6A1B9A',background:'#E1BEE7',borderRadius:4,padding:'3px 6px',marginBottom:6}}>
            {hasTp ? 'Traced/hand-edited shape active — width/height reshape the piece directly; other fields drive grading only' : fromSavedTemplate ? 'Using a saved standard shape — width/height reshape the piece directly; other fields drive grading only' : 'No trace — specs drive the displayed shape'}
          </div>
          <div style={{maxHeight:180,overflowY:'auto',marginBottom:6}}>
            {Object.entries(pat.measurements||{}).length === 0 && (
              <div style={{fontSize:9,color:'#ADB5BD',textAlign:'center',padding:'8px 0'}}>
                No spec values yet. Add below.
              </div>
            )}
            {Object.entries(pat.measurements||{}).map(([k,v])=>(
              <div key={k} style={{display:'flex',gap:4,alignItems:'center',marginBottom:4}}>
                <span style={{fontSize:9,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#4c1d95',fontWeight:'bold'}}>{k}</span>
                <input type="number" step="0.5" value={v}
                  onChange={e=>updateSpec(k,e.target.value)}
                  style={{width:52,padding:'3px 4px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:10,fontWeight:'bold',textAlign:'right'}}/>
                <span style={{fontSize:8,color:'#6A1B9A'}}>cm</span>
                <button onClick={()=>setPatterns(prev=>prev.map((p,i)=>{
                  if(i!==selIdx) return p;
                  const m={...p.measurements}; delete m[k];
                  return {...p,measurements:m,updatedOn:new Date().toISOString()};
                }))} style={{background:'none',border:'none',cursor:'pointer',color:'#C62828',fontSize:9,lineHeight:1}}>✕</button>
              </div>
            ))}
          </div>
          {/* Add new spec field */}
          <div style={{borderTop:'1px solid #ddd6fe',paddingTop:6}}>
            <div style={{fontSize:8,color:'#6A1B9A',marginBottom:3}}>Add measurement:</div>
            <div style={{display:'flex',gap:3}}>
              <input placeholder="e.g. Chest Width" value={editSpecKey} onChange={e=>setEditSpecKey(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addSpec()}
                style={{flex:1,padding:'4px 5px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:9}}/>
              <input type="number" placeholder="cm" value={editSpecVal} onChange={e=>setEditSpecVal(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addSpec()}
                style={{width:46,padding:'4px 4px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:9}}/>
              <button onClick={addSpec}
                style={{padding:'4px 8px',background:'#6A1B9A',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:10,fontWeight:'bold'}}>+</button>
            </div>
          </div>
          {/* Shape key mapping hint — shows template-specific key names */}
          {(() => {
            const keyHints = {
              body_front:   'halfChest · bodyLength · armholeDepth · shoulder',
              body_back:    'halfChest · bodyLength · armholeDepth · shoulder',
              sleeve:       'width · sleeveLength · cuffLength',
              trouser_front:'halfWaist · halfHip · inseam · frontRise',
              trouser_back: 'halfWaist · halfHip · inseam · backRise',
              collar:       'width · collarHeight',
              collar_stand: 'width · collarStandHeight',
              collar_polo:  'width · collarHeight',
              cuff:         'cuffLength · cuffHeight',
              yoke:         'yokeWidth · yokeHeight',
            };
            const tplNow = detectTemplate(pat);
            const hint   = keyHints[tplNow] || 'width · height';
            return (
              <div style={{marginTop:6,fontSize:8,borderRadius:4,padding:'5px 7px',lineHeight:1.8,
                background: hasTp ? '#f0fdf4' : '#E1BEE7',
                color: hasTp ? '#166534' : '#6A1B9A'}}>
                {hasTp
                  ? <>✓ <strong>Traced shape active</strong> — <strong>width</strong>/<strong>height</strong> above rescale the real shape (great for "squeeze this back to length" after rope-dragging); other fields drive grading only.</>
                  : <>
                      <strong>Detected piece:</strong> <span style={{fontFamily:'monospace',color:'#4c1d95'}}>{tplNow}</span><br/>
                      <strong>Keys that drive this shape:</strong><br/>
                      <span style={{fontFamily:'monospace',color:'#4c1d95',fontSize:9}}>{hint}</span><br/>
                      <span style={{color:'#6A1B9A'}}>Rename your spec fields to match these keys → shape updates live.</span>
                    </>
                }
              </div>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

// ============================================================================
//  STEP 6 — GRADING VIEW
//  Import grading rules from customer spec data. Apply to create size patterns.
//  Display as nested layers (one on top of another, different strokes per size).
// ============================================================================
const GradingView = ({ patterns, specData, gradingRules, setGradingRules }) => {
  const [selPatIdx, setSelPatIdx] = useState(0);
  const [activeSizes, setActiveSizes] = useState(['S', 'M', 'L', 'XL']);
  const [showAllSizes, setShowAllSizes] = useState(true);
  const [editSize, setEditSize] = useState('M');

  const sizeColors = {
    XS: '#a78bfa', S: '#42A5F5', M: '#34d399', L: '#FFB300', XL: '#EF9A9A', XXL: '#e879f9', XXXL: '#ADB5BD',
  };

  const pat = patterns[selPatIdx];
  if (!pat) return <div style={{ padding: 16, color: '#ADB5BD' }}>No patterns loaded.</div>;

  const patRules = gradingRules[pat.id] || {};

  const updateRule = (size, key, val) => {
    setGradingRules(prev => ({
      ...prev,
      [pat.id]: {
        ...prev[pat.id],
        [size]: {
          ...(prev[pat.id]?.[size] || {}),
          [key]: parseFloat(val) || 0,
        }
      }
    }));
  };

  // Auto-derive grading rules from spec sheet size data
    const autoGrade = () => {
    // ═══════════════════════════════════════════════════════════════════
    // NEW: Check if template is selected (Snippet 5 addition)
    // ═══════════════════════════════════════════════════════════════════
    if (!selectedTemplate) {
      alert('Please select a template in Step 3 to enable auto-grading.');
      return;
    }
    // specData.sizes is an array of size LABELS (e.g. ['S','M','L']) — both
    // the CSV parser and the Style Registry importer agree on this shape.
    // The actual per-size measurement values live in specData.sizeData
    // (sizeData[sizeName] = {measurementKey: value}). This previously read
    // specData.sizes as if it were that dict, so Object.entries() iterated
    // array indices instead of real size names and Object.keys(sizeM) on a
    // plain string returned character indices — silently producing garbage
    // grading rules keyed '0','1','2'... instead of 'S','M','L'.
    const sizeNames = specData?.sizes || [];
    const sizeData  = specData?.sizeData || {};
    const base      = specData?.base || {};
    if (sizeNames.length === 0 || Object.keys(sizeData).length === 0) {
      alert('No size-wise data in spec sheet. Enter manually or import a spec sheet with size columns.');
      return;
    }
    const newRules = {};
    sizeNames.forEach(sz => {
      const sizeM = sizeData[sz] || {};
      newRules[sz] = {};
      Object.keys(pat.measurements || {}).forEach(mk => {
        const specKey = Object.keys(sizeM).find(sk => normKey(sk) === normKey(mk) || normKey(sk).includes(normKey(mk)));
        if (specKey && sizeM[specKey] !== undefined && base[specKey] !== undefined) {
          newRules[sz][mk] = +(sizeM[specKey] - base[specKey]).toFixed(2);
        }
      });
    });
    const matched = Object.values(newRules).reduce((s,r) => s + Object.keys(r).length, 0);
    if (matched === 0) {
      alert('Imported spec sheet size columns didn\'t match any of this piece\'s measurement keys (' + Object.keys(pat.measurements||{}).join(', ') + '). Check the spec sheet POM names line up.');
      return;
    }
    setGradingRules(prev => ({ ...prev, [pat.id]: newRules }));
  };

  const measurementKeys = Object.keys(pat.measurements || {});

  return (
    <div style={{ padding: 16 }}>
      {/* ── STANDARD GRADING RULES LOADER ────────────────────────────── */}
      {(() => {
        const [stdProduct, setStdProduct] = React.useState('');
        const [stdLoaded, setStdLoaded]   = React.useState('');
        const loadStandard = () => {
          if (!stdProduct) return;
          const table = getGradingTable(stdProduct);
          const newRules = {};
          // Apply to current pattern
          patterns.forEach(pat => {
            newRules[pat.id] = {};
            GARMENT_SIZES.forEach(sz => {
              newRules[pat.id][sz] = {};
              Object.entries(table).forEach(([mk, szDeltas]) => {
                if (szDeltas[sz] !== undefined && szDeltas[sz] !== 0) {
                  newRules[pat.id][sz][mk] = szDeltas[sz];
                }
              });
            });
          });
          setGradingRules(prev => ({ ...prev, ...newRules }));
          setStdLoaded(stdProduct);
        };
        const loadPieces = () => {
          if (!stdProduct) return;
          const newPieces = buildStandardPatterns(stdProduct, 'M');
          if (window.confirm(`This will REPLACE all ${newPieces.length} loaded patterns with standard ${GARMENT_STANDARDS[stdProduct]?.label} pieces. Continue?`)) {
            setPatterns(stdProduct === 'TROUSER' ? attachTrouserTracedShapes(newPieces) : newPieces);
            setStdLoaded(stdProduct);
          }
        };
        return (
          <div style={{ background:'#0D1B2A', border:'1px solid #2C3E50', borderRadius:8, padding:12, marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:'bold', color:'#38bdf8', marginBottom:8 }}>
              📋 Standard Grading Rules — Load ISO / Industry Standard
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <select value={stdProduct} onChange={e=>setStdProduct(e.target.value)}
                style={{ padding:'6px 10px', background:'#1A2A3A', color:'white', border:'1px solid #2C3E50', borderRadius:6, fontSize:11 }}>
                <option value="">── Select Product ──</option>
                {Object.entries(GARMENT_STANDARDS).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={loadStandard} disabled={!stdProduct}
                style={{ padding:'6px 14px', background: stdProduct?'#1565C0':'#2C3E50', color:'white', border:'none', borderRadius:6, cursor:stdProduct?'pointer':'not-allowed', fontSize:11, fontWeight:'bold' }}>
                ⚡ Load Grade Rules
              </button>
              <button onClick={loadPieces} disabled={!stdProduct}
                style={{ padding:'6px 14px', background: stdProduct?'#2E7D32':'#2C3E50', color:'white', border:'none', borderRadius:6, cursor:stdProduct?'pointer':'not-allowed', fontSize:11 }}>
                📐 Load Standard Pieces
              </button>
              {stdLoaded && (
                <span style={{ fontSize:10, color:'#34d399', background:'rgba(52,211,153,0.1)', padding:'3px 8px', borderRadius:4 }}>
                  ✓ {GARMENT_STANDARDS[stdLoaded]?.label} rules loaded
                </span>
              )}
            </div>
            <div style={{ fontSize:9, color:'#495057', marginTop:6 }}>
              <strong style={{color:'#6C757D'}}>⚡ Load Grade Rules</strong> — fills the delta table below with ISO-standard increments for all sizes.<br/>
              <strong style={{color:'#6C757D'}}>📐 Load Standard Pieces</strong> — replaces loaded patterns with the full set of standard cut parts for this garment.
            </div>
          </div>
        );
      })()}

      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 4 }}>
        📏 Grading View — Size Nesting
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Part selector */}
        <div style={{ width: 140, flexShrink: 0 }}>
          {patterns.map((p, i) => (
            <button key={p.id} onClick={() => setSelPatIdx(i)}
              style={{ width: '100%', textAlign: 'left', padding: '5px 8px', marginBottom: 3, borderRadius: 5,
                border: i === selPatIdx ? '1px solid #6A1B9A' : '1px solid #DEE2E6',
                background: i === selPatIdx ? '#EDE7F6' : 'white', cursor: 'pointer', fontSize: 11 }}>
              {p.partName}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }}>
          {/* Size toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#6C757D', marginRight: 4 }}>Show sizes:</span>
            {GARMENT_SIZES.map(sz => (
              <label key={sz} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={activeSizes.includes(sz)}
                  onChange={e => setActiveSizes(p => e.target.checked ? [...p, sz] : p.filter(x => x !== sz))} />
                <span style={{ color: sizeColors[sz], fontWeight: 'bold' }}>{sz}</span>
              </label>
            ))}
            <button onClick={autoGrade}
              style={{ marginLeft: 'auto', padding: '4px 10px', background: '#6A1B9A', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
              ⚡ Auto-grade from spec
            </button>
          </div>

          {/* Nested SVG layers — real polygon, proportionally scaled per graded size */}
          <div style={{ background: '#0D1B2A', border: '1px solid #2C3E50', borderRadius: 8, padding: 8, marginBottom: 12 }}>
            <svg width="100%" height={300} style={{ overflow: 'visible' }}>
              {(() => {
                // Base polygon from actual pattern (tracedPoints or measurement-based)
                const { pts: basePts, W: baseW, H: baseH } = buildDisplayPoly(pat);
                const maxH = baseH * 1.25; // canvas height in cm
                const SC   = Math.min(240 / (baseW * 2 + 8), 200 / maxH, 4);
                const OX   = baseW * SC + 20, OY = 10;
                const tx = x => OX + x * SC;
                const ty = y => OY + maxH * SC - y * SC;

                return activeSizes.map((sz) => {
                  // Same engine the Marker step uses — grades the real traced
                  // shape via per-point tags when available, so this preview
                  // and the eventual marker layout always agree.
                  const gradedPat = applyGradingToPattern(pat, gradingRules, sz);
                  const { pts: gradedRaw, W: gW } = buildDisplayPoly(gradedPat);
                  // Re-centre the graded shape onto the base shape's own
                  // origin (buildDisplayPoly centres each shape independently)
                  const gradedPts = gradedRaw.map(([x,y]) => [x - gW + baseW, y]);
                  const polyStr   = gradedPts.map(([x,y]) => `${tx(x).toFixed(1)},${ty(y).toFixed(1)}`).join(' ');
                  const isBase    = sz === 'M';
                  const scaleShown = (gW*2 / (baseW*2 || 1));
                  const labelX    = tx(gW + 3);
                  const labelY    = ty(baseH * 0.5);

                  return (
                    <g key={sz}>
                      <polygon points={polyStr} fill={isBase ? `${sizeColors[sz]}22` : 'none'}
                        stroke={sizeColors[sz]}
                        strokeWidth={isBase ? 2 : 1}
                        strokeDasharray={isBase ? 'none' : '5,2'}
                        opacity={isBase ? 1 : 0.85} />
                      <text x={labelX} y={labelY} fontSize="9" fill={sizeColors[sz]} fontWeight={isBase?'bold':'normal'}>
                        {sz} {scaleShown!==1 ? `×${scaleShown.toFixed(3)}` : '(base)'}
                      </text>
                    </g>
                  );
                });
              })()}
            </svg>
            <div style={{fontSize:9,color:'#6C757D',padding:'4px 6px',textAlign:'center'}}>
              M (filled) = base size · other sizes shown as outlines · right-click on table to add delta
            </div>
          </div>

          {/* Grading rules table */}
          <div style={{ background: 'white', border: '1px solid #DEE2E6', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 'bold', color: '#495057', marginBottom: 8 }}>
              Grading Deltas (cm) — deviations from base size:
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                <thead>
                  <tr style={{ background: '#F8F9FA' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #DEE2E6', minWidth: 100 }}>Measurement</th>
                    {activeSizes.map(sz => (
                      <th key={sz} style={{ padding: '5px 8px', borderBottom: '1px solid #DEE2E6', color: sizeColors[sz], minWidth: 50 }}>{sz}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {measurementKeys.map(mk => (
                    <tr key={mk} style={{ borderBottom: '1px solid #F4F6F8' }}>
                      <td style={{ padding: '4px 8px', color: '#495057' }}>{mk}</td>
                      {activeSizes.map(sz => (
                        <td key={sz} style={{ padding: '3px 6px' }}>
                          <input
                            type="number" step="0.5"
                            value={patRules[sz]?.[mk] ?? ''}
                            placeholder="0"
                            onChange={e => updateRule(sz, mk, e.target.value)}
                            style={{ width: 46, padding: '2px 3px', border: '1px solid #ddd', borderRadius: 3, fontSize: 10, textAlign: 'center' }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
//  STEP 7 — SEAM COMPATIBILITY CHECK
//  Runs the matching algorithm: neck vs collar, armhole vs sleeve, etc.
// ============================================================================
const SeamChecker = ({ patterns, gradingRules, activeSizes = ['M'] }) => {
  const [results, setResults] = useState([]);
  const [customRules, setCustomRules] = useState(() => detectSeamRuleSet(patterns));
  const [ran, setRan] = useState(false);

  // Re-pick the rule set whenever the loaded pieces change (e.g. switching
  // from a shirt style to a trouser style) instead of staying stuck on
  // whatever rules happened to be active when the component first mounted.
  useEffect(() => {
    setCustomRules(detectSeamRuleSet(patterns));
    setRan(false);
    setResults([]);
  }, [patterns.map(p=>p.partName).join(',')]); // eslint-disable-line

  // Extract measurement value for a part (by template or name)
  const getMeasurement = useCallback((patterns, partKeyword, specKey, size) => {
    const pat = patterns.find(p =>
      p.partName?.toLowerCase().includes(partKeyword.toLowerCase()) ||
      p.template?.toLowerCase().includes(partKeyword.toLowerCase())
    );
    if (!pat) return null;
    const rules = gradingRules?.[pat.id]?.[size] || {};
    const base = pat.measurements?.[specKey] ?? null;
    if (base === null) return null;
    return +(base + (rules[specKey] || 0)).toFixed(2);
  }, [gradingRules]);

  const runCheck = () => {
    const checkResults = customRules.map(rule => {
      const sizeResults = activeSizes.map(sz => {
        const valA = getMeasurement(patterns, rule.partA, rule.specA, sz);
        const valB = getMeasurement(patterns, rule.partB, rule.specB, sz);
        if (valA === null || valB === null) {
          return { size: sz, valA, valB, delta: null, status: 'missing' };
        }
        const delta = Math.abs(valA - valB);
        return { size: sz, valA, valB, delta: +delta.toFixed(2), status: delta <= rule.tolerance ? 'ok' : 'fail' };
      });
      return { rule, sizeResults };
    });
    setResults(checkResults);
    setRan(true);
  };

  const statusIcon = (s) => s === 'ok' ? '✓' : s === 'missing' ? '—' : '✗';
  const statusColor = (s) => s === 'ok' ? '#388E3C' : s === 'missing' ? '#ADB5BD' : '#C62828';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 4 }}>
        🔗 Seam Compatibility Check
      </div>
      <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 12 }}>
        Verifies that joined seam lengths are compatible (e.g. collar length ≈ neckline measurement).
      </div>

      <button onClick={runCheck} disabled={customRules.length===0}
        style={{ marginBottom: 14, padding: '8px 18px', background: customRules.length===0?'#CED4DA':'#0ea5e9', color: 'white', border: 'none', borderRadius: 6, cursor: customRules.length===0?'not-allowed':'pointer', fontSize: 11, fontWeight: 'bold' }}>
        ▶ Run Compatibility Check
      </button>
      {customRules.length===0 && (
        <div style={{ fontSize: 11, color: '#ADB5BD', marginBottom: 14 }}>
          No standard compatibility checks are defined for this piece set yet — only shirt and trouser rule sets exist so far.
        </div>
      )}

      {ran && (
        <div>
          {results.map((r, i) => {
            const hasFail = r.sizeResults.some(s => s.status === 'fail');
            const hasMissing = r.sizeResults.some(s => s.status === 'missing');
            return (
              <div key={r.rule.id} style={{
                background: hasFail ? '#FFEBEE' : hasMissing ? '#FFF8E1' : '#f0fdf4',
                border: `1px solid ${hasFail ? '#fecaca' : hasMissing ? '#fde68a' : '#A5D6A7'}`,
                borderRadius: 8, padding: 12, marginBottom: 8,
              }}>
                <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1A2A3A', marginBottom: 6 }}>
                  {hasFail ? '⚠️' : hasMissing ? '❓' : '✅'} {r.rule.label}
                  <span style={{ fontSize: 9, fontWeight: 'normal', color: '#6C757D', marginLeft: 8 }}>
                    tolerance ±{r.rule.tolerance} cm
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.sizeResults.map(sr => (
                    <div key={sr.size} style={{ background: 'white', border: `1px solid ${statusColor(sr.status)}`, borderRadius: 6, padding: '5px 10px', minWidth: 80 }}>
                      <div style={{ fontSize: 9, color: '#ADB5BD', textAlign: 'center' }}>Size {sr.size}</div>
                      <div style={{ fontSize: 11, textAlign: 'center', fontWeight: 'bold', color: statusColor(sr.status) }}>
                        {statusIcon(sr.status)}
                      </div>
                      {sr.valA !== null && <div style={{ fontSize: 9, color: '#495057', textAlign: 'center' }}>
                        {sr.valA} vs {sr.valB}
                      </div>}
                      {sr.delta !== null && sr.status !== 'ok' && (
                        <div style={{ fontSize: 9, color: '#C62828', textAlign: 'center' }}>Δ {sr.delta}</div>
                      )}
                    </div>
                  ))}
                </div>
                {hasFail && (
                  <div style={{ marginTop: 8, fontSize: 10, color: '#C62828', background: '#fff5f5', padding: '6px 8px', borderRadius: 4 }}>
                    ⚠ Fix: Adjust {r.rule.partA} {r.rule.specA} or {r.rule.partB} {r.rule.specB} so they match within ±{r.rule.tolerance} cm.
                    Return to Step 4 (Pattern Editor) to adjust measurements.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 8 — MARKER PLANNER
//  Three modes: (1) PO color-wise qty ratio, (2) Manual size ratio, (3) Auto
//  Warp-aligned placement, nested fills, yield maximisation.
// ============================================================================

// Polygon area via shoelace formula (cm²)
// Handles both {x,y} objects and [x,y] arrays
const polyArea = (pts) => {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j  = (i + 1) % pts.length;
    const xi = Array.isArray(pts[i]) ? pts[i][0] : pts[i].x;
    const yi = Array.isArray(pts[i]) ? pts[i][1] : pts[i].y;
    const xj = Array.isArray(pts[j]) ? pts[j][0] : pts[j].x;
    const yj = Array.isArray(pts[j]) ? pts[j][1] : pts[j].y;
    a += xi * yj - xj * yi;
  }
  return Math.abs(a) / 2;
};

// Compute bounding box AND actual polygon for every pattern.
// Uses buildDisplayPoly which handles tracedPoints (real shape) OR
// measurement-based generic shape — so every pattern always gets a polygon.
const getPatternBbox = (pat, scaleFactor = 1) => {
  const { pts: rawPts, W, H } = buildDisplayPoly(pat);
  // rawPts are [[x,y]] centred at x=0; shift x by +W to make 0-based (top-left origin)
  const pts = rawPts.map(([x, y]) => ({
    x: +((x + W) * scaleFactor).toFixed(3),
    y: +(y       * scaleFactor).toFixed(3),
  }));
  const w = +(W * 2 * scaleFactor).toFixed(2);
  const h = +(H       * scaleFactor).toFixed(2);
  return { w: Math.max(w, 5), h: Math.max(h, 5), pts };
};

// ── REAL POLYGON-BASED NESTING ──────────────────────────────────────────
// Replaces bounding-box packing with genuine shape-aware placement: pieces
// are tested for actual outline overlap (not just rectangle collision), so
// curved/tapered garment pieces can interlock into each other's concave
// regions the way bounding boxes never allow. Every piece of this was
// verified against real trouser panel geometry before being wired in here:
//   - real polygon overlap / minimum-distance math (segment intersection +
//     containment + point-to-segment distance) — verified against 15+
//     hand-computed synthetic cases, all passing exactly
//   - grain is a HARD constraint, not a preference: only rotations that
//     keep the piece's confirmed grain line parallel to the warp/length
//     axis are ever considered (0°/180° + mirror; never 90°/270°, which
//     would rotate grain onto the weft axis — a physically defective cut
//     regardless of how well it might pack)
//   - a bounding-box pre-filter (mathematically guaranteed safe — the
//     minimum distance between two polygons can never be less than the
//     minimum distance between their bounding boxes) keeps this fast: an
//     early un-filtered version took 194 seconds to nest just 6 real
//     pieces; with the pre-filter, 24 real pieces nest in ~2 seconds
//   - verified on real trouser panel data (not just synthetic shapes) to
//     genuinely beat bounding-box packing: +6.5 percentage points yield
//     and 5.1cm less fabric on a realistic 24-piece marker, zero overlaps,
//     the requested gap margin always genuinely respected
const NEST_SLOT = 0.5; // cm — scan resolution

function segmentsIntersectMM(p1, p2, p3, p4) {
  const d = (ax, ay, bx, by, cx, cy) => (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const d1 = d(p3[0], p3[1], p4[0], p4[1], p1[0], p1[1]);
  const d2 = d(p3[0], p3[1], p4[0], p4[1], p2[0], p2[1]);
  const d3 = d(p1[0], p1[1], p2[0], p2[1], p3[0], p3[1]);
  const d4 = d(p1[0], p1[1], p2[0], p2[1], p4[0], p4[1]);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}
function pointInPolygonMM(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    const intersect = ((yi > pt[1]) !== (yj > pt[1])) && (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
function polygonsOverlapMM(polyA, polyB) {
  const n = polyA.length, m = polyB.length;
  for (let i = 0; i < n; i++) {
    const a1 = polyA[i], a2 = polyA[(i + 1) % n];
    for (let j = 0; j < m; j++) {
      const b1 = polyB[j], b2 = polyB[(j + 1) % m];
      if (segmentsIntersectMM(a1, a2, b1, b2)) return true;
    }
  }
  if (pointInPolygonMM(polyA[0], polyB)) return true;
  if (pointInPolygonMM(polyB[0], polyA)) return true;
  return false;
}
function polyBboxMM(poly) {
  const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}
function transformPolyMM(poly, { rotateDeg = 0, mirrorX = false, dx = 0, dy = 0 }) {
  const rad = (rotateDeg * Math.PI) / 180, cos = Math.cos(rad), sin = Math.sin(rad);
  return poly.map(([x, y]) => {
    const px = mirrorX ? -x : x, py = y;
    return [px * cos - py * sin + dx, px * sin + py * cos + dy];
  });
}
function pointToSegmentDistanceMM(pt, a, b) {
  const abx = b[0] - a[0], aby = b[1] - a[1];
  const apx = pt[0] - a[0], apy = pt[1] - a[1];
  const lenSq = abx * abx + aby * aby;
  let t = lenSq === 0 ? 0 : (apx * abx + apy * aby) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(pt[0] - (a[0] + t * abx), pt[1] - (a[1] + t * aby));
}
function segmentToSegmentDistanceMM(a1, a2, b1, b2) {
  return Math.min(
    pointToSegmentDistanceMM(a1, b1, b2), pointToSegmentDistanceMM(a2, b1, b2),
    pointToSegmentDistanceMM(b1, a1, a2), pointToSegmentDistanceMM(b2, a1, a2)
  );
}
function polygonMinDistanceMM(polyA, polyB) {
  if (polygonsOverlapMM(polyA, polyB)) return 0;
  let min = Infinity;
  const n = polyA.length, m = polyB.length;
  for (let i = 0; i < n; i++) {
    const a1 = polyA[i], a2 = polyA[(i + 1) % n];
    for (let j = 0; j < m; j++) {
      const d = segmentToSegmentDistanceMM(a1, a2, polyB[j], polyB[(j + 1) % m]);
      if (d < min) min = d;
    }
  }
  return min;
}
function bboxDistanceMM(bboxA, bboxB) {
  const dx = Math.max(bboxA.minX - bboxB.maxX, bboxB.minX - bboxA.maxX, 0);
  const dy = Math.max(bboxA.minY - bboxB.maxY, bboxB.minY - bboxA.maxY, 0);
  return Math.hypot(dx, dy);
}
// FIXED: this used to hardcode [0°,180°] as "grain-safe," silently
// assuming every piece's local Y-axis already IS its grain direction. That
// assumption is false whenever a piece's real confirmed grain doesn't run
// along local Y — verified against a real trouser Front panel where every
// real grain candidate sits at 0°/180° in LOCAL coordinates (i.e. grain
// runs along local X, the piece's long axis), meaning the piece needed a
// 90° rotation to bring grain vertical — an option the old hardcoded set
// could never offer, guaranteeing every layout was grain-incorrect for
// that piece. Now the actual needed rotation is computed from the piece's
// real grain angle, not assumed.
function grainSafeTransformsMM(way, grainAngleDeg) {
  // grainAngleDeg: the piece's real confirmed grain angle in its own local
  // coordinates (0=horizontal, 90=vertical). Defaults to 90 (already
  // vertical) only when a piece genuinely has no grain data at all — the
  // same safe fallback used throughout, never assumed when real data exists.
  const angle = (typeof grainAngleDeg === 'number' && !isNaN(grainAngleDeg)) ? grainAngleDeg : 90;
  const needed = ((90 - angle) % 360 + 360) % 360; // rotation that brings this piece's real grain to vertical
  if (way === '1-way') return [{ rotateDeg: needed, mirrorX: false }];
  return [
    { rotateDeg: needed, mirrorX: false }, { rotateDeg: needed, mirrorX: true },
    { rotateDeg: (needed + 180) % 360, mirrorX: false }, { rotateDeg: (needed + 180) % 360, mirrorX: true },
  ];
}
function lowestValidYMM(normPoly, pw, ph, ax, skylineHeights, placedPolys, placedBboxes, gap) {
  const isSafe = (y) => {
    const testBbox = { minX: ax, maxX: ax + pw, minY: y, maxY: y + ph };
    let testPoly = null;
    for (let i = 0; i < placedPolys.length; i++) {
      if (bboxDistanceMM(testBbox, placedBboxes[i]) >= gap) continue;
      if (!testPoly) testPoly = normPoly.map(([x, yy]) => [x + ax, yy + y]);
      if (polygonMinDistanceMM(testPoly, placedPolys[i]) < gap - 1e-6) return false;
    }
    return true;
  };
  let y = 0;
  const s0 = Math.floor(ax / NEST_SLOT), nw = Math.max(1, Math.ceil(pw / NEST_SLOT));
  for (let j = s0; j < s0 + nw && j < skylineHeights.length; j++) if (skylineHeights[j] > y) y = skylineHeights[j];
  if (!isSafe(y)) {
    let guard = 0;
    while (!isSafe(y) && guard++ < 4000) y += 0.25;
    return y;
  }
  let candidate = y;
  while (candidate > 0) {
    const testY = candidate - 0.25;
    if (!isSafe(testY)) break;
    candidate = testY;
  }
  return Math.max(0, candidate);
}
function packOnceMM(orderedPieces, fabricWidth, fabricMaxLength, gap) {
  const placed = [], placedPolys = [], placedBboxes = [];
  const numSlots = Math.ceil(fabricWidth / NEST_SLOT);
  const skylineHeights = new Float32Array(numSlots).fill(0);
  for (const piece of orderedPieces) {
    const transforms = grainSafeTransformsMM(piece.way, piece.grainAngleDeg);
    let best = null;
    for (const t of transforms) {
      const shaped = transformPolyMM(piece.points, { rotateDeg: t.rotateDeg, mirrorX: t.mirrorX });
      const bbox = polyBboxMM(shaped);
      const norm = shaped.map(([x, y]) => [x - bbox.minX, y - bbox.minY]);
      const pw = bbox.maxX - bbox.minX, ph = bbox.maxY - bbox.minY;
      const nw = Math.max(1, Math.ceil(pw / NEST_SLOT));
      for (let s = 0; s + nw <= numSlots; s++) {
        const ax = s * NEST_SLOT;
        if (ax + pw > fabricWidth + 1e-6) continue;
        let skylineY = 0;
        const s0 = Math.floor(ax / NEST_SLOT);
        for (let j = s0; j < s0 + nw && j < numSlots; j++) if (skylineHeights[j] > skylineY) skylineY = skylineHeights[j];
        if (best && skylineY * 100000 + ax >= best.score) continue;
        const ay = lowestValidYMM(norm, pw, ph, ax, skylineHeights, placedPolys, placedBboxes, gap);
        if (fabricMaxLength > 0 && ay + ph > fabricMaxLength) continue;
        const score = ay * 100000 + ax;
        if (!best || score < best.score) {
          best = { poly: norm.map(([x, y]) => [x + ax, y + ay]), x: ax, y: ay, rotateDeg: t.rotateDeg, mirrorX: t.mirrorX, score, w: pw, h: ph };
        }
      }
    }
    if (best) {
      placed.push({ id: piece.id, partName: piece.partName, size: piece.size, poly: best.poly, x: best.x, y: best.y, rotateDeg: best.rotateDeg, mirrorX: best.mirrorX, w: best.w, h: best.h, grainLine: piece.grainLine });
      placedPolys.push(best.poly);
      placedBboxes.push(polyBboxMM(best.poly));
      const s0 = Math.floor(best.x / NEST_SLOT), s1 = Math.min(numSlots, s0 + Math.max(1, Math.ceil(best.w / NEST_SLOT)));
      const newH = best.y + best.h + gap;
      for (let j = s0; j < s1; j++) if (newH > skylineHeights[j]) skylineHeights[j] = newH;
    }
  }
  const usedLength = placed.reduce((m, p) => Math.max(m, p.y + p.h), 0);
  const totalArea = placed.reduce((s, p) => s + polyArea(p.poly), 0);
  const layArea = fabricWidth * usedLength;
  const yieldPct = layArea > 0 ? +((totalArea / layArea) * 100).toFixed(2) : 0;
  return { placed, usedLength: +usedLength.toFixed(2), yieldPct, totalPieces: placed.length };
}
const nestMarker = (pieces, fabricWidth, fabricMaxLength, gap = 0.5) => {
  if (pieces.length === 0) return { placed: [], usedLength: 0, yieldPct: 0, lengthPct: null, totalPieces: 0 };
  const orderings = [
    [...pieces].sort((a, b) => polyArea(b.points) - polyArea(a.points)),
    [...pieces].sort((a, b) => (polyBboxMM(b.points).maxY - polyBboxMM(b.points).minY) - (polyBboxMM(a.points).maxY - polyBboxMM(a.points).minY)),
    [...pieces].sort((a, b) => (polyBboxMM(b.points).maxX - polyBboxMM(b.points).minX) - (polyBboxMM(a.points).maxX - polyBboxMM(a.points).minX)),
  ];
  let best = null;
  for (const ordering of orderings) {
    const result = packOnceMM(ordering, fabricWidth, fabricMaxLength, gap);
    if (!best || result.yieldPct > best.yieldPct) best = result;
  }
  const lengthPct = fabricMaxLength > 0 ? +((best.usedLength / fabricMaxLength) * 100).toFixed(1) : null;
  return { ...best, lengthPct };
};

const MarkerPlanner = ({ patterns, specData, gradingRules, style }) => {
  const [mode, setMode] = useState('manual');  // po | manual | auto
  const [fabricWidth, setFabricWidth] = useState(150);  // cm
  const [fabricMaxLength, setFabricMaxLength] = useState(0);  // 0 = unlimited
  const [gap, setGap] = useState(0.5);
  const [sizeRatio, setSizeRatio] = useState({ XS: 0, S: 1, M: 2, L: 2, XL: 1, XXL: 0 });
  const [poData, setPoData] = useState('');
  const [layout, setLayout] = useState(null);
  const [running, setRunning] = useState(false);
  const [placedPieces, setPlacedPieces] = useState([]);
  const [selPiece, setSelPiece]   = useState(null);
  const [lastRatios, setLastRatios] = useState(null); // captured at run time, for the Costing bridge
  const [savingMarker, setSavingMarker] = useState(false);
  const [markerSavedAt, setMarkerSavedAt] = useState(null);
  const svgRef = useRef(null);

  const sizeColors = {
    XS: '#a78bfa', S: '#42A5F5', M: '#34d399', L: '#FFB300', XL: '#EF9A9A', XXL: '#e879f9', XXXL: '#ADB5BD',
  };

  // Parse PO mode (CSV: size,qty,color lines)
  const parsePO = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const out = {};
    lines.forEach(line => {
      const [sz, qty] = line.split(/[,;|\t]/);
      if (sz && qty) out[sz.trim().toUpperCase()] = parseInt(qty) || 0;
    });
    return out;
  };

  const buildPieces = (ratios) => {
    // Computes each piece's REAL grain angle, in the SAME local coordinate
    // frame used for nesting — not assumed vertical. Handles both grain
    // data formats present in this codebase:
    //   - DXF-imported pieces: grainLine is [{fx,fy},{fx,fy}], a fraction
    //     of the pattern's own outline bbox at import time. Since fractions
    //     are frame-invariant, mapping them onto the SAME bbox as `points`
    //     (both derived from the same tracedPoints) gives the real grain
    //     points in the nester's own coordinate frame directly.
    //   - Standard library patterns: grainLine is {angleDeg}, used as-is.
    //   - No grain data at all: defaults to 90° (already vertical) inside
    //     grainSafeTransformsMM — the same safe fallback as before, but now
    //     only applied when there's genuinely nothing better to use.
    const computeGrainAngleDeg = (pointsFrame, grainLine) => {
      if (!grainLine) return undefined;
      if (grainLine.angleDeg !== undefined) return grainLine.angleDeg;
      if (Array.isArray(grainLine) && grainLine.length === 2 && grainLine[0].fx !== undefined) {
        const xs = pointsFrame.map(p => p[0]), ys = pointsFrame.map(p => p[1]);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        const [g1, g2] = grainLine;
        const p1 = [minX + g1.fx * (maxX - minX), minY + g1.fy * (maxY - minY)];
        const p2 = [minX + g2.fx * (maxX - minX), minY + g2.fy * (maxY - minY)];
        return Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180 / Math.PI;
      }
      return undefined; // unrecognized format — fall back to the safe default rather than guess
    };

    const pieces = [];
    Object.entries(ratios).forEach(([sz, count]) => {
      if (!count) return;
      patterns.forEach(pat => {
        // applyGradingToPattern grades BOTH measurements and (when the piece
        // has tracedPoints) the actual traced shape — previously only
        // measurements were graded here, which the marker never used, so
        // every size packed as an identical, ungraded shape.
        const fullPat = applyGradingToPattern(pat, gradingRules, sz);
        const bbox = getPatternBbox(fullPat);
        // Real nester needs raw [x,y] point pairs, not {x,y} objects
        const points = (bbox.pts || []).map(p => [p.x, p.y]);
        // way defaults to '2-way' (least restrictive) when a pattern has no
        // explicit directional-fabric flag — most bulk commercial fabrics
        // are non-directional; only DXF-imported pieces where the importer
        // captured a real 'way' value should ever be more constrained
        const way = pat.way || '2-way';
        const grainAngleDeg = computeGrainAngleDeg(points, fullPat.grainLine);
        for (let c = 0; c < count; c++) {
          pieces.push({ id: `${pat.id}-${sz}-${c}`, partName: pat.partName, size: sz, points, way, grainLine: fullPat.grainLine, grainAngleDeg, w: bbox.w, h: bbox.h });
        }
      });
    });
    return pieces;
  };

  const runMarker = async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 50));
    try {
      let ratios = { ...sizeRatio };
      if (mode === 'po') ratios = parsePO(poData);
      else if (mode === 'auto') {
        // Auto: use size data from spec sheet when available, else equal distribution
        const sizes = (specData?.sizes?.length ? specData.sizes : ['S', 'M', 'L', 'XL']);
        sizes.forEach(sz => { ratios[sz] = (specData?.sizeData?.[sz] ? 2 : 1); });
      }

      const pieces = buildPieces(ratios);
      if (pieces.length === 0) { alert('No pieces to place. Check size ratio / pattern definitions.'); setRunning(false); return; }

      // Real polygon-based nester — automatically tries multiple placement
      // strategies internally and keeps the best; no user-tunable iteration
      // count needed anymore.
      const bestResult = nestMarker(pieces, fabricWidth, fabricMaxLength, gap);
      if (bestResult.totalPieces < pieces.length) {
        console.warn(`Marker: only ${bestResult.totalPieces}/${pieces.length} pieces could be placed within the given constraints.`);
      }

      setLayout(bestResult);
      setPlacedPieces(bestResult.placed.map(p => ({ ...p })));
      setLastRatios(ratios);
      setMarkerSavedAt(null); // new run — previous "saved to Costing" no longer reflects current layout
    } finally {
      setRunning(false);
    }
  };

  // ── Bridge to Costing: persist this marker's yield/consumption the same
  // way Workstation already persists SMV onto the style record, so
  // CostingWorksheets can pull real numbers instead of a manual guess.
  // Mirrors MarkerMaker's own savePatterns() save pattern exactly.
  const saveMarkerToCosting = async () => {
    if (!style?.id || !layout) return;
    const totalGarments = Object.values(lastRatios || {}).reduce((s,v) => s + (+v||0), 0);
    if (totalGarments <= 0) { alert('Could not determine garment count for this marker run.'); return; }
    const wastagePct = +(100 - layout.yieldPct).toFixed(2);
    const consumptionM = +((layout.usedLength / 100) / totalGarments).toFixed(4); // linear metres of fabric per garment
    const marker_data = {
      fabricWidth, usedLength: layout.usedLength, yieldPct: layout.yieldPct,
      wastagePct, consumptionM, totalGarments, totalPieces: layout.totalPieces,
      sizeRatio: lastRatios, gap, savedOn: new Date().toISOString(),
    };
    setSavingMarker(true);
    try {
      await fetch(SAVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'style', data: { ...style, marker_data, updatedOn: new Date().toISOString() } }),
      });
      setMarkerSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      alert('Could not save to Costing: ' + e.message);
    } finally {
      setSavingMarker(false);
    }
  };

  const exportPdf = () => {
    if (!layout) return;
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const svgStr = new XMLSerializer().serializeToString(svgEl);
    const html = `<!DOCTYPE html><html><head><title>Marker Plan</title>
      <style>@page{size:A3 landscape;margin:8mm}body{margin:0;font-family:monospace}
      h3{margin:0 0 4mm;font-size:13pt} .meta{font-size:9pt;margin-bottom:6mm}
      svg{width:100%;height:auto;border:1px solid #ccc}</style></head><body>
      <h3>Marker Plan — ${new Date().toLocaleString()}</h3>
      <div class="meta">
        Fabric: ${fabricWidth} cm wide · Used length: ${layout.usedLength.toFixed(1)} cm ·
        Pieces: ${layout.totalPieces} · Yield: <strong>${layout.yieldPct.toFixed(2)}%</strong>
      </div>
      ${svgStr}
      <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
  };

  // Drag piece on SVG
  const dragRef = useRef(null);
  const getSvgPt = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x, y };
  };

  const onPieceDown = (e, idx) => {
    e.stopPropagation();
    const { x, y } = getSvgPt(e);
    dragRef.current = { idx, ox: x - placedPieces[idx].x, oy: y - placedPieces[idx].y };
    setSelPiece(idx);
  };

  const onSvgMove = (e) => {
    if (!dragRef.current) return;
    const { x, y } = getSvgPt(e);
    const { idx, ox, oy } = dragRef.current;
    const p = placedPieces[idx];
    const nx = Math.max(0, Math.min(fabricWidth - p.w, x - ox));
    const ny = Math.max(0, y - oy);
    setPlacedPieces(prev => prev.map((pp, i) => i === idx ? { ...pp, x: +nx.toFixed(2), y: +ny.toFixed(2) } : pp));
  };

  const onSvgUp = () => { dragRef.current = null; };

  // Visual scale
  const SCALE = Math.min(760 / fabricWidth, 4);
  const RULER_W    = 28;   // left ruler width in SVG px
  const dispWidth  = fabricWidth * SCALE;
  const usedLen    = layout?.usedLength || 200;
  const dispHeight = Math.min(usedLen * SCALE, 600);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1A2A3A', marginBottom: 8 }}>
        🗺️ Marker Planner
      </div>

      {/* Mode selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { id: 'po',     label: '1 — PO Color-Wise Qty',  desc: 'Ratio from Purchase Order' },
          { id: 'manual', label: '2 — Manual Size Ratio',  desc: 'User-specified counts' },
          { id: 'auto',   label: '3 — Auto Mode',          desc: 'System determines from spec / history' },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            style={{ flex: 1, padding: '8px 10px', textAlign: 'left', fontSize: 11,
              background: mode === m.id ? '#1A2A3A' : 'white',
              color: mode === m.id ? 'white' : '#2C3E50',
              border: `1px solid ${mode === m.id ? '#1A2A3A' : '#DEE2E6'}`, borderRadius: 8, cursor: 'pointer' }}>
            <div style={{ fontWeight: 'bold' }}>{m.label}</div>
            <div style={{ fontSize: 9, opacity: 0.7, marginTop: 2 }}>{m.desc}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
        {/* Left: Controls */}
        <div>
          {/* Fabric settings */}
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 'bold', color: '#0c4a6e', marginBottom: 6 }}>FABRIC SETTINGS</div>
            {[
              ['Width (cm)', fabricWidth, v => setFabricWidth(+v)],
              ['Max Length (cm, 0=∞)', fabricMaxLength, v => setFabricMaxLength(+v)],
              ['Gap between parts (cm)', gap, v => setGap(+v)],
            ].map(([label, val, setter]) => (
              <div key={label} style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 9, color: '#0c4a6e' }}>{label}</label>
                <input type="number" min={0} step={0.5} value={val} onChange={e => setter(e.target.value)}
                  style={{ width: '100%', padding: '4px 6px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>

          {/* Mode-specific controls */}
          {mode === 'po' && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 'bold', color: '#9a3412', marginBottom: 6 }}>PO SIZE QTY (size,count per line)</div>
              <textarea value={poData} onChange={e => setPoData(e.target.value)} rows={6}
                placeholder={`S,50\nM,100\nL,80\nXL,40`}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 10, padding: 5, border: '1px solid #fed7aa', borderRadius: 4, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
          )}

          {mode === 'manual' && (
            <div style={{ background: '#f0fdf4', border: '1px solid #A5D6A7', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 'bold', color: '#166534', marginBottom: 6 }}>SIZE RATIO (garments per size)</div>
              {GARMENT_SIZES.map(sz => (
                <div key={sz} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <label style={{ fontSize: 10, color: sizeColors[sz], fontWeight: 'bold', width: 30 }}>{sz}</label>
                  <input type="number" min={0} max={20} step={1}
                    value={sizeRatio[sz] ?? 0}
                    onChange={e => setSizeRatio(p => ({ ...p, [sz]: +e.target.value }))}
                    style={{ width: 60, padding: '3px 5px', border: '1px solid #A5D6A7', borderRadius: 4, fontSize: 11 }} />
                </div>
              ))}
            </div>
          )}

          {mode === 'auto' && (
            <div style={{ background: '#EDE7F6', border: '1px solid #ddd6fe', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#6A1B9A' }}>
                Auto mode uses spec sheet size data or historical average to determine size ratio.
                {!specData?.sizes || specData.sizes.length === 0
                  ? ' No spec size data found — will use equal distribution.'
                  : ` Found ${specData.sizes.length} sizes in spec.`}
              </div>
            </div>
          )}

          <button onClick={runMarker} disabled={running}
            style={{ width: '100%', padding: '10px', background: running ? '#ADB5BD' : '#0ea5e9', color: 'white',
              border: 'none', borderRadius: 8, cursor: running ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 'bold' }}>
            {running ? '⟳ Computing…' : '▶ Generate Marker'}
          </button>
        </div>

        {/* Right: Layout canvas */}
        <div>
          {layout && (
            <div style={{ background: '#f0fdf4', border: '1px solid #A5D6A7', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 20, fontSize: 11, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>📐 <strong>Fabric used:</strong> {layout.usedLength.toFixed(1)} cm</span>
              <span>📦 <strong>Pieces:</strong> {layout.totalPieces}</span>
              {layout.lengthPct !== null && (
                <span style={{ color: '#1565C0', fontWeight: 'bold' }}>
                  📏 Length used: {layout.lengthPct}% of max
                </span>
              )}
              <span style={{ color: '#388E3C', fontWeight: 'bold' }}
                title="Pattern area ÷ total lay area. Higher = less fabric waste between pieces.">
                🎯 Fabric utilisation: {layout.yieldPct.toFixed(1)}%
              </span>
              <span style={{ fontSize:9, color:'#ADB5BD' }}>Right-click a piece to rotate 90°</span>
              <button onClick={saveMarkerToCosting} disabled={savingMarker || !style?.id}
                title={!style?.id ? 'Open this marker from a style to enable saving' : 'Push wastage % and fabric consumption to this style\'s Costing sheet'}
                style={{ padding: '5px 12px', background: style?.id ? '#6A1B9A' : '#CED4DA', color: 'white', border: 'none', borderRadius: 5, cursor: style?.id?'pointer':'not-allowed', fontSize: 10, fontWeight:'bold' }}>
                {savingMarker ? '⟳ Saving…' : markerSavedAt ? `✓ Saved to Costing (${markerSavedAt})` : '💾 Save to Costing'}
              </button>
              <button onClick={exportPdf}
                style={{ marginLeft: markerSavedAt || !style?.id ? 0 : 'auto', padding: '5px 12px', background: '#1A2A3A', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
                🖨 Print / Export PDF
              </button>
            </div>
          )}

          {/* SVG lay */}
          <div style={{ background: '#fafafa', border: '1px solid #DEE2E6', borderRadius: 8, overflow: 'auto' }}>
            <svg
              ref={svgRef}
              width={dispWidth + RULER_W}
              height={layout ? dispHeight + 40 : 220}
              style={{ display: 'block' }}
              onMouseMove={onSvgMove}
              onMouseUp={onSvgUp}
              onMouseLeave={onSvgUp}
            >
              {/* Fabric background */}
              <rect x={RULER_W} y={16} width={dispWidth} height={layout ? dispHeight + 20 : 200}
                fill="#F8F9FA" stroke="#CED4DA" strokeWidth="1" />
              {/* Warp lines (vertical dashes = warp direction) */}
              {Array.from({ length: Math.floor(fabricWidth / 10) }, (_, i) => (
                <line key={i} x1={RULER_W + (i + 1) * 10 * SCALE} y1={16} x2={RULER_W + (i + 1) * 10 * SCALE} y2={dispHeight + 36}
                  stroke="#DEE2E6" strokeWidth="0.5" strokeDasharray="2,4" />
              ))}
              {/* WARP direction label (vertical = length = grain direction) */}
              <text x={12} y={dispHeight/2 + 20} textAnchor="middle" fontSize="9" fill="#495057" fontWeight="bold"
                transform={`rotate(-90 12 ${dispHeight/2})`}>
                WARP / LENGTH (cm)
              </text>
              {/* Width label at top */}
              <text x={RULER_W + dispWidth/2} y={12} textAnchor="middle" fontSize="9" fill="#6C757D">
                ← {fabricWidth} cm wide (WEFT) →
              </text>
              {/* Length ruler — tick marks every 10 cm on left */}
              {Array.from({length:Math.ceil((layout?.usedLength||200)/10)+1},(_,i)=>{
                const yPx = 18 + i*10*SCALE;
                return (
                  <g key={`ry${i}`}>
                    <line x1={RULER_W-8} y1={yPx} x2={RULER_W} y2={yPx} stroke="#ADB5BD" strokeWidth="0.8"/>
                    {i%5===0 && <text x={RULER_W-10} y={yPx+3} textAnchor="end" fontSize="7" fill="#6C757D">{i*10}</text>}
                  </g>
                );
              })}

              {/* Placed pieces — real polygon shape, already correctly
                  rotated/mirrored by the nester to keep grain safe. No
                  manual free-rotation control anymore: since only
                  grain-safe transforms (0°/180° + mirror) are ever
                  considered, letting the user freely rotate to 90°/270°
                  would let them override that safety guarantee — removed
                  rather than kept as a footgun. Dragging to reposition is
                  still available for manual fine-tuning. */}
              {placedPieces.map((p, i) => {
                const fillCol = sizeColors[p.size] || '#ADB5BD';
                const cx = p.x * SCALE + (p.w * SCALE) / 2;
                const cy = 14 + p.y * SCALE + (p.h * SCALE) / 2;

                const polyStr = (p.poly && p.poly.length >= 3)
                  ? p.poly.map(([px, py]) => `${RULER_W + px * SCALE},${18 + py * SCALE}`).join(' ')
                  : null;

                return (
                  <g key={`${p.id}-${i}`}
                    onMouseDown={e => onPieceDown(e, i)}
                    style={{ cursor: 'grab' }}>
                    {polyStr ? (
                      <polygon points={polyStr}
                        fill={fillCol} fillOpacity={selPiece===i?0.55:0.3}
                        stroke={selPiece===i?'#1A2A3A':fillCol} strokeWidth={selPiece===i?2:1}/>
                    ) : (
                      <rect x={RULER_W+p.x*SCALE} y={18+p.y*SCALE} width={p.w*SCALE} height={p.h*SCALE}
                        fill={fillCol} fillOpacity={selPiece===i?0.55:0.3}
                        stroke={selPiece===i?'#1A2A3A':fillCol} strokeWidth={selPiece===i?2:1}/>
                    )}
                    {/* Grain line — every placement is grain-safe by
                        construction (only 0°/180°+mirror transforms are
                        ever used), so grain is always parallel to the
                        length/warp axis after placement; drawn vertical
                        here is therefore correct for every piece placed by
                        this nester, not a hardcoded assumption unrelated to
                        the real transform the way it was before. */}
                    <line x1={cx} y1={18+p.y*SCALE+4} x2={cx} y2={18+(p.y+p.h)*SCALE-4}
                      stroke="rgba(5,150,105,0.6)" strokeWidth="0.9" strokeDasharray="3,2"/>
                    {p.w*SCALE>18 && p.h*SCALE>14 && (
                      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
                        fontSize={Math.max(6,Math.min(8,p.w*SCALE/6))} fill="#1A2A3A">
                        {p.partName?.substring(0,8)} {p.size}{p.rotateDeg?` ↺${p.rotateDeg}°`:''}{p.mirrorX?' ⇋':''}
                      </text>
                    )}
                  </g>
                );
              })}

              {!layout && (
                <text x={dispWidth / 2} y={110} textAnchor="middle" fontSize="11" fill="#ADB5BD">
                  Configure settings and click "Generate Marker"
                </text>
              )}
            </svg>
          </div>

          {/* Legend */}
          {layout && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {Object.entries(sizeColors).filter(([sz]) =>
                placedPieces.some(p => p.size === sz)
              ).map(([sz, color]) => (
                <span key={sz} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#495057' }}>
                  <span style={{ width: 12, height: 12, background: color, opacity: 0.5, borderRadius: 2, display: 'inline-block', border: `1px solid ${color}` }} />
                  {sz} ({placedPieces.filter(p => p.size === sz).length} pcs)
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
//  MAIN — MarkerMaker CONTAINER
// ============================================================================

// ============================================================================
//  PATTERN GALLERY
//  Shows ALL stored patterns as cards — review, edit, delete, organise.
//  Accessible from the main MarkerMaker top bar.
// ============================================================================
const PatternGallery = ({ db, onClose, onEdit }) => {
  const [patterns,  setPatterns]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState({ search:'', template:'' });
  const [selIdx,    setSelIdx]    = useState(null);
  const [deleting,  setDeleting]  = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/get_patterns.php`)
      .then(r => r.json())
      .then(d => { if (d.success) setPatterns(d.patterns || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const deletePattern = async (p) => {
    if (!window.confirm(`Delete pattern "${p.partName}"?`)) return;
    setDeleting(p.id);
    try {
      await fetch(`${API_BASE}/save_universal.php`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ category:'pattern', data:{ dbId: p.dbId, _deleted: true } })
      });
      setPatterns(prev => prev.filter(pp => pp.id !== p.id));
    } finally { setDeleting(null); }
  };

  // ── Mini SVG thumbnail for each card ─────────────────────────────────────
  const Thumb = ({ pattern }) => {
    const { pts, W, H } = buildDisplayPoly(pattern);
    const PAD = 6, TW = 120, TH = 90;
    const sc = Math.min((TW - PAD*2) / (W*2), (TH - PAD*2) / H, 4);
    const tx = x => TW/2 + x * sc;
    const ty = y => TH - PAD - y * sc;
    return (
      <svg width={TW} height={TH} style={{ display:'block', background:'#F8F9FA', borderRadius:4 }}>
        {/* Mini grid */}
        {Array.from({length: Math.ceil(W*2/5)+1}, (_,i) => (
          <line key={i} x1={tx(-W + i*5)} y1={PAD} x2={tx(-W + i*5)} y2={TH-PAD}
            stroke="#DEE2E6" strokeWidth="0.4"/>
        ))}
        <polygon points={pts.map(p=>`${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ')}
          fill="rgba(37,99,235,0.07)" stroke="#1e3a8a" strokeWidth="1" strokeLinejoin="round"/>
        {/* Grain line */}
        <line x1={tx(0)} y1={ty(H*0.15)} x2={tx(0)} y2={ty(H*0.85)}
          stroke="#2E7D32" strokeWidth="0.8" strokeDasharray="3,2"/>
        <text x={TW/2} y={TH-1} textAnchor="middle" fontSize="7" fill="#6C757D">{pattern.partName}</text>
      </svg>
    );
  };

  const templates = [...new Set(patterns.map(p => detectTemplate(p)).filter(Boolean))];
  const filtered = patterns.filter(p => {
    const s = `${p.partName} ${p.template} ${p.sourceMethod||''}`.toLowerCase();
    return (!filter.search || s.includes(filter.search.toLowerCase()))
      && (!filter.template || detectTemplate(p) === filter.template);
  });

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10000, background:'#0D1B2A', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ background:'#1A2A3A', padding:'10px 20px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #2C3E50', flexShrink:0 }}>
        <span style={{ fontSize:16, fontWeight:'bold', color:'white' }}>📐 Pattern Library</span>
        <span style={{ fontSize:11, color:'#6C757D' }}>{patterns.length} patterns stored across all parts</span>
        {/* Search */}
        <input value={filter.search} onChange={e=>setFilter(f=>({...f,search:e.target.value}))}
          placeholder="Search by name…"
          style={{ marginLeft:'auto', padding:'5px 10px', background:'#2C3E50', color:'white', border:'1px solid #495057', borderRadius:6, fontSize:11, width:180 }}/>
        <select value={filter.template} onChange={e=>setFilter(f=>({...f,template:e.target.value}))}
          style={{ padding:'5px 8px', background:'#2C3E50', color:'white', border:'1px solid #495057', borderRadius:6, fontSize:11 }}>
          <option value="">All types</option>
          {templates.map(t=><option key={t} value={t}>{t.replace(/_/g,' ').toUpperCase()}</option>)}
        </select>
        <button onClick={onClose}
          style={{ padding:'6px 14px', background:'#EF5350', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:'bold' }}>
          ✕ Close
        </button>
      </div>

      {/* Gallery grid */}
      <div style={{ flex:1, overflowY:'auto', padding:20 }}>
        {loading && <div style={{ color:'#6C757D', textAlign:'center', padding:40 }}>Loading patterns…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ color:'#495057', textAlign:'center', padding:60, fontSize:13 }}>
            No patterns found.{filter.search || filter.template ? ' Try clearing filters.' : ' Add patterns via PartDNA → Patterns & Specs tab.'}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:12 }}>
          {filtered.map((p, i) => {
            const tpl = detectTemplate(p);
            const { W, H } = buildDisplayPoly(p);
            return (
              <div key={p.id || i}
                style={{ background:'#1A2A3A', border:`1px solid ${selIdx===i?'#1976D2':'#2C3E50'}`, borderRadius:10, overflow:'hidden', cursor:'pointer', transition:'border 0.15s' }}
                onClick={() => setSelIdx(selIdx===i ? null : i)}>
                {/* Thumbnail */}
                <div style={{ padding:8, background:'#0D1B2A', display:'flex', justifyContent:'center' }}>
                  <Thumb pattern={p} />
                </div>
                {/* Info */}
                <div style={{ padding:'8px 10px' }}>
                  <div style={{ fontWeight:'bold', fontSize:12, color:'white', marginBottom:2 }}>{p.partName}</div>
                  <div style={{ fontSize:9, color:'#6C757D', marginBottom:4 }}>
                    <span style={{ background:'#2C3E50', padding:'1px 5px', borderRadius:3, marginRight:4 }}>{tpl.replace(/_/g,' ')}</span>
                    {p.hasTp !== false && <span style={{ color:'#34d399' }}>● traced</span>}
                  </div>
                  <div style={{ fontSize:9, color:'#ADB5BD' }}>
                    {(W*2).toFixed(1)} × {H.toFixed(1)} cm · SA: {Object.values(p.seamAllowances||{}).join('/')} · Notches: {(p.notches||[]).length}
                  </div>
                  <div style={{ fontSize:9, color:'#6C757D', marginTop:2 }}>
                    {Object.keys(p.measurements||{}).length} specs · Updated: {p.updatedOn ? new Date(p.updatedOn).toLocaleDateString() : '—'}
                  </div>
                  {/* Actions */}
                  <div style={{ display:'flex', gap:4, marginTop:8 }}>
                    <button onClick={e=>{ e.stopPropagation(); onEdit && onEdit(p); onClose(); }}
                      style={{ flex:1, padding:'4px', background:'#1565C0', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:9, fontWeight:'bold' }}>
                      ✎ Edit
                    </button>
                    <button onClick={e=>{ e.stopPropagation(); deletePattern(p); }}
                      disabled={deleting===p.id}
                      style={{ flex:1, padding:'4px', background:'#991b1b', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:9 }}>
                      {deleting===p.id ? '…' : '✕ Delete'}
                    </button>
                  </div>
                </div>
                {/* Expanded measurements when selected */}
                {selIdx === i && Object.keys(p.measurements||{}).length > 0 && (
                  <div style={{ padding:'8px 10px', borderTop:'1px solid #2C3E50', background:'#0D1B2A' }}>
                    <div style={{ fontSize:9, fontWeight:'bold', color:'#6C757D', marginBottom:4 }}>MEASUREMENTS</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 8px' }}>
                      {Object.entries(p.measurements).map(([k,v]) => (
                        <div key={k} style={{ fontSize:9, color:'#ADB5BD', display:'flex', justifyContent:'space-between' }}>
                          <span style={{ color:'#6C757D' }}>{k}</span>
                          <span style={{ color:'#34d399', fontWeight:'bold' }}>{v} cm</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function MarkerMaker({ db, onClose, preloadStyle }) {
  const [step, setStep] = useState(preloadStyle ? 2 : 1);  // jump to step 2 if style pre-loaded
  const [selectedStyle, setSelectedStyle] = useState(preloadStyle || null);
  const [showGallery, setShowGallery] = useState(false);
  const [patterns, setPatterns] = useState([]);
  const [specData, setSpecData] = useState(null);
  const [gradingRules, setGradingRules] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);  // POM Template for grading

  // Save all patterns (with any adjustments) back to style in DB
  const savePatterns = async () => {
    if (!selectedStyle) return;
    setSaving(true);
    const updated = { ...selectedStyle, patterns, updatedOn: new Date().toISOString() };
    try {
      const res = await fetch(SAVE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'style', data: updated }),
      });
      if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
      const json = await res.json().catch(() => null);
      if (json && json.success === false) throw new Error(json.error || 'Server rejected the save');
      alert(`✓ ${patterns.length} patterns saved to style "${selectedStyle.name}"`);
    } catch (e) {
      alert('⚠ Save FAILED — patterns were NOT saved: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const canGoNext = () => {
    if (step === 1 && !selectedStyle) return false;
    if (step === 2 && patterns.length === 0) return false;
    return true;
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#F8F9FA', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ background: '#1A2A3A', color: 'white', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Scissors size={18} color="#38bdf8" />
          <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>MARKER MAKER</span>
          {selectedStyle && (
            <span style={{ fontSize: 11, color: '#ADB5BD', borderLeft: '1px solid #2C3E50', paddingLeft: 10 }}>
              {selectedStyle.name || selectedStyle.styleCode}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedStyle && patterns.length > 0 && (
            <button onClick={savePatterns} disabled={saving}
              style={{ padding: '5px 12px', background: '#388E3C', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>
              {saving ? '⟳' : '💾'} Save Patterns
            </button>
          )}
          <button onClick={() => setShowGallery(true)}
            style={{ padding:'5px 12px', background:'#2C3E50', color:'#38bdf8', border:'1px solid #495057', borderRadius:5, cursor:'pointer', fontSize:10, fontWeight:'bold' }}>
            📐 Pattern Library
          </button>
          <button onClick={onClose}
            style={{ padding: '5px 10px', background: '#2C3E50', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* Pattern Library Gallery */}
      {showGallery && <PatternGallery db={db} onClose={() => setShowGallery(false)} onEdit={(p) => { setPatterns([p]); setStep(5); }} />}
      {/* Wizard steps */}
      <WizardHeader step={step} onStepClick={s => {
        if (s <= step || (s === step + 1 && canGoNext())) setStep(s);
      }} />

      {/* Step content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {step === 1 && (
          <StyleSelector db={db} onSelect={s => { setSelectedStyle(s); setPatterns([]); setStep(2); }} />
        )}
        {step === 2 && (
          <PatternLoader style={selectedStyle} db={db} patterns={patterns} setPatterns={setPatterns}
            gradingRules={gradingRules} setGradingRules={setGradingRules} />
        )}
        {step === 3 && (
          <>
            <SpecSheetImporter
              style={selectedStyle}
              specData={specData}
              setSpecData={setSpecData}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              onImportComplete={d => { setSpecData(d); setStep(4); }}
            />

            {/* Template Selector (if spec loaded but template not yet selected) */}
            {specData && !selectedTemplate && step === 3 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #DEE2E6', paddingTop: 16 }}>
                <TemplateSelector
                  specData={specData}
                  onSelectTemplate={(template) => {
                    setSelectedTemplate(template);
                    setStep(4);
                  }}
                  onSkip={() => {
                    setStep(4);
                  }}
                  garmentType={selectedStyle?.garmentType || null}
                />
              </div>
            )}
          </>
        )}
        {step === 4 && (
          <SpecCrossCheck
            patterns={patterns}
            specData={specData}
            onAdjust={adj => { setPatterns(adj); setStep(5); }}
          />
        )}
        {step === 5 && (
          <PatternEditor patterns={patterns} setPatterns={setPatterns} />
        )}
        {step === 6 && (
          <GradingView
            patterns={patterns}
            specData={specData}
            gradingRules={gradingRules}
            setGradingRules={setGradingRules}
          />
        )}
        {step === 7 && (
          <SeamChecker
            patterns={patterns}
            gradingRules={gradingRules}
            activeSizes={specData?.sizes?.length ? specData.sizes : ['M']}
          />
        )}
        {step === 8 && (
          <MarkerPlanner
            patterns={patterns}
            specData={specData}
            gradingRules={gradingRules}
            style={selectedStyle}
          />
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ background: 'white', borderTop: '1px solid #DEE2E6', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <button
          onClick={() => setStep(s => Math.max(1, s - 1))}
          disabled={step === 1}
          style={{ padding: '7px 16px', background: step === 1 ? '#F4F6F8' : '#DEE2E6', border: 'none', borderRadius: 6,
            cursor: step === 1 ? 'not-allowed' : 'pointer', fontSize: 11, color: '#495057', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <span style={{ fontSize: 10, color: '#ADB5BD' }}>Step {step} of {STEPS.length}</span>
        <button
          onClick={() => setStep(s => Math.min(STEPS.length, s + 1))}
          disabled={step === STEPS.length || !canGoNext()}
          style={{ padding: '7px 16px',
            background: (step === STEPS.length || !canGoNext()) ? '#F4F6F8' : '#0ea5e9',
            color: (step === STEPS.length || !canGoNext()) ? '#ADB5BD' : 'white',
            border: 'none', borderRadius: 6,
            cursor: (step === STEPS.length || !canGoNext()) ? 'not-allowed' : 'pointer',
            fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
